import { Formatters } from '../shared/lib/formatters';
import { parSizesText } from '../shared/lib/parSizes';   // R113: "incl. 6 King · 3 Queen"
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';
import { enqueueFrame, counts as queueCounts, pendingByArea, isSyncing, syncNow, storageWarning, loadQueue } from '../lib/captureQueue';
import { openPhotoQueueSheet } from '../shared/components/PhotoQueueSheet';   // R114: every photo still on this phone, per inspection
import { phoneQueueLine, saveExitText } from '../../../lib/frameQueuePolicy.mjs';   // R114 fix: the one "photos on this phone" line + the honest Save & exit tooltip
import { heldTargetGroups, type HeldBrief, type HeldOptionGroup } from '../../../lib/heldPhotoOptions.mjs';   // R114 fix2: Recovered photos never hide the right inspection
import { QuickLogIssue } from '../shared/components/QuickLogIssue';
import { openExternal, openApiDoc, fieldViewSwitcherHtml, bindFieldViewSwitcher } from '../shared/lib/nav';
import { openLightbox } from '../shared/components/Lightbox';   // R113 #2: a room's reference photos (cover + "+N")
import { kebabHtml, bindKebabs, closeKebab } from '../shared/components/Kebab';   // R114: per-row ⋯ (Move to date)
import { applicableGroups, applicableItems, stockGaps as stockGapsOf, labelsFor, splitTitle } from '../../../lib/stockCheck.mjs';   // R120 (team: "flashlight"): the ONE stocking & safety check (+ Flashlight), shared with the grader
import { inspectorPool, poolLabel } from '../../../lib/inspectorPool.mjs';   // R120 (Pearl/Jesse): ONE inspector list for every picker (contract C7)
import { isBoardUnknown, offerBoardRetry, BOARD_UNKNOWN_MSG } from '../shared/lib/boardRetry';   // review R120 #14 (round 6)

// Inspections surface (API: /api/assetops/inspections·inspection-templates).
//
// The product (Phase 1 — capture-first AI core): the inspector CAPTURES (records
// a narrated walkthrough or snaps room-by-room photos); the AI does the paperwork
// (transcribes, extracts frames, maps evidence to template items, grades each
// with a cited photo, flags coverage gaps + issues). The human confirms/corrects.
//
//   (1) OVERVIEW — stat strip (inspections this month, 90-day coverage WITH the
//       denominator stated, per-inspector count + avg grade) + a board, one row
//       per active listing: last inspection, days since, last grade (colored).
//   (2) INSPECTIONS — list (property · date · inspector · mode · grade). 'New
//       inspection' (property + template) opens the mobile CAPTURE view; the row
//       opens the AI REPORT CARD (or the capture view if nothing's analyzed yet).
//       The manual checklist detail is still reachable as a fallback link.
//   (3) TEMPLATES — edit inspection checklists (items carry an area).
//
// CAPTURE view (mobile-first, works at 390px): RECORD WALKTHROUGH (getUserMedia +
// MediaRecorder + live SpeechRecognition transcript, client-side frame extraction)
// or PHOTO MODE (guided room-by-room strip). Frames upload via /photo bucket=frame;
// the raw video via /video is best-effort and SKIPPED when oversized (grading is
// frame-based, so length never blocks). 'Analyze' → POST /analyze → REPORT CARD.
//
// PRINCIPLES.md #4: denominators are always stated; a metric that can't be
// computed honestly says why instead of faking a zero. Every number states its
// basis (confidence is 0–100%; grade is 'AI grade from N frames + transcript').
const API = '/api/assetops';
// A walkthrough is graded from FRAMES + narration, never the raw video — so the
// full file never needs to upload. Sample this many frames on-device (the grader
// uses up to 20 for audits, 10 for condition). The raw-video upload is optional
// and SKIPPED when the file is over the server cap, so a 30-minute walkthrough
// grades fine without ever attempting a multi-GB upload.
const FRAME_SAMPLES = 20;
const VIDEO_MAX_BYTES = 200 * 1024 * 1024;
const MODES: Array<{ key: 'checklist' | 'photo' | 'video'; label: string }> = [
  { key: 'checklist', label: 'Checklist' },
  { key: 'photo', label: 'Photo' },
  { key: 'video', label: 'Video' },
];
const ITEM_STATES: Array<'pass' | 'fail' | 'na'> = ['pass', 'fail', 'na'];

// Feature-detect the record-walkthrough path (camera + recorder). Photo mode +
// manual transcript always work, so the capture view degrades gracefully.
const CAN_RECORD = typeof navigator !== 'undefined'
  && !!navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function'
  && typeof (window as any).MediaRecorder !== 'undefined';
const SpeechRec: any = typeof window !== 'undefined'
  ? ((window as any).webkitSpeechRecognition || (window as any).SpeechRecognition || null)
  : null;
type IssueAction = 'wo' | 'restock' | 'owner_note' | 'none';

export class InspectionsPage {
  private container: HTMLElement;
  private tab: 'queue' | 'calendar' | 'overview' | 'inspections' | 'templates' | 'managers' = 'queue';
  private opsCal: any = null;   // GET /ops-calendar — combined clean+inspection calendar
  private stats: any = null;
  private analytics: any = null; // GET /inspections/analytics?days=90 (Phase 4 B)
  private board: any = null;
  private queue: any = null; // GET /inspections/queue response
  private queueDays = 14; // 7 / 14 / 30 day horizon for the turnover queue (field view)
  private queueTab: 'today' | '7d' | '14d' = 'today'; // office queue view — lands on Today's Inspections
  // R46 (Kai): a specific checkout DATE overrides the window tabs. Picking a
  // date beyond the fetched horizon widens queueDays (≤60, the server ceiling).
  private queueDate: string | null = null;
  // Donielle: inspector-mobile any-property start panel + queue name filter.
  private mobStartOpen = false;
  private mobShowAll = false;        // R64 (Stacy): false = only MY assigned work (the default)
  private schedOpen = false;         // R64 (office): ad-hoc schedule composer open
  // R114 (Madel): "↪ Move to date" dialog for one queue row — the row's KEY
  // date (from), its kind (row type) and the picked date; null = closed.
  private moveDlg: { lid: string; from: string; kind: string; name: string; inspector: string; movedFrom: string; to: string; max: string; checkin: string; busy: boolean; err: string; stale?: boolean } | null = null;
  private propBrief: any = null;     // R64: /inspections/property-brief/:lid (reference photos + quirks)
  private propBriefFor = '';
  private refPanelOpen = true;       // reference panel open state survives capture redraws
  private invPanelOpen: boolean | null = null;   // R79: inventory panel open state (null = default: open when due)
  private invChain: Promise<any> = Promise.resolve();   // serialized count saves
  private mobListings: any[] | null = null;   // /api/listings/active?include=onboarding (lazy, cached)
  private mobSearch = '';
  // Inspections LIST tab date-range filter (on completed_at || created_at).
  // Debounced like the WO date filters — Chrome fires 'change' per SEGMENT.
  private inspFrom = '';
  private inspTo = '';
  private inspDateTimer: number | null = null;
  private captureStep: 1 | 2 = 1; // R25 stepwise basic flow: 1 = issues, 2 = scoring
  // R41 (CEO): the bare-minimum stocking standard EVERY property must meet,
  // plus the safety check — one tap per item, persisted per-inspection in
  // form.checks. Collapsed by default (Jesse: long lists get scrolled past).
  // R120 (team: "flashlight"): the list lives in lib/stockCheck.mjs (shared
  // with the grader, which now sees the ticks); Safety gained Flashlight —
  // on inspections started after the deploy (applicableItems' since-rule).
  private stockOpen = false;
  // R37: three consecutive screens stack their primary CTA at the same sticky
  // bottom spot (Continue to scoring → Submit Inspection → Complete). A double
  // tap on a slow phone cascaded straight through — 1013 Hornblend completed
  // mid-scoring. These stamps make the NEXT screen's button inert for a beat.
  private stepChangedAt = 0;
  private reportShownAt = 0;
  // R26: live quick-logged ticket count for the ACTIVE capture (fed by the
  // embedded logger's onCount). Keyed by inspection id so stale counts never
  // leak across inspections.
  private activeLoggedFor: string | null = null;
  private activeLoggedCount = 0;
  // R26: ONE live embed instance per inspection — page redraws re-attach its
  // root instead of re-instantiating, so composer text/photos/mic and upload
  // badges survive every redraw. Replaced instances are destroy()ed.
  private qliInstance: QuickLogIssue | null = null;
  private qliFor: string | null = null;
  private templates: any[] = [];
  private inspections: any[] = [];
  private boardListings: any[] = []; // {listing_id, listing_name} for the New form
  private openId: string | null = null; // manual-checklist detail modal open
  private editTplId: string | null = null; // template editor open
  private loaded = false;

  // capture-first flow ─ a full-page view (not the tab board) while an inspection
  // is being captured/reported. null = the tab board is shown.
  // R114 fix2: every CHANGE of view bumps viewSeq and settles a pending
  // Save & exit wait (see waitForVideoOrLeave) — so that handler can tell
  // "never left" from "left (← Inspections, Submit) and came back", and the
  // re-opened screen's own Save & exit is never stuck behind the old one.
  private viewState: 'capture' | 'report' | null = null;
  private viewSeq = 0;
  private leaveWaitCancel: (() => void) | null = null;
  private get view(): 'capture' | 'report' | null { return this.viewState; }
  private set view(v: 'capture' | 'report' | null) {
    if (v === this.viewState) return;
    this.viewState = v;
    this.viewSeq++;
    const cancel = this.leaveWaitCancel;
    if (cancel) { this.leaveWaitCancel = null; cancel(); }
  }
  private activeId: string | null = null; // the inspection being captured/reported

  // Phase 3 — adaptive checklist suggestion (per active capture session).
  private suggestOpen = false; // is the suggest panel showing in the capture view?
  private suggest: any = null; // GET /suggest-template/:listing_id response
  private suggestLoading = false;

  // Feature C — baseline walkthrough upload (per active capture session).
  private baselineProcessing = false; // extracting frames / awaiting analysis
  private baselineMsg: { text: string; kind: 'ok' | 'warn' | 'err' } | null = null;

  // Phase 3 — condition history modal: { listing_id, name } + fetched data.
  private historyFor: { listing_id: string; name: string } | null = null;
  private history: any = null; // GET /history/:listing_id response
  private historyLoading = false;

  // live capture session state (record-walkthrough); reset per capture view.
  private mediaStream: MediaStream | null = null;
  private recorder: any = null; // MediaRecorder
  private recChunks: Blob[] = [];
  private recordedBlob: Blob | null = null;
  private recognition: any = null; // SpeechRecognition
  private transcriptFinal = ''; // committed (final) speech segments
  // R114: frame uploads are no longer counted here — every frame is persisted
  // to the phone's queue first (lib/captureQueue) and the tally reads the
  // queue's own state (this inspection vs earlier ones). The one thing still
  // uploaded directly is a walkthrough's raw video (best-effort, review only).
  private videoUploading = false;
  // R114 fix: the in-flight walkthrough video upload. It is NOT on the phone
  // (a ≤200 MB blob in IndexedDB would fight the frame queue's quota), so
  // Save & exit waits for it — with a "Leave now" way out — and the tooltip /
  // banner say it is still going instead of "Everything is saved".
  private videoUploadP: Promise<void> | null = null;
  private saveExitBusy = false;
  private syncNote: { text: string; kind: 'ok' | 'warn' | 'err'; for: string | null } | null = null;
  private syncBusy = false;
  // A capture-view message that survives redraws (R114: a showCapMsg written
  // before draw() used to be wiped instantly — "Sync now" looked dead).
  private capNote: { html: string; kind: 'ok' | 'warn' | 'err'; for: string | null; at: number; sticky: boolean } | null = null;
  // R114 live-instance guard: HubPage mounts a NEW InspectionsPage on every tab
  // click and App keeps a singleton; they all draw into the shared #insp-body.
  // Only the instance that rendered last may draw or react to queue events.
  static live: InspectionsPage | null = null;
  // bulk picks: auto-sort runs when that inspection's photos have all left the phone
  private static bulkSortFor = new Map<string, number>();
  private queueRedrawT: number | null = null;

  // Feature B — cadence FOCUS items: the DUE "required this visit" set for the
  // capture view, plus the inspector's ticked-off ids (addressed this visit).
  private focus: any[] | null = null; // GET /inspections/focus/:listing_id rows
  private focusFor: string | null = null; // listing_id the loaded focus belongs to
  private focusDone = new Set<string>(); // focus_ids the inspector ticked
  private focusSkip = new Set<string>(); // focus_ids marked N/A this visit
  private focusManager: any[] | null = null; // focus-items for the Templates manager
  private editFocusId: string | null = null; // focus-item editor open ('' = new)

  // Feature D — PM roster + per-property assignment + scorecard (Managers tab).
  private managers: any[] | null = null; // GET /inspections/managers
  private editMgrId: string | null = null; // R120: the roster row being edited inline (name / login email)
  private mgrMsg = '';                     // R120: a refused roster save, in plain words
  private propertyPm: any | null = null; // GET /inspections/property-pm { assignments, listings }
  private pmScorecard: any | null = null; // GET /inspections/pm-scorecard
  // New-inspection form: the chosen inspection purpose (condition default / audit).
  private newInspectionType: 'condition' | 'audit' = 'condition';

  constructor(container: HTMLElement) {
    this.container = container;
    if (typeof window !== 'undefined') {
      // R114: the queue module owns every wake trigger (boot, online,
      // pageshow, visible, a 30 s interval). This instance only listens —
      // and only acts while it is the live one.
      window.addEventListener('surge-frame-queue', (e: Event) => this.onQueueEvent((e as CustomEvent).detail || {}));
      void loadQueue();
    }
  }

  async render(): Promise<void> {
    InspectionsPage.live = this;
    // R115 C6: one-shot exact-date deep link (Executive Summary drill → a
    // listing's 2nd checkout of the week, which the window tabs never show).
    try { const d = sessionStorage.getItem('insp_queue_date'); if (d) { sessionStorage.removeItem('insp_queue_date'); if (/^\d{4}-\d{2}-\d{2}$/.test(d)) { this.queueDate = d; this.tab = 'queue'; } } } catch { /* private mode */ }
    this.container.innerHTML = `
      <div class="page-content">
        <div id="insp-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div>
      </div>`;
    await this.loadAll();
    this.draw();
  }

  // ── data ────────────────────────────────────────────────────
  private people: any[] = [];   // /api/tasks/assignees — for inspector assignment
  // R120 (Pearl/Jesse): the inspector roster (ao_managers — the "Inspectors &
  // PMs" tab) feeds every inspector picker through lib/inspectorPool. Read
  // only in office views; a field login never fetches it.
  private roster: any[] = [];
  // R120 (contract C6): every name this login's inspections may be assigned
  // under — its profile name plus the roster rows linked to it (/api/me
  // inspector_names) — so "Mine" finds a job assigned as "Lacey(IHC)".
  private myInspectorNames: string[] = [];
  // Review R120 #14 (D11): this login's user id (/api/me profile.id, else the
  // session profile's) — whose dismissals it may undo. '' = unknown.
  private myUserId = '';
  private async loadAll(): Promise<void> {
    const office = InspectionsPage.isOfficeRole();
    const [analytics, board, tpls, insps, queue, forms, ppl, mgrs, me] = await Promise.all([
      this.get('/inspections/analytics?days=90'),
      this.get('/inspections/board'),
      this.get('/inspection-templates'),
      this.get('/inspections'),
      this.get(`/inspections/queue?days=${this.queueDays}${this.queueDate ? `&date=${this.queueDate}` : ''}`),   // R115: exact-date mode survives a full reload
      this.get('/inspections/forms'),
      fetch('/api/tasks/assignees').then(r => r.ok ? r.json() : null).catch(() => null),
      office ? this.get('/inspections/managers') : Promise.resolve(null),
      office ? Promise.resolve(null) : fetch('/api/me').then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    if (mgrs && Array.isArray(mgrs.managers)) this.roster = mgrs.managers;
    if (me && Array.isArray(me.inspector_names)) this.myInspectorNames = me.inspector_names.map((x: any) => String(x || '').trim()).filter(Boolean);
    this.myUserId = String((me && me.profile && me.profile.id) || InspectionsPage.profileId() || '');
    this.analytics = analytics;
    this.board = board;
    this.queue = queue || this.queue;
    this.boardListings = (board?.rows || []).map((r: any) => ({ listing_id: r.listing_id, listing_name: r.listing_name }));
    this.templates = tpls?.templates || [];
    // R37: a failed GET /inspections must NEVER blank the list — an empty list
    // while view==='capture' silently exitCapture()s and ejects the inspector
    // mid-scoring (the 1013 Hornblend "my inspection closed on me" report).
    this.inspections = insps?.inspections || this.inspections;
    this.formDefs = forms?.forms || null;
    this.people = (ppl && ppl.people) || [];
    this.loaded = true;
  }
  // People eligible to inspect — R120 (Pearl/Jesse; contract C7): ONE list
  // for every picker (lib/inspectorPool): inspector logins, the inspector
  // roster (a row with a login's email is offered under the login's name —
  // so the pick lands in that person's own "Mine"), leads "(manager)", then
  // names already on jobs. A plain cleaner is not offered; an assignment
  // already on the row ("Lacey(IHC)") always stays selected.
  private jobNames(): string[] {
    return [
      ...(((this.queue && this.queue.rows) || []).map((r: any) => r && r.assigned_inspector)),
      ...((this.inspections || []).map((i: any) => i && i.inspector)),
    ].map(x => String(x || '').trim()).filter(Boolean);
  }
  private inspectorOpts(current: string): string {
    const profiles = this.people.map(p => ({
      // /api/tasks/assignees falls back to the email for a login with no name — never a name
      full_name: p.full_name && !String(p.full_name).includes('@') ? p.full_name : '',
      email: p.email || '', role: p.role || '', is_active: p.is_active !== false,
    }));
    const pool = inspectorPool({ profiles, roster: this.roster, jobNames: this.jobNames() });
    const cur = String(current || '').trim();
    const has = (n: string) => pool.some(p => p.name.toLowerCase() === n.toLowerCase());
    return [`<option value="">— unassigned —</option>`]
      .concat(pool.map(p => `<option value="${this.esc(p.name)}" ${cur && cur.toLowerCase() === p.name.toLowerCase() ? 'selected' : ''}>${this.esc(poolLabel(p))}</option>`))
      .concat(cur && !has(cur) ? [`<option value="${this.esc(cur)}" selected>${this.esc(cur)}</option>`] : [])
      .join('');
  }
  private async reloadQueue(): Promise<void> {
    // R46: in exact-date mode the SERVER picks each listing's checkout equal to
    // the date (a client-side filter over the normal rows missed properties
    // whose soonest checkout precedes the picked date).
    const dateQ = this.queueDate ? `&date=${this.queueDate}` : '';
    const q = await this.get(`/inspections/queue?days=${this.queueDays}${dateQ}`);
    if (q) this.queue = q;
  }
  private async reloadInspections(): Promise<void> {
    const j = await this.get('/inspections');
    // R37: keep the stale list on a failed refresh (see loadAll) — blanking it
    // mid-capture ejects the inspector via the drawCaptureSurface guard.
    if (j?.inspections) this.inspections = j.inspections;
  }

  // R37: called by App's 15-min auto-refresh — never wipe the full-page
  // capture/report surface out from under a working inspector.
  suspendAutoRefresh(): boolean {
    return this.view === 'capture' || this.view === 'report' || this.mobStartOpen;   // a half-typed property name must not be wiped by the 15-min refresh
  }
  private async reloadStatsAndBoard(): Promise<void> {
    const [analytics, board] = await Promise.all([this.get('/inspections/analytics?days=90'), this.get('/inspections/board')]);
    if (analytics) this.analytics = analytics;
    if (board) { this.board = board; this.boardListings = (board.rows || []).map((r: any) => ({ listing_id: r.listing_id, listing_name: r.listing_name })); }
  }
  private async get(path: string): Promise<any> {
    return fetch(`${API}${path}`).then(r => (r.ok ? r.json() : null)).catch(() => null);
  }
  // retried (review R120 #14, round 6): the server could not read the Issues
  // board (503 board_unknown) and saved nothing. With a retried callback the
  // page says so in plain words and offers Try again (the SAME request; its
  // answer goes to retried); without one, the plain words alone.
  private async post(path: string, body: any, retried?: (j: any) => void): Promise<any> {
    let r: Response;
    // AI-unblock: a dropped connection (phone backgrounded mid-submit) returns
    // null like any failure — never an unhandled throw that leaves Submit stuck.
    try { r = await fetch(`${API}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); }
    catch (_) { return null; }
    const j = await r.json().catch(() => ({}));
    if (!r.ok && isBoardUnknown(r.status, j)) {
      if (retried) offerBoardRetry(async () => { const j2 = await this.post(path, body, retried); if (j2 && j2.ok) retried(j2); });
      else alert(BOARD_UNKNOWN_MSG);
      return null;
    }
    if (!r.ok) alert(j.error || `request failed (${r.status})`);
    return r.ok ? j : null;
  }
  private async del(path: string): Promise<any> {
    const r = await fetch(`${API}${path}`, { method: 'DELETE' });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) alert(j.error || `request failed (${r.status})`);
    return r.ok ? j : null;
  }

  // ── helpers ─────────────────────────────────────────────────
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }
  // Stacy 2026-10-07: an inspection is dated by the VISIT (server performed_at
  // = first on-site photo), not by when Complete was pressed — finishing last
  // week's stuck one today is still last week's inspection.
  private visitAt(i: any): string | null { return (i && (i.performed_at || i.created_at)) || null; }
  private localDay(iso: string | null | undefined): string {
    const d = iso ? new Date(iso) : null;
    return d && !isNaN(d.getTime()) ? d.toLocaleDateString('en-CA') : '';
  }
  // "completed Oct 2" — or "inspected Oct 2 · completed Oct 7" when they differ
  private doneLabel(i: any): string {
    const v = this.visitAt(i), c = i && i.completed_at;
    return c && v && this.localDay(v) !== this.localDay(c)
      ? `inspected ${this.fmtDate(v)} · completed ${this.fmtDate(c)}`
      : `completed ${this.fmtDate(c || v)}`;
  }
  private fmtDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    // Bare 'YYYY-MM-DD' must be rendered as a CALENDAR date, not parsed as UTC
    // midnight (which displays as the PREVIOUS day in Pacific — the "Jul 30 on
    // Jul 31" bug). Pin both the parse and the render to UTC so the printed day
    // always equals the stored day.
    const bare = /^\d{4}-\d{2}-\d{2}$/.test(String(iso));
    const d = bare ? new Date(iso + 'T00:00:00Z') : new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', bare
      ? { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }
      : { month: 'short', day: 'numeric', year: 'numeric' });
  }
  private gradeHex(g: number | null | undefined): string {
    if (g == null) return 'var(--text-muted)';
    if (g >= 90) return 'var(--success-text, #166534)';
    if (g >= 75) return '#ca8a04';
    return 'var(--danger-text, #b91c1c)';
  }
  private gradeSoft(g: number | null | undefined): string {
    if (g == null) return 'var(--bg-subtle)';
    if (g >= 90) return 'var(--success-soft)';
    if (g >= 75) return 'var(--warning-soft)';
    return 'var(--danger-soft)';
  }
  private daysHex(days: number | null): string {
    if (days == null) return 'var(--danger-text, #b91c1c)'; // never inspected
    if (days <= 90) return 'var(--success-text, #166534)';
    if (days <= 180) return '#ca8a04';
    return 'var(--danger-text, #b91c1c)';
  }
  private modeLabel(m: string): string { return (MODES.find(x => x.key === m)?.label) || m; }
  private tplName(id: string): string { return this.templates.find(t => t.id === id)?.name || id; }

  // The property's saved adaptive default (set by POST /property-template) shows
  // up in the templates list as a non-standard template carrying its listing_id.
  // We use that to tell the inspector "this property already has a saved checklist."
  private savedPropTemplate(listing_id: string | null | undefined): any | null {
    if (listing_id == null) return null;
    const lid = String(listing_id);
    return this.templates.find(t => !t.is_standard && t.listing_id != null && String(t.listing_id) === lid) || null;
  }

  private activeInsp(): any { return this.inspections.find(i => i.id === this.activeId) || null; }

  // ── shell ───────────────────────────────────────────────────
  private draw(): void {
    // R114: a stale (hub-replaced) instance must never paint over the live view
    if (InspectionsPage.live && InspectionsPage.live !== this) return;
    const body = document.getElementById('insp-body');
    if (!body) return;
    // Full-page capture / report views take over the surface (mobile-first).
    if (this.view === 'capture' || this.view === 'report') { this.drawCaptureSurface(body); return; }
    // R5 #1 — an INSPECTOR gets a clean field view (today's queue + resume),
    // not the 5-tab manager cockpit. The capture/report flow above is shared.
    if (InspectionsPage.fieldRole() === 'inspector') { this.drawInspectorMobile(body); return; }
    const tabs: Array<[string, string]> = [['queue', 'Queue'], ['calendar', 'Calendar'], ['overview', 'Overview'], ['inspections', 'Inspections'], ['templates', 'Templates'], ['managers', 'Inspectors & PMs']];   // R120: the roster IS the inspector list
    body.innerHTML = `
      ${this.styleBlock()}
      <div class="dash-head">
        <h1>Inspections</h1>
        <p>capture-first AI inspections · checklist fallback · ${this.board?.active_count ?? '?'} active properties</p>
      </div>
      ${this.phoneQueueBanner()}
      <div class="insp-tabs">
        ${tabs.map(([k, label]) => `<button class="insp-tab ${this.tab === k ? 'on' : ''}" data-insp-tab="${k}">${label}</button>`).join('')}
      </div>
      <div id="insp-tab-body">
        ${this.tab === 'queue' ? this.renderQueue()
          : this.tab === 'calendar' ? this.renderOpsCalendar()
          : this.tab === 'overview' ? this.renderOverview()
          : this.tab === 'inspections' ? this.renderInspections()
          : this.tab === 'managers' ? this.renderManagers()
          : this.renderTemplates()}
      </div>`;
    this.bind();
    if (this.openId) this.renderDetailModal();
    if (this.editTplId !== null) this.renderTemplateModal();
    if (this.editFocusId !== null) this.renderFocusModal();
  }

  // Style block shared by the tab board AND the full-page capture/report surface
  // (both replace #insp-body innerHTML, so each must carry the styles).
  private styleBlock(): string {
    return `
      <style>
        .insp-head { display:flex; align-items:baseline; gap:12px; margin-bottom:6px; flex-wrap:wrap; }
        .insp-head h2 { margin:0; font-size:18px; font-weight:600; letter-spacing:-0.015em; color:var(--text); }
        .insp-head .sub { font-size:12px; color:var(--text-muted); }
        .insp-tabs { display:flex; gap:4px; margin:14px 0 20px; border-bottom:1px solid var(--border); }
        .insp-tab { padding:8px 16px; font-size:12px; font-weight:600; color:var(--text-muted); cursor:pointer; border:none; background:none; border-bottom:2px solid transparent; transition:color .15s ease, border-color .15s ease; }
        .insp-tab:hover { color:var(--text); }
        .insp-tab.on { color:var(--text); border-bottom-color:var(--text); }
        .insp-basis { font-size:11px; color:var(--text-faint, #9ca3af); margin:4px 0 14px; line-height:1.5; }
        .q-head { display:flex; align-items:center; justify-content:space-between; gap:12px; margin:4px 0 16px; flex-wrap:wrap; }
        .q-days { display:inline-flex; border:1px solid var(--border); border-radius:var(--radius-pill); overflow:hidden; flex:none; }
        .q-day-btn { font-size:11px; font-weight:600; padding:6px 14px; border:none; background:var(--card); cursor:pointer; color:var(--text-muted); transition:background .15s ease, color .15s ease; }
        .q-day-btn:hover { color:var(--text); }
        .q-day-btn.on { background:var(--text); color:#fff; }
        .insp-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); }
        .insp-strip { display:grid; grid-template-columns:repeat(auto-fit, minmax(190px, 1fr)); gap:14px; margin-bottom:20px; }
        .insp-kpi { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:16px 18px; }
        .insp-kpi .lbl { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); }
        .insp-kpi .val { font-size:27px; font-weight:600; margin-top:5px; color:var(--text); letter-spacing:-0.02em; font-variant-numeric:tabular-nums; }
        .insp-kpi .basis { font-size:10px; color:var(--text-faint, #9ca3af); margin-top:5px; line-height:1.4; }
        table.insp-tbl { width:100%; border-collapse:collapse; table-layout:fixed; }
        table.insp-tbl th { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); text-align:left; padding:11px 14px; border-bottom:1px solid var(--border); white-space:nowrap; }
        table.insp-tbl td { font-size:12.5px; padding:12px 14px; border-bottom:1px solid var(--border-soft, #f1f5f9); vertical-align:middle; color:var(--text); }
        table.insp-tbl tr:last-child td { border-bottom:none; }
        table.insp-tbl tbody tr { transition:background .12s ease; }
        table.insp-tbl tbody tr.click { cursor:pointer; }
        table.insp-tbl tbody tr.click:hover { background:var(--bg-subtle); }
        .insp-name { font-weight:600; color:var(--text); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .insp-assign { padding:6px 8px; border:1px solid var(--border); border-radius:7px; font-size:12px; max-width:140px; background:var(--card,#fff); }
        /* R19 #4a — the queue table stacks into cards on a phone so the property
           name is never clipped (the PM's mobile complaint). */
        @media (max-width:760px) {
          .insp-tbl-wrap { overflow-x:visible; }
          table.insp-tbl { table-layout:auto; }
          table.insp-tbl, table.insp-tbl thead, table.insp-tbl tbody, table.insp-tbl th, table.insp-tbl td, table.insp-tbl tr { display:block; }
          table.insp-tbl thead { position:absolute; left:-9999px; }
          table.insp-tbl tr { border:1px solid var(--border); border-radius:12px; margin-bottom:10px; padding:6px 4px; }
          table.insp-tbl td { border:none; padding:6px 12px; display:flex; justify-content:space-between; align-items:center; gap:10px; text-align:right !important; white-space:normal; }
          table.insp-tbl td::before { content:attr(data-th); font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted); flex:0 0 auto; text-align:left; }
          table.insp-tbl td.insp-name { font-size:15px; overflow:visible; white-space:normal; }
          table.insp-tbl td.insp-name::before { content:''; }
          .insp-assign { max-width:60%; }
        }
        .insp-sub { font-size:11px; color:var(--text-muted); }
        .insp-pill { display:inline-block; font-size:10.5px; font-weight:600; padding:3px 10px; border-radius:var(--radius-pill); white-space:nowrap; }
        .insp-chip { display:inline-block; font-size:10.5px; font-weight:600; border:1px solid var(--border); border-radius:var(--radius-pill); padding:3px 10px; color:var(--text-muted); background:var(--card); white-space:nowrap; }
        .insp-grade { display:inline-block; font-size:11px; font-weight:700; padding:3px 11px; border-radius:var(--radius-pill); font-variant-numeric:tabular-nums; }
        .insp-form { display:flex; flex-wrap:wrap; gap:10px; align-items:flex-end; padding:16px; margin-bottom:16px; }
        .insp-form label { display:flex; flex-direction:column; gap:4px; font-size:10.5px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
        .insp-form input, .insp-form select { padding:8px 10px; border:1px solid var(--border); border-radius:var(--radius-sm); font-size:12.5px; background:var(--card); color:var(--text); transition:border-color .15s ease; }
        .insp-form input:focus, .insp-form select:focus { outline:none; border-color:var(--text); }
        .insp-form button { padding:8px 16px; border:none; border-radius:var(--radius-sm); background:var(--text); color:#fff; font-size:12.5px; font-weight:600; cursor:pointer; transition:opacity .15s ease; }
        .insp-form button:hover { opacity:0.88; }
        .insp-btn { padding:7px 14px; border:1px solid var(--border); border-radius:var(--radius-sm); font-size:11px; font-weight:600; background:var(--card); cursor:pointer; color:var(--text); transition:border-color .15s ease, background .15s ease; }
        .insp-btn:hover { border-color:var(--text-muted); background:var(--bg-subtle); }
        .insp-btn.primary { background:var(--text); color:#fff; border-color:var(--text); }
        .insp-btn.primary:hover { background:var(--text); opacity:0.88; }
        .insp-btn.danger { color:var(--danger); }
        .insp-pdf-tag { display:inline-block; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; padding:1px 6px; border-radius:var(--radius-pill); background:var(--bg-subtle); color:var(--text-muted); margin-left:5px; vertical-align:middle; }
        .insp-btn.primary .insp-pdf-tag { background:rgba(255,255,255,0.22); color:#fff; }
        .insp-modal-bg { position:fixed; inset:0; background:rgba(15,23,42,0.45); z-index:200; display:flex; align-items:flex-start; justify-content:center; padding:32px 16px; overflow-y:auto; }
        .insp-modal { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:0 10px 40px rgba(15,23,42,0.18); width:min(760px, 96vw); max-height:none; display:flex; flex-direction:column; padding:22px; gap:14px; }
        .insp-modal h3 { margin:0; font-size:15px; font-weight:600; color:var(--text); letter-spacing:-0.01em; }
        .insp-modal-head { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; }
        .insp-modal-actions { display:flex; gap:8px; flex-wrap:wrap; }
        .insp-x { cursor:pointer; font-size:18px; color:var(--text-muted); border:none; background:none; line-height:1; transition:color .15s ease; }
        .insp-x:hover { color:var(--text); }
        .insp-area { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); margin:14px 0 5px; }
        .insp-item { display:grid; grid-template-columns:1fr auto; gap:8px; align-items:center; padding:9px 0; border-bottom:1px solid var(--border-soft, #f1f5f9); }
        .insp-item .ttl { font-size:12.5px; color:var(--text); }
        .insp-item .note { font-size:11px; color:var(--text-muted); margin-top:2px; }
        .insp-seg { display:inline-flex; border:1px solid var(--border); border-radius:var(--radius-sm); overflow:hidden; }
        .insp-seg button { font-size:10px; font-weight:600; padding:5px 10px; border:none; background:var(--card); cursor:pointer; color:var(--text-muted); border-right:1px solid var(--border); transition:background .12s ease, color .12s ease; }
        .insp-seg button:last-child { border-right:none; }
        .insp-seg button.on.pass { background:var(--success-soft); color:var(--success-text, #166534); }
        .insp-seg button.on.fail { background:var(--danger-soft); color:var(--danger-text, #b91c1c); }
        .insp-seg button.on.na { background:var(--bg-subtle); color:var(--text); }
        .insp-thumb { width:36px; height:36px; border-radius:var(--radius-sm); object-fit:cover; border:1px solid var(--border); vertical-align:middle; }
        .insp-link { color:var(--info); cursor:pointer; font-size:11px; font-weight:600; }
        .insp-link:hover { text-decoration:underline; }
        .insp-media { display:flex; flex-wrap:wrap; gap:8px; margin-top:6px; }
        .insp-media a img { width:64px; height:64px; border-radius:var(--radius-sm); object-fit:cover; border:1px solid var(--border); display:block; }
        .insp-ai { background:var(--bg-subtle); border:1px solid var(--border); border-radius:var(--radius); padding:14px; font-size:12.5px; line-height:1.5; }
        .insp-ai .miss { color:var(--danger-text, #b91c1c); }
        .insp-fld { display:flex; flex-direction:column; gap:4px; font-size:10.5px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
        .insp-fld input, .insp-fld textarea, .insp-fld select { padding:8px 10px; border:1px solid var(--border); border-radius:var(--radius-sm); font-size:12.5px; font-weight:400; text-transform:none; letter-spacing:0; color:var(--text); background:var(--card); transition:border-color .15s ease; }
        .insp-fld input:focus, .insp-fld textarea:focus, .insp-fld select:focus { outline:none; border-color:var(--text); }
        .insp-fld textarea { min-height:64px; resize:vertical; font-family:inherit; line-height:1.5; }
        .insp-tpl-item { display:grid; grid-template-columns:1fr 160px auto; gap:8px; align-items:center; margin-bottom:7px; }
        .insp-tpl-item input { padding:7px 10px; border:1px solid var(--border); border-radius:var(--radius-sm); font-size:12.5px; background:var(--card); color:var(--text); transition:border-color .15s ease; }
        .insp-tpl-item input:focus { outline:none; border-color:var(--text); }

        /* ── capture-first flow (mobile-first; works at 390px) ───────── */
        .cap-wrap { max-width:560px; margin:0 auto; }
        .cap-top { display:flex; align-items:center; gap:10px; margin-bottom:14px; }
        .cap-back { border:none; background:none; cursor:pointer; font-size:13px; font-weight:600; color:var(--text-muted); padding:6px 0; transition:color .15s ease; }
        .cap-back:hover { color:var(--text); }
        .cap-title { font-size:18px; font-weight:600; letter-spacing:-0.015em; margin:0; color:var(--text); }
        .cap-meta { font-size:11px; color:var(--text-muted); margin-top:3px; }
        .cap-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:18px; margin-bottom:14px; }
        .cap-card.card-elev {}
        .cap-modes { display:flex; gap:6px; background:var(--bg-subtle); border:1px solid var(--border); border-radius:var(--radius); padding:4px; margin-bottom:14px; }
        .cap-mode-btn { flex:1; padding:10px 8px; border:none; background:none; border-radius:calc(var(--radius) - 4px); font-size:13px; font-weight:600; color:var(--text-muted); cursor:pointer; transition:color .15s ease; }
        .cap-mode-btn.on { background:var(--card); color:var(--text); box-shadow:var(--shadow-card); }
        .cap-section-h { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); margin:0 0 8px; }
        .cap-big-btn { width:100%; padding:18px; border-radius:12px; border:none; font-size:16px; font-weight:700; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:10px; }
        .cap-big-btn.rec { background:var(--danger); color:#fff; }
        .cap-big-btn.stop { background:var(--text); color:#fff; }
        .cap-big-btn:disabled { opacity:0.5; cursor:not-allowed; }
        .cap-video { width:100%; border-radius:10px; background:#0f172a; aspect-ratio:16/9; object-fit:cover; margin-bottom:12px; display:block; }
        .cap-recdot { width:10px; height:10px; border-radius:50%; background:#fff; animation:cap-pulse 1s infinite; }
        @keyframes cap-pulse { 0%,100%{opacity:1;} 50%{opacity:0.3;} }
        .cap-transcript { width:100%; box-sizing:border-box; min-height:90px; resize:vertical; padding:10px; border:1px solid var(--border); border-radius:8px; font-size:13px; font-family:inherit; line-height:1.45; color:var(--text); }
        .cap-transcript .interim { color:var(--text-muted); }
        .cap-hint { font-size:11px; color:var(--text-faint, #9ca3af); margin-top:6px; line-height:1.4; }
        /* photo mode — room strip */
        .cap-rooms { display:flex; flex-direction:column; gap:10px; }
        .cap-room { border:1px solid var(--border); border-radius:var(--radius); padding:14px; display:flex; align-items:center; gap:12px; transition:border-color .15s ease, background .15s ease; }
        .cap-room.lit { border-color:var(--success); background:var(--success-soft); }
        .cap-room-info { flex:1; min-width:0; }
        .cap-room-area { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); }
        .cap-room-ttl { font-size:13px; font-weight:600; color:var(--text); overflow:hidden; text-overflow:ellipsis; }
        .cap-room-thumbs { display:flex; gap:5px; margin-top:6px; flex-wrap:wrap; }
        .cap-room-thumbs img { width:40px; height:40px; border-radius:var(--radius-sm); object-fit:cover; border:1px solid var(--border); }
        .cap-add { flex:none; }
        .cap-add label { display:inline-flex; align-items:center; justify-content:center; min-width:64px; min-height:44px; padding:0 14px; border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--card); font-size:13px; font-weight:600; cursor:pointer; color:var(--text); transition:border-color .15s ease, background .15s ease; }
        .cap-add label:hover { border-color:var(--text-muted); background:var(--bg-subtle); }
        .cap-add input { display:none; }
        .cap-bulk { margin:0 0 14px; padding:12px; border:1px dashed var(--info,#2563eb); border-radius:10px; background:var(--info-soft,#eff6ff); }
        .cap-bulk-btn { display:inline-flex; align-items:center; justify-content:center; gap:6px; min-height:44px; padding:0 18px; border-radius:10px; border:none; background:var(--info,#2563eb); color:#fff; font-size:14px; font-weight:700; cursor:pointer; }
        .cap-bulk-btn:hover { filter:brightness(1.05); }
        .cap-bulk-btn input { display:none; }
        .cap-frames-tally { font-size:12px; color:var(--text-muted); margin:12px 0 4px; line-height:1.6; }
        .cap-pending { color:var(--warning-text,#92400e); font-weight:600; }
        .cap-pending.bad { color:var(--danger-text); }
        .cap-sync { font-size:11.5px; font-weight:600; min-height:30px; padding:2px 12px; border-radius:999px; border:1px solid var(--accent-border, var(--border)); background:var(--accent-soft); color:var(--accent); cursor:pointer; margin-left:4px; }
        .cap-sync:disabled { opacity:.6; cursor:default; }
        .cap-older { margin-top:6px; padding:8px 10px; border-radius:var(--radius-sm, 8px); background:var(--bg-subtle); border:1px solid var(--border); color:var(--text); font-size:12px; line-height:1.45; }
        .cap-older.bad { background:var(--warning-soft); color:var(--warning-text); border-color:transparent; }
        .cap-review { border:none; background:none; padding:6px 2px; min-height:32px; color:var(--accent); font:inherit; font-weight:700; cursor:pointer; text-decoration:underline; }
        .cap-sync-note { margin-top:6px; font-size:12px; font-weight:600; }
        .cap-sync-note.ok { color:var(--success-text); } .cap-sync-note.warn { color:var(--warning-text); } .cap-sync-note.err { color:var(--danger-text); }
        .cap-room-thumbs img.late { outline:2px dashed var(--warning, #b45309); outline-offset:-2px; }
        .ifld-fq { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:10px 12px; margin:0 0 12px; border-radius:var(--radius); background:var(--accent-soft); color:var(--text); font-size:12.5px; font-weight:600; line-height:1.4; }
        .ifld-fq .btn { flex:none; min-height:36px; }
        .cap-leave-now { min-height:36px; margin-left:6px; }
        .cap-upload-row { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-top:14px; padding-top:14px; border-top:1px solid var(--border-soft,#f1f5f9); }
        .cap-upload-btn { font-size:12px; font-weight:600; padding:8px 16px; border-radius:var(--radius-sm); border:1px solid var(--border,#e2e8f0); background:var(--card); color:var(--text,#0f172a); cursor:pointer; white-space:nowrap; transition:border-color .15s ease, background .15s ease; }
        .cap-upload-btn:hover { border-color:var(--text-muted); background:var(--bg-subtle); }
        .cap-upload-hint { font-size:11px; color:var(--text-faint,#94a3b8); }
        .frm-row { padding:10px 0; border-bottom:1px solid var(--border-soft, #f1f5f9); }
        .frm-row-t { font-size:13px; font-weight:600; margin-bottom:7px; }
        .frm-req { color:var(--danger-text, #b91c1c); }
        .frm-seg-row { display:flex; gap:6px; }
        .frm-seg-row.wrap { flex-wrap:wrap; }
        .frm-seg { min-width:44px; min-height:40px; font-size:14px; font-weight:700; border:1px solid var(--border); border-radius:10px; background:var(--bg-subtle); color:var(--text-muted); cursor:pointer; }
        .frm-seg.wide { min-width:0; flex:1; font-size:12.5px; padding:0 10px; }
        .frm-seg.ten { min-width:38px; }
        .frm-seg.on { background:var(--text); color:#fff; border-color:var(--text); }
        .frm-sec { border:1px solid var(--border); border-radius:12px; margin:8px 0; padding:0 12px; background:var(--bg-subtle); }
        .frm-sec summary { font-size:13px; font-weight:700; padding:11px 0; cursor:pointer; list-style-position:inside; }
        .frm-sec-letter { display:inline-flex; align-items:center; justify-content:center; width:22px; height:22px; border-radius:7px; background:var(--text); color:#fff; font-size:11px; font-weight:700; margin-right:2px; }
        .frm-sec-n { float:right; font-size:11.5px; font-weight:600; color:var(--text-muted); }
        .frm-check { display:flex; gap:9px; align-items:flex-start; font-size:13px; padding:7px 0; border-top:1px solid var(--border-soft, #f1f5f9); cursor:pointer; }
        .frm-check input { width:18px; height:18px; flex:none; margin-top:1px; }
        .frm-note { width:100%; padding:9px 11px; border:1px solid var(--border); border-radius:8px; font-size:12.5px; margin:8px 0; background:#fff; }
        .frm-hint { font-size:11.5px; color:var(--text-muted); margin:4px 0 6px; line-height:1.45; }
        .frm-photo-hint { font-size:11.5px; color:var(--info, #1d4ed8); margin:6px 0 10px; line-height:1.4; }
        .frm-comp { float:right; font-size:11px; font-weight:700; color:var(--text-muted); }
        /* R25 — stepwise basic flow */
        .cap-stepbar { display:flex; align-items:center; gap:8px; margin-bottom:14px; }
        .cap-step { flex:1; padding:10px 12px; border:1px solid var(--border); border-radius:10px; background:var(--card,#fff); font-size:13.5px; font-weight:600; color:var(--text-muted); cursor:pointer; }
        .cap-step.on { background:var(--accent); color:#fff; border-color:var(--accent); }
        .cap-step-sep { color:var(--text-faint,#9ca3af); font-weight:700; }
        .frm-dual { display:flex; align-items:center; gap:8px; margin-top:4px; }
        .frm-dual-l { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted); width:44px; flex:none; }
        .iss-step-composer { display:flex; flex-direction:column; gap:8px; margin-bottom:10px; }
        .iss-step-in { padding:10px 12px; border:1px solid var(--border); border-radius:10px; font-size:14.5px; width:100%; }
        .iss-step-crow { display:flex; gap:8px; flex-wrap:wrap; }
        .iss-step-fu { padding:7px 9px; border:1px solid var(--border); border-radius:8px; font-size:12.5px; background:var(--card,#fff); }
        .iss-step-list { display:flex; flex-direction:column; gap:8px; margin-top:6px; }
        .iss-step-row { border:1px solid var(--border-soft,#f1f5f9); border-radius:10px; padding:9px 11px; display:flex; flex-direction:column; gap:7px; }
        .iss-step-main { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
        .iss-step-type { font-size:11px; color:var(--text-muted); }
        .iss-step-src { font-size:12px; }
        .iss-step-note { font-size:14px; flex:1 1 100%; }
        .iss-step-acts { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
        .iss-step-badge { font-size:10.5px; font-weight:700; padding:2px 8px; border-radius:999px; }
        .iss-step-badge.fixed { background:#f0fdf4; color:#15803d; }
        .iss-step-badge.fu { background:#fff7ed; color:#c2410c; }
        .iss-step-badge.open { background:#eff6ff; color:#1d4ed8; }
        .cap-analyze-bar { position:sticky; bottom:0; background:linear-gradient(to top, var(--bg, #f8fafc) 60%, transparent); padding:14px 0 4px; margin-top:8px; }
        .cap-analyze-btn { width:100%; padding:16px; border-radius:var(--radius); border:none; background:var(--info); color:#fff; font-size:15px; font-weight:700; cursor:pointer; transition:filter .15s ease; }
        .cap-analyze-btn:hover:not(:disabled) { filter:brightness(1.05); }
        .cap-analyze-btn:disabled { background:var(--bg-subtle); color:var(--text-muted); cursor:not-allowed; border:1px solid var(--border); }
        .cap-upload { font-size:12px; color:var(--info); font-weight:600; margin-top:8px; text-align:center; }
        .cap-msg { font-size:12px; padding:11px 13px; border-radius:var(--radius-sm); margin-top:10px; line-height:1.45; }
        .cap-msg.err { background:var(--danger-soft); color:var(--danger-text, #991b1b); }
        .cap-msg.warn { background:var(--warning-soft); color:var(--warning-text, #92400e); }
        .cap-msg.ok { background:var(--success-soft); color:var(--success-text, #166534); }
        .cap-link-row { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
        .cap-link-input { flex:1 1 280px; min-width:0; font-size:13px; padding:10px 12px; border-radius:var(--radius-sm); border:1px solid var(--border,#e2e8f0); background:var(--card); color:var(--text,#0f172a); transition:border-color .15s ease; }
        .cap-link-input:focus { outline:none; border-color:var(--info); }
        .cap-link-btn { font-size:12px; font-weight:600; padding:10px 18px; border-radius:var(--radius-sm); border:1px solid var(--border,#e2e8f0); background:var(--card); color:var(--text,#0f172a); cursor:pointer; white-space:nowrap; transition:border-color .15s ease, background .15s ease; }
        .cap-link-btn:hover { border-color:var(--text-muted); background:var(--bg-subtle); }
        .cap-link-btn:disabled { opacity:.55; cursor:not-allowed; }

        /* ── Feature B: cadence focus items ("required this visit") ───── */
        .foc-count { display:inline-block; min-width:18px; padding:0 7px; margin-left:6px; border-radius:var(--radius-pill); background:var(--danger-soft); color:var(--danger-text,#b91c1c); font-size:11px; font-weight:700; text-align:center; }
        /* R80 (Stacy's screenshot): the row is a COLUMN — R58's detail/WO
           divs sat beside the title in a row flex, squeezing it to one word
           per line and overlapping the note. Head row holds tick+title+N/A. */
        .foc-row { display:flex; flex-direction:column; gap:4px; padding:12px; border:1px solid var(--border,#e2e8f0); border-radius:var(--radius); margin-bottom:8px; transition:border-color .15s ease, background .15s ease; }
        .foc-head { display:flex; align-items:flex-start; gap:10px; width:100%; }
        .foc-row:hover { background:var(--bg-subtle); }
        .foc-row.on { border-color:var(--success-text,#166534); background:var(--success-soft); }
        .foc-row.skip { opacity:.6; background:var(--bg-subtle); }
        .foc-pick { display:flex; align-items:flex-start; gap:10px; flex:1; min-width:0; cursor:pointer; }
        .foc-row input[type=checkbox] { width:18px; height:18px; margin-top:1px; flex:none; }
        .foc-skip { flex:none; align-self:flex-start; margin-top:2px; font-size:10px; font-weight:700; padding:4px 10px; border-radius:var(--radius-pill); border:1px solid var(--border); background:#fff; color:var(--text-muted); cursor:pointer; white-space:nowrap; }
        .foc-skip:hover { border-color:#9ca3af; color:var(--text); }
        .foc-skip.on { background:var(--text); color:#fff; border-color:var(--text); }
        .foc-badge.quirk { background:#fce7f3; color:#9d174d; }
        .foc-badge.photo { background:#ede9fe; color:#6d28d9; }
        .foc-main { display:flex; flex-direction:column; gap:3px; min-width:0; }
        .foc-ttl { font-size:13px; font-weight:600; color:var(--text,#0f172a); }
        .foc-sub { font-size:11px; color:var(--text-muted); display:flex; flex-wrap:wrap; align-items:center; gap:6px; }
        .foc-badge { display:inline-block; padding:2px 8px; border-radius:var(--radius-pill); font-size:10px; font-weight:700; }
        .foc-badge.over { background:var(--danger-soft); color:var(--danger-text,#b91c1c); }
        .foc-badge.due  { background:var(--warning-soft); color:var(--warning-text,#92400e); }
        .foc-badge.new  { background:var(--bg-subtle); color:var(--text-muted); }
        .foc-badge.miss { background:var(--danger-soft); color:var(--danger-text,#b91c1c); }
        /* focus-items manager (Templates tab) */
        .fm-cad { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
        .fm-cad select, .fm-cad input { padding:8px 10px; border:1px solid var(--border,#e2e8f0); border-radius:var(--radius-sm); font-size:13px; background:var(--card); color:var(--text); transition:border-color .15s ease; }
        .fm-cad select:focus, .fm-cad input:focus { outline:none; border-color:var(--text); }

        /* ── Phase 3: suggest-checklist panel (in the capture view) ───── */
        .sg-bar { display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
        .sg-saved { font-size:11px; color:var(--success-text, #166534); background:var(--success-soft); border-radius:var(--radius-pill); padding:4px 12px; font-weight:600; }
        .sg-note { font-size:12px; line-height:1.5; color:var(--text); background:var(--bg-subtle); border:1px solid var(--border); border-radius:var(--radius-sm); padding:11px 13px; margin:10px 0; }
        .sg-chips { display:flex; flex-wrap:wrap; gap:6px; margin:10px 0; }
        .sg-chip { display:inline-flex; align-items:center; gap:5px; font-size:10px; font-weight:600; border:1px solid var(--border); border-radius:var(--radius-pill); padding:3px 10px; color:var(--text-muted); background:var(--card); }
        .sg-chip.add { color:var(--info); border-color:#bfdbfe; background:#eff6ff; }
        .sg-area-h { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); margin:14px 0 5px; }
        .sg-item { font-size:12.5px; padding:7px 0; border-bottom:1px solid var(--border-soft, #f1f5f9); display:flex; gap:8px; align-items:baseline; color:var(--text); }
        .sg-item:last-child { border-bottom:none; }
        .sg-item .new { font-size:9px; font-weight:700; color:var(--info); text-transform:uppercase; letter-spacing:0.04em; flex:none; }
        /* Feature C — per-item provenance chip (which signal introduced it). */
        .sg-src { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.03em; flex:none; border-radius:var(--radius-pill); padding:2px 8px; border:1px solid var(--border); color:var(--text-muted); background:var(--card); white-space:nowrap; align-self:center; }
        .sg-src.standard { color:#475569; border-color:#e2e8f0; background:#f8fafc; }
        .sg-src.hostaway { color:#1d4ed8; border-color:#bfdbfe; background:#eff6ff; }
        .sg-src.pds { color:#7c3aed; border-color:#ddd6fe; background:#f5f3ff; }
        .sg-src.baseline { color:#0e7490; border-color:#a5f3fc; background:#ecfeff; }
        .sg-src.recurring { color:#b45309; border-color:#fde68a; background:#fffbeb; }
        .sg-src.quirk { color:#9d174d; border-color:#fbcfe8; background:#fdf2f8; }
        .sg-src.manual { color:#3730a3; border-color:#bfd7fe; background:#eaf2ff; }
        .sg-additem { display:flex; gap:6px; align-items:center; flex-wrap:wrap; margin-top:12px; padding:10px; border:1px dashed var(--border); border-radius:var(--radius); background:var(--bg-subtle); }
        .sg-additem input[type=text], .sg-additem input:not([type]) { flex:1; min-width:140px; padding:6px 9px; border:1px solid var(--border); border-radius:6px; font-size:12.5px; }
        .sg-add-flag { display:inline-flex; align-items:center; gap:4px; font-size:11px; font-weight:600; color:var(--text-muted); white-space:nowrap; }
        .sg-must { font-size:9px; font-weight:800; letter-spacing:.04em; color:#9d174d; background:#fce7f3; border:1px solid #fbcfe8; border-radius:var(--radius-pill); padding:1px 7px; flex:none; align-self:center; }
        .sg-photo { font-size:10px; font-weight:600; color:#9d174d; flex:none; align-self:center; white-space:nowrap; }
        .insp-must { font-size:9px; font-weight:800; letter-spacing:.04em; color:#9d174d; background:#fce7f3; border:1px solid #fbcfe8; border-radius:var(--radius-pill); padding:1px 6px; vertical-align:middle; }
        .insp-photoreq { color:#9d174d !important; font-weight:700; }
        .insp-photoreq.missing { font-size:11px; font-weight:700; color:#b91c1c; }
        .insp-item-req .ttl { font-weight:600; }
        .sg-baseline { border:1px dashed var(--border); border-radius:var(--radius); padding:13px; margin-top:12px; background:var(--bg-subtle); }
        .sg-baseline .lbl { display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:600; color:var(--text); cursor:pointer; }
        .sg-baseline-msg { font-size:11px; line-height:1.45; margin-top:8px; border-radius:var(--radius-sm); padding:8px 10px; }
        .sg-baseline-msg.ok { color:var(--success-text, #166534); background:var(--success-soft); }
        .sg-baseline-msg.warn { color:#92400e; background:#fffbeb; border:1px solid #fde68a; }
        .sg-baseline-msg.err { color:#b91c1c; background:#fef2f2; border:1px solid #fecaca; }
        .sg-actions { display:flex; gap:8px; flex-wrap:wrap; margin-top:14px; }

        /* ── Phase 3: condition-history modal ─────────────────────────── */
        .ch-grade-row { display:flex; align-items:center; gap:16px; flex-wrap:wrap; }
        .ch-grade-num { font-size:36px; font-weight:600; line-height:1; color:var(--text); letter-spacing:-0.02em; font-variant-numeric:tabular-nums; }
        .ch-delta { font-size:12px; font-weight:600; padding:3px 10px; border-radius:var(--radius-pill); }
        .ch-delta.up { background:var(--success-soft); color:var(--success-text, #166534); }
        .ch-delta.down { background:var(--danger-soft); color:var(--danger-text, #b91c1c); }
        .ch-delta.flat { background:var(--bg-subtle); color:var(--text-muted); }
        .ch-spark { flex:none; }
        .ch-meta { font-size:11px; color:var(--text-muted); line-height:1.5; }
        .ch-sec-h { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); margin:20px 0 7px; }
        .ch-rec { display:flex; align-items:center; gap:10px; padding:11px 0; border-bottom:1px solid var(--border-soft, #f1f5f9); }
        .ch-rec:last-child { border-bottom:none; }
        .ch-rec-main { flex:1; min-width:0; }
        .ch-rec-ttl { font-size:12.5px; font-weight:600; color:var(--text); }
        .ch-rec-sub { font-size:11px; color:var(--text-muted); margin-top:2px; }
        .ch-rec-pill { font-size:10px; font-weight:700; padding:2px 9px; border-radius:var(--radius-pill); flex:none; }
        .ch-rec-pill.rec { background:var(--danger-soft); color:var(--danger-text, #991b1b); }
        .ch-rec-pill.once { background:var(--bg-subtle); color:var(--text-muted); }
        .ch-appl { display:flex; align-items:baseline; gap:10px; padding:9px 0; border-bottom:1px solid var(--border-soft, #f1f5f9); }
        .ch-appl:last-child { border-bottom:none; }
        .ch-appl-name { font-size:12.5px; font-weight:600; color:var(--text); flex:none; min-width:120px; }
        .ch-appl-model { font-size:11px; color:var(--text-muted); }

        /* ── AI report card ──────────────────────────────────────────── */
        .rc-grade-row { display:flex; align-items:center; gap:16px; }
        .rc-ring { flex:none; }
        .rc-grade-meta { min-width:0; }
        .rc-grade-num { font-size:36px; font-weight:600; line-height:1; color:var(--text); letter-spacing:-0.02em; font-variant-numeric:tabular-nums; }
        .rc-grade-lbl { font-size:11px; color:var(--text-muted); margin-top:6px; line-height:1.45; }
        .rc-area-h { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); margin:18px 0 7px; }
        .rc-item { display:flex; gap:12px; padding:14px 0; border-bottom:1px solid var(--border-soft, #f1f5f9); }
        .rc-item:last-child { border-bottom:none; }
        .rc-ev { flex:none; }
        .rc-ev img { width:60px; height:60px; border-radius:var(--radius-sm); object-fit:cover; border:1px solid var(--border); cursor:zoom-in; display:block; }
        .rc-ev .none { width:60px; height:60px; border-radius:var(--radius-sm); border:1px dashed var(--border); display:flex; align-items:center; justify-content:center; font-size:9px; color:var(--text-faint, #9ca3af); text-align:center; }
        .rc-item-main { flex:1; min-width:0; }
        .rc-item-ttl { font-size:13px; font-weight:600; color:var(--text); }
        .rc-item-note { font-size:12px; color:var(--text-muted); margin-top:3px; line-height:1.5; }
        .rc-item-row2 { display:flex; align-items:center; gap:8px; margin-top:8px; flex-wrap:wrap; }
        .rc-conf { font-size:10px; font-weight:600; padding:2px 9px; border-radius:var(--radius-pill); background:var(--bg-subtle); color:var(--text-muted); }
        .rc-src { font-size:10px; font-weight:600; color:var(--info); }
        .rc-seg { display:inline-flex; border:1px solid var(--border); border-radius:var(--radius-sm); overflow:hidden; }
        .rc-seg button { font-size:11px; font-weight:600; padding:7px 12px; border:none; background:var(--card); cursor:pointer; color:var(--text-muted); border-right:1px solid var(--border); min-height:34px; transition:background .12s ease, color .12s ease; }
        .rc-seg button:last-child { border-right:none; }
        .rc-seg button.on.pass { background:var(--success-soft); color:var(--success-text, #166534); }
        .rc-seg button.on.fail { background:var(--danger-soft); color:var(--danger-text, #b91c1c); }
        .rc-seg button.on.na { background:var(--bg-subtle); color:var(--text); }
        .rc-callout { border-radius:var(--radius); padding:14px 16px; margin-bottom:14px; }
        .rc-callout.gaps { background:var(--warning-soft); border:1px solid #fde68a; }
        .rc-callout.ai { background:var(--accent-soft, #eaf2ff); border:1px solid var(--accent-border, #bfd7fe); font-size:13px; line-height:1.5; }
        .rc-callout.gaps .h { font-size:12px; font-weight:600; color:var(--warning-text, #92400e); margin-bottom:6px; }
        .rc-callout.gaps ul { margin:0; padding-left:18px; font-size:12px; color:var(--warning-text, #92400e); line-height:1.5; }
        .rc-issue { display:flex; gap:12px; padding:14px; border:1px solid var(--border); border-radius:var(--radius); margin-bottom:10px; }
        .rc-issue img { width:60px; height:60px; border-radius:var(--radius-sm); object-fit:cover; border:1px solid var(--border); cursor:zoom-in; flex:none; }
        .rc-issue-main { flex:1; min-width:0; }
        .rc-issue-tags { display:flex; gap:6px; align-items:center; margin-bottom:5px; flex-wrap:wrap; }
        .rc-issue-note { font-size:12px; line-height:1.5; color:var(--text); }
        .rc-issue-act { margin-top:8px; }
        .rc-sev { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; padding:2px 9px; border-radius:var(--radius-pill); }
        .rc-sev.high { background:var(--danger-soft); color:var(--danger-text, #991b1b); }
        .rc-sev.med { background:var(--warning-soft); color:var(--warning-text, #92400e); }
        .rc-sev.low { background:var(--bg-subtle); color:var(--text-muted); }
        .rc-type { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.04em; color:var(--text-muted); border:1px solid var(--border); border-radius:var(--radius-pill); padding:2px 9px; }
        .rc-phase2 { padding:7px 12px; border:1px dashed var(--border); border-radius:var(--radius-sm); font-size:11px; font-weight:600; background:var(--bg-subtle); color:var(--text-muted); cursor:not-allowed; }
        /* issue actions (Phase 2) */
        .rc-act-row { display:flex; flex-wrap:wrap; gap:6px; }
        .rc-closet-row { display:flex; align-items:center; gap:8px; flex-wrap:wrap; font-size:11.5px; margin:4px 0 6px; }
        .rc-closet-l { font-weight:700; color:#92400e; background:#fef3c7; border-radius:999px; padding:1px 8px; }
        .rc-closet-chips { display:inline-flex; gap:4px; align-items:center; color:var(--text-muted); }
        .rc-closet { border:1px solid var(--border); background:#fff; border-radius:999px; padding:2px 9px; font-size:11px; cursor:pointer; color:var(--text-muted); font-family:inherit; }
        .rc-closet.on { background:var(--text,#111827); color:#fff; border-color:var(--text,#111827); }
        .rc-act-btn { padding:7px 12px; min-height:36px; border:1px solid var(--border); border-radius:var(--radius-sm); font-size:11px; font-weight:600; background:var(--card); color:var(--text); cursor:pointer; transition:border-color .15s ease, background .15s ease; }
        .rc-act-btn:hover { border-color:var(--text-muted); background:var(--bg-subtle); }
        .rc-act-btn.primary { background:var(--text); color:#fff; border-color:var(--text); }
        .rc-act-btn.primary:hover { background:var(--text); opacity:0.88; }
        .rc-act-btn:disabled { opacity:0.55; cursor:progress; }
        .rc-acted { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
        .rc-acted-mark { font-size:12px; font-weight:700; color:var(--success-text, #166534); }
        .rc-acted.dismissed .rc-acted-mark { color:var(--text-muted); }
        .rc-acted-link { font-size:11px; font-weight:600; color:var(--info); cursor:pointer; }
        .rc-acted-link:hover { text-decoration:underline; }
        /* R120: the Dismissed (N) group, its reason chips and Undo; the stocking annotations */
        .rc-dis { border:1px solid var(--border); border-radius:var(--radius); margin-top:4px; background:var(--bg-subtle); }
        .rc-dis-h { cursor:pointer; padding:10px 14px; min-height:36px; box-sizing:border-box; font-size:12px; font-weight:700; color:var(--text-muted); list-style-position:inside; }
        .rc-dis-row { padding:10px 14px; border-top:1px solid var(--border); }
        .rc-dis-row.just { background:var(--card); }
        .rc-dis-note { font-size:12px; line-height:1.5; color:var(--text-muted); overflow-wrap:anywhere; }
        .rc-dis-meta { font-size:11.5px; color:var(--text-muted); margin-top:3px; overflow-wrap:anywhere; }
        .rc-dis-acts { display:flex; flex-wrap:wrap; gap:6px; align-items:center; margin-top:6px; }
        .rc-reasons { display:inline-flex; flex-wrap:wrap; gap:6px; }
        .rc-reason { min-height:36px; padding:6px 12px; border:1px solid var(--border); border-radius:var(--radius-pill); background:var(--card); color:var(--text-muted); font-size:11.5px; font-weight:600; cursor:pointer; font-family:inherit; }
        .rc-reason.on { border-color:var(--accent-border, #bfd7fe); background:var(--accent-soft, #eaf2ff); color:var(--accent); }
        .rc-undo { min-height:36px; padding:6px 14px; border:1px solid var(--accent-border, #bfd7fe); border-radius:var(--radius-sm); background:var(--card); color:var(--accent); font-size:12px; font-weight:700; cursor:pointer; font-family:inherit; margin-left:auto; }
        .rc-reason:disabled, .rc-undo:disabled { opacity:0.55; cursor:progress; }
        .rc-dis-office { font-size:11px; color:var(--text-muted); margin-left:auto; }
        .rc-item-stock { font-size:11.5px; font-weight:600; color:var(--success-text, #166534); margin-top:3px; }
        .rc-gap-ok { color:var(--success-text, #166534); font-weight:600; }
        .rc-actions { display:flex; gap:8px; flex-wrap:wrap; margin-top:16px; position:sticky; bottom:0; background:linear-gradient(to top, var(--bg, #f8fafc) 60%, transparent); padding:14px 0 4px; }
        .cap-lightbox { position:fixed; inset:0; background:rgba(15,23,42,0.85); z-index:300; display:flex; align-items:center; justify-content:center; padding:24px; cursor:zoom-out; }
        .cap-lightbox img { max-width:96vw; max-height:92vh; border-radius:var(--radius); }
        .emg-bar { display:flex; align-items:center; gap:8px; flex-wrap:wrap; background:#fef2f2; border:1px solid #fecaca; border-radius:12px; padding:10px 12px; margin:0 0 12px; }
        .emg-t { font-size:12.5px; font-weight:700; color:#b91c1c; }
        .emg-btn { display:inline-flex; align-items:center; justify-content:center; min-height:40px; padding:6px 16px; border-radius:10px; background:#dc2626; color:#fff; font-size:13px; font-weight:700; text-decoration:none; }
        .emg-num { font-size:12.5px; font-weight:600; color:#b91c1c; }
        /* ── R26 F: mobile-first pass — 44px targets, safe areas, no page-level
           horizontal scroll at 375px. Inputs get 16px on small screens so iOS
           Safari stops zooming the page on focus mid-walkthrough. ── */
        .cap-analyze-bar { padding-bottom: calc(4px + env(safe-area-inset-bottom)); }
        .rc-actions { padding-bottom: calc(4px + env(safe-area-inset-bottom)); }
        .cap-bar-2 { display:flex; gap:8px; align-items:stretch; }
        .cap-bar-2 .cap-analyze-btn { flex:1; width:auto; }
        .cap-exit-btn { flex:none; padding:12px 16px; border-radius:12px; border:1px solid var(--border); background:var(--card); font-size:13.5px; font-weight:600; cursor:pointer; color:var(--text); min-height:44px; }
        .insp-ip-badge { font-size:11px; font-weight:700; color:var(--accent); margin-top:2px; }
        .cap-step { min-height:44px; }
        .frm-seg { min-height:44px; }
        .frm-seg.ten { min-width:44px; min-height:44px; }
        .frm-seg-row { flex-wrap:wrap; }
        .rc-grade-row { flex-wrap:wrap; }
        .rc-seg button { min-height:44px; }
        .rc-act-btn { min-height:44px; }
        .iss-step-fu { min-height:40px; }
        @media (max-width:760px) {
          .insp-tabs { overflow-x:auto; -webkit-overflow-scrolling:touch; scrollbar-width:none; }
          .insp-tabs::-webkit-scrollbar { display:none; }
          .insp-tab { padding:11px 12px; white-space:nowrap; flex:none; }
          .q-head { flex-direction:column; align-items:stretch; }
          .q-days { display:flex; width:100%; }
          .q-day-btn { flex:1; padding:11px 4px; }
          .insp-form input, .insp-form select, .iss-step-in, .iss-step-fu, .cap-transcript, .insp-assign, .frm-note { font-size:16px; }
          .insp-form select { max-width:100%; }
          #rc-complete { min-height:48px; flex:1; }
          /* R33 #2: give the embedded quick-logger its width back on phones —
             18px card padding was squeezing the composer to ~277px. */
          .cap-card { padding:14px 12px; }
          .cap-wrap { max-width:none; }
        }
        /* ── R115 mobile (CSS only): the queue's window tabs wrapped "Today's
           Inspections · 9" onto three lines — they are one scrolling line now;
           the property name in a stacked queue card reads left-aligned. */
        @media (max-width:640px) {
          .q-days { overflow-x:auto; -webkit-overflow-scrolling:touch; scrollbar-width:none; }
          .q-days::-webkit-scrollbar { display:none; }
          .q-day-btn { flex:none; white-space:nowrap; padding:10px 12px; }
          table.insp-tbl td.insp-name { justify-content:flex-start; text-align:left !important; }
        }
      </style>`;
  }

  // ── tab 0: turnover queue (landing view) ────────────────────
  // Days-to-checkout from the queue's as_of date (whole days), so "in Nd" is
  // computed against the same clock the server used.
  private daysToCheckout(checkoutISO: string): number | null {
    const asOf = this.queue?.as_of;
    if (!checkoutISO || !asOf) return null;
    const a = new Date(asOf + 'T00:00:00Z').getTime();
    const c = new Date(checkoutISO + 'T00:00:00Z').getTime();
    if (isNaN(a) || isNaN(c)) return null;
    return Math.round((c - a) / 86400000);
  }
  // R26 D: the in-flight inspection for a listing (if any) + its ticket count,
  // so queue rows can offer Resume instead of Start. Only RECENT ones count —
  // a months-old abandoned in_progress record must not hijack Start forever.
  // Stacy 2026-10-07: with a checkout date, only an inspection begun ON/AFTER
  // that checkout belongs to this turnover — last week's unfinished one (the
  // AI-credits outage left ~20) must not hijack today's row as "Resume"; it
  // lives in "Unfinished from earlier visits" instead.
  private inProgressFor(listingId: any, sinceDay?: string | null): { insp: any; n: number } | null {
    const cutoff = Date.now() - 7 * 86400000;
    // R37: a REOPENED old inspection counts from its reopen time, not creation —
    // otherwise the queue row silently reverts to Start and mints a duplicate.
    const activeAt = (i: any) => Math.max(new Date(i.created_at || 0).getTime(), new Date(i.reopened?.at || 0).getTime());
    const cands = this.inspections.filter((i: any) =>
      i.status === 'in_progress'
      && String(i.listing_id) === String(listingId)
      && (sinceDay
        ? (this.localDay(this.visitAt(i)) >= sinceDay || (!!i.reopened?.at && this.localDay(i.reopened.at) >= sinceDay))   // R37: a reopen in this turnover resumes too
        : activeAt(i) >= cutoff));
    if (!cands.length) return null;
    const ip = cands.sort((a: any, b: any) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0];
    const n = (ip.logged_issues || []).length + (ip.issues || []).filter((x: any) => x && x.source === 'inspector').length;
    return { insp: ip, n };
  }
  private urgencyPill(u: string): string {
    const map: Record<string, [string, string, string]> = {
      high: ['var(--danger-soft)', 'var(--danger-text, #991b1b)', 'High'],
      med: ['var(--warning-soft)', 'var(--warning-text, #92400e)', 'Med'],
      low: ['var(--bg-subtle)', 'var(--text-muted)', 'Low'],
    };
    const [bg, fg, label] = map[u] || map.low;
    return `<span class="insp-pill" style="background:${bg}; color:${fg};">${label}</span>`;
  }

  // Logged-in role (client-side UI gate). Shared by the field-view branches.
  static fieldRole(): string { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } }
  private static profileName(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.full_name || p.name || '').trim(); } catch { return ''; }
  }
  private static profileId(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.id || '').trim(); } catch { return ''; }
  }

  // R5 #1 — INSPECTOR FIELD VIEW. A single-column "what to inspect today" screen:
  // resume any in-progress inspection, then today's/upcoming turnover queue as
  // tap-to-start cards. Reuses the queue data + the shared capture→report flow;
  // no manager tabs/analytics/templates. Renders at ~390px.
  private drawInspectorMobile(body: HTMLElement): void {
    const name = InspectionsPage.profileName();
    // R64 (Stacy): default to HER work — her own in-progress inspections and
    // queue rows assigned to her. "Show all" reveals everything; if nothing
    // is hers (no assignments yet), fall back to all so the screen is never
    // uselessly empty.
    const nameLc = name.trim().toLowerCase();
    // R120 (contract C6): "mine" = my profile name OR any roster name linked
    // to my login (/api/me inspector_names — "Lacey(IHC)" for Lacey)
    const mine = new Set([nameLc, ...this.myInspectorNames.map(n => n.toLowerCase())].filter(Boolean));
    const mineInsp = (i: any) => !mine.size || mine.has(String(i.inspector || '').trim().toLowerCase());
    const mineRow = (r: any) => !mine.size || mine.has(String(r.assigned_inspector || '').trim().toLowerCase());
    const inProgressAll = (this.inspections || []).filter((i: any) => i.status === 'in_progress');
    const rowsAllRaw: any[] = (this.queue && this.queue.rows) || [];
    const hasMine = inProgressAll.some(mineInsp) || rowsAllRaw.some(mineRow);
    const showAll = this.mobShowAll || !hasMine;
    const inProgress = showAll ? inProgressAll : inProgressAll.filter(mineInsp);
    const rowsAll = showAll ? rowsAllRaw : rowsAllRaw.filter(mineRow);
    const hiddenN = (inProgressAll.length - inProgress.length) + (rowsAllRaw.length - rowsAll.length);
    // Donielle: name filter (search box) + missed rollovers float first within their day.
    const qSearch = this.mobSearch.trim().toLowerCase();
    // roll_to = the day a row sits on (Today for a missed one; the checkout
    // for an early-walked one — R114 review), else its own date
    const qd = (r: any) => r.roll_to || (r.type === 'checkin_light' ? (r.checkin_date || r.checkout_date) : r.checkout_date);
    const rows = rowsAll
      .filter(r => !qSearch || String(r.name || '').toLowerCase().includes(qSearch))
      .sort((a, b) => qd(a) === qd(b) ? Number(!!b.missed) - Number(!!a.missed) : 0);

    // today's walks first; anything begun on an earlier day is an unfinished
    // EARLIER visit — finishing it records it for that day, not today
    const todayLocal = this.localDay(new Date().toISOString());
    // a record with no photos yet isn't dated to an earlier visit — the server
    // dates it by its first photo, which would be today
    const isOld = (i: any) => this.evidenceCount(i) > 0 && this.localDay(this.visitAt(i)) < todayLocal;
    const curIp = inProgress.filter((i: any) => !isOld(i));
    const oldIp = inProgress.filter((i: any) => isOld(i))
      .sort((a: any, b: any) => String(this.visitAt(b) || '').localeCompare(String(this.visitAt(a) || '')));
    // R112: HostBuddy card — pale icon square, name, one-line status, chevron.
    // The whole card is the tap target (data-insp-open), nothing else on it.
    const ipCard = (i: any, old: boolean) => `
        <div class="ifld-card ifld-card-go" data-insp-open="${this.esc(i.id)}">
          <span class="ifld-ic ${old ? 'old' : ''}">${icon(old ? 'flag' : 'clock', 18)}</span>
          <div class="ifld-card-main">
            <div class="ifld-card-ttl">${this.esc(i.listing_name || i.listing_id)}</div>
            <div class="ifld-card-sub">${old ? `Visit on ${this.fmtDate(this.visitAt(i))} — finish it and it's recorded for that day, not today` : `In progress · started ${this.fmtDate(i.created_at)}`}</div>
          </div>
          <span class="ifld-go">${old ? 'Finish' : 'Resume'} →</span>
        </div>`;
    const resume = (curIp.length ? `
      <div class="ifld-sec-h">Resume</div>
      ${curIp.map((i: any) => ipCard(i, false)).join('')}` : '')
      + (oldIp.length ? `
      <div class="ifld-sec-h">Unfinished from earlier visits (${oldIp.length})</div>
      ${oldIp.map((i: any) => ipCard(i, true)).join('')}` : '');

    // R37: recently-completed inspections stay reachable so a wrongly-closed
    // one can be reopened from its report card ("no way to go back and
    // complete my work") — 3-day window, newest first.
    const doneCut = Date.now() - 3 * 86400000;
    const recentDone = (this.inspections || [])
      .filter((i: any) => i.status === 'complete' && new Date(i.completed_at || 0).getTime() >= doneCut)
      .sort((a: any, b: any) => String(b.completed_at || '').localeCompare(String(a.completed_at || '')))
      .slice(0, 10);
    const doneSec = recentDone.length ? `
      <div class="ifld-sec-h">Completed — last 3 days (tap to review / reopen)</div>
      ${recentDone.map((i: any) => `
        <div class="ifld-card ifld-card-go" data-insp-open="${this.esc(i.id)}">
          <span class="ifld-ic done">${icon('file', 18)}</span>
          <div class="ifld-card-main">
            <div class="ifld-card-ttl">${this.esc(i.listing_name || i.listing_id)}</div>
            <div class="ifld-card-sub">Completed ${this.fmtDate(i.completed_at)}${i.inspector ? ` · ${this.esc(i.inspector)}` : ''}</div>
          </div>
          <span class="ifld-go">Report →</span>
        </div>`).join('')}` : '';

    // R112: a three-up glance strip (main.css .kpi-card at phone density) over
    // the SAME scope as the cards below (Mine / Everyone), never the search.
    const dueToday = rowsAll.filter(r => {
      if (r.inspected_today || r.skip) return false;
      const d = this.daysToCheckout(qd(r));
      return d != null && d <= 0;
    }).length;
    const doneToday = (this.inspections || []).filter((i: any) =>
      i.status === 'complete' && this.localDay(i.completed_at) === todayLocal && (showAll || mineInsp(i))).length;
    const kpi = (label: string, ico: string, v: number, cls = '') => `
      <div class="kpi-card ${cls}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(ico, 14)}</span></div>
        <div class="kpi-card-value">${v}</div>
      </div>`;
    const kpis = `
      <div class="kpi-grid ifld-kpis">
        ${kpi('Due today', 'flag', dueToday, dueToday > 0 ? 'tone-warn' : '')}
        ${kpi('In progress', 'clock', inProgress.length)}
        ${kpi('Done today', 'star', doneToday, doneToday > 0 ? 'tone-good' : '')}
      </div>`;

    // One property per card, name first and unmissable; the facts sit below as
    // separate labeled chips instead of one crammed dot-joined string (R17 PDF:
    // "the list is all jumbled up — hard to see the names of the properties").
    // R112: chips are token pills (semantic hot/warn/ok kept), the ONE primary
    // action per card sits in its own full-width action row (46px tap target).
    const queueCards = rows.length ? rows.map(r => {
      // Light check-in rows (already inspected while empty) deadline on the
      // ARRIVAL, not a checkout — date + wording follow suit.
      const light = r.type === 'checkin_light';
      const dToCo = this.daysToCheckout(r.walked_early ? r.post_checkout_due : (light ? (r.checkin_date || r.checkout_date) : r.checkout_date));
      const word = light ? 'Check-in' : (r.walked_early ? 'Post-checkout check' : (r.type === 'moved' ? 'Due' : 'Checkout'));   // R114: a moved job's date isn't a checkout
      const coIn = dToCo == null ? '' : (dToCo <= 0 ? `${word} today` : `${word} in ${dToCo}d`);
      const never = r.last_inspection_at == null;
      const lastTxt = never ? 'Never inspected' : (r.days_since_inspection == null ? 'Inspected before' : `Inspected ${r.days_since_inspection}d ago`);
      const turn = r.turnaround_days == null ? '' : `${r.turnaround_days}d turnaround`;
      const action = (() => {   // R44 (Mark): done today / skipped / outstanding — at a glance
        const skipKey = `${this.esc(r.listing_id)}|${this.esc(r.checkout_date)}|${this.esc(r.next_checkin || '')}`;
        if (r.skip && r.skip.reason === 'cancelled') return `
            <span class="ifld-chip" title="${this.esc(r.skip.note || 'Cancelled by the office')}">✕ Cancelled by the office</span>`;
        if (r.skip) return `
            <span class="ifld-chip">⏭ ${r.skip.reason === 'early_checkin' ? 'Guest inside' : 'Not possible'}</span>
            <button type="button" class="btn btn-secondary btn-sm ifld-undo" data-q-unskip="${skipKey}">Undo</button>`;
        if (r.inspected_today) return r.last_inspection_id
          ? `<button type="button" class="btn btn-success ifld-start" data-insp-open="${this.esc(r.last_inspection_id)}">✓ Done today</button>`
          : `<span class="ifld-chip ok">✓ Done today</span>`;
        const ip = this.inProgressFor(r.listing_id, (r.turnover_since || (r.type === 'checkin_light' ? null : (r.checkout_date && this.queue?.as_of && r.checkout_date > this.queue.as_of ? this.queue.as_of : r.checkout_date))));
        const main = ip
          ? `<button type="button" class="btn btn-primary ifld-start" data-insp-open="${this.esc(ip.insp.id)}">Resume (${ip.n}) →</button>`
          : `<button type="button" class="btn btn-primary ifld-start" data-q-start="${this.esc(r.listing_id)}|${this.esc(r.name)}${light || r.light ? '|light' : ''}"${InspectionsPage.qForAttr(r)}>Start →</button>`;
        return `${r.eci ? `<span class="ifld-link" data-q-skip="${skipKey}">🚪 Guest inside — skip</span>` : ''}${main}`;
      })();
      return `
        <div class="ifld-card ifld-card-q" data-qname="${this.esc(String(r.name || '').toLowerCase())}">
          <div class="ifld-card-top">
            <div class="ifld-card-ttl">${this.esc(r.name)}</div>
            ${this.urgencyPill(r.urgency)}
          </div>
          <div class="ifld-chips">
            ${r.missed ? '<span class="ifld-chip hot" title="Checkout passed with no completed inspection since — rolled into today">⏰ missed</span>' : ''}
            ${r.vip ? '<span class="ifld-chip pill-amber" title="Next check-in is an owner stay">👑 VIP — owner arriving</span>' : ''}
            ${r.type === 'manual' ? '<span class="ifld-chip pill-purple" title="Scheduled by the office — not tied to a checkout">📌 scheduled</span>' : ''}
            ${r.moved_from ? `<span class="ifld-chip moved" title="Rescheduled by the office — original date ${this.esc(r.moved_from)}">↪ moved from ${this.esc(this.fmtShort(r.moved_from))}</span>` : ''}
            ${r.orphaned ? `<span class="ifld-chip warn" title="No stay checks out on ${this.esc(r.origin_date || r.moved_from || '')} any more — the booking was cancelled or its dates changed. Check with the office before going.">⚠ booking changed</span>` : ''}
            ${r.walked_early ? `<span class="ifld-chip" title="Walked on ${this.esc(r.walked_early)}, before the guest checked out — the inspection after checkout is still owed on ${this.esc(r.post_checkout_due || '')}">👣 Walked ${this.esc(this.fmtShort(r.walked_early))} · after-checkout check ${this.esc(this.fmtShort(r.post_checkout_due))}</span>` : ''}
            ${light ? `<span class="ifld-chip pill-green" title="${this.esc(r.note || '')}">🟢 Light check-in · already clean, go early</span>` : ''}
            ${r.light ? `<span class="ifld-chip pill-green" title="Pre-arrival check — before the ${this.esc(r.checkin_deadline || 'next')} arrival">🟢 Light check-in</span>` : ''}
            ${r.same_day ? '<span class="ifld-chip hot">⚡ same-day</span>' : ''}
            ${r.eci ? `<span class="ifld-chip warn" title="Next guest arrives EARLY">⏰ ECI${r.eci_time ? ` ${this.esc(r.eci_time)}` : ''}</span>` : ''}
            ${r.lco ? `<span class="ifld-chip" title="Departing guest leaves LATE">🌙 LCO${r.lco_time ? ` ${this.esc(r.lco_time)}` : ''}</span>` : ''}
            ${coIn ? `<span class="ifld-chip ${dToCo != null && dToCo <= 0 ? 'hot' : ''}">${this.esc(coIn)}</span>` : ''}
            ${turn ? `<span class="ifld-chip">${this.esc(turn)}</span>` : ''}
            <span class="ifld-chip ${never ? 'warn' : ''}">${this.esc(lastTxt)}</span>
          </div>
          ${r.eci && !r.inspected_today && !r.skip ? '<div class="ifld-flag">⚠ Early check-in flagged — knock / confirm nobody is inside before entering.</div>' : ''}
          ${r.guest_inside && !r.eci && !r.inspected_today && !r.skip ? '<div class="ifld-flag">⚠ A guest is booked in that day — knock / confirm nobody is inside before entering.</div>' : ''}
          <div class="ifld-act">${action}</div>
        </div>`;
    }).join('') : `<div class="ifld-empty">No checkouts in the next ${this.queue?.days ?? this.queueDays} days. Widen the window above or check back.</div>`;

    body.innerHTML = `
      ${this.styleBlock()}
      <style>
        /* R112: inspector field view in the HostBuddy idiom — every colour is a
           :root token, cards on --radius, the one blue action per card. */
        .ifld-head { display:flex; align-items:flex-start; gap:10px; padding:4px 0 12px; }
        .ifld-head h1 { margin:0; font-size:22px; font-weight:700; letter-spacing:-0.02em; color:var(--text); line-height:1.15; }
        .ifld-head .ifld-who { font-size:12.5px; color:var(--text-muted); margin-top:3px; }
        .ifld-head .btn { flex:none; min-height:40px; }
        .kpi-grid.ifld-kpis { grid-template-columns:repeat(3, 1fr); gap:8px; margin:0 0 12px; }
        .ifld-kpis .kpi-card { padding:10px 12px; }
        .ifld-kpis .kpi-card-label { font-size:10px; letter-spacing:.05em; }
        .ifld-kpis .kpi-card-ic { width:28px; height:28px; border-radius:8px; }
        .ifld-kpis .kpi-card-ic svg { width:14px; height:14px; }
        .ifld-kpis .kpi-card-value { font-size:22px; margin-top:6px; }
        .seg-tabs.ifld-seg { display:flex; width:100%; box-sizing:border-box; margin:0 0 10px; }
        .ifld-seg .seg-tab { flex:1; justify-content:center; min-height:40px; font-size:12.5px; }
        .btn.ifld-wide { width:100%; justify-content:center; min-height:46px; font-size:13.5px; box-sizing:border-box; margin:0 0 10px; }
        .ifld-sec-h { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.09em; color:var(--text-faint); margin:18px 2px 10px; }
        .ifld-card { display:flex; align-items:center; gap:12px; background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:14px 16px; margin-bottom:10px; box-sizing:border-box; transition:box-shadow .15s ease, border-color .15s ease, background .12s ease; }
        .ifld-card-go { cursor:pointer; min-height:66px; }
        .ifld-card-go:hover { background:var(--bg-subtle); border-color:var(--border-strong); }
        .ifld-card-go:active { box-shadow:none; }
        .ifld-card-q { display:block; }
        .ifld-ic { width:38px; height:38px; border-radius:11px; display:inline-flex; align-items:center; justify-content:center; background:var(--accent-soft); color:var(--accent); flex:none; }
        .ifld-ic svg { width:18px; height:18px; }
        .ifld-ic.done { background:var(--success-soft); color:var(--success-text); }
        .ifld-ic.old { background:var(--warning-soft); color:var(--warning-text); }
        .ifld-card-main { flex:1; min-width:0; }
        .ifld-card-top { display:flex; align-items:flex-start; gap:8px; justify-content:space-between; }
        .ifld-card-top .insp-pill { flex:none; margin-top:2px; }
        .ifld-card-ttl { font-size:16px; font-weight:700; line-height:1.3; letter-spacing:-0.01em; overflow-wrap:anywhere; color:var(--text); }
        .ifld-card-sub { font-size:12px; color:var(--text-muted); margin-top:3px; line-height:1.4; }
        .ifld-chips { display:flex; gap:6px; flex-wrap:wrap; margin-top:8px; }
        .ifld-chip { display:inline-flex; align-items:center; gap:4px; font-size:11px; font-weight:600; padding:3px 9px; border-radius:var(--radius-pill); background:var(--bg-subtle); color:var(--text-muted); border:1px solid var(--border); white-space:nowrap; line-height:1.5; }
        .ifld-chip.hot { background:var(--danger-soft); color:var(--danger-text); border-color:var(--danger-border); }
        .ifld-chip.warn { background:var(--warning-soft); color:var(--warning-text); border-color:transparent; }
        .ifld-chip.ok { background:var(--success-soft); color:var(--success-text); border-color:var(--success-border); font-weight:700; }
        .ifld-chip.pill-amber, .ifld-chip.pill-purple, .ifld-chip.pill-green { border-color:transparent; }
        .ifld-chip.moved { background:var(--accent-soft); color:var(--accent); border-color:transparent; }
        .ifld-flag { font-size:12px; font-weight:700; color:var(--warning-text); margin-top:8px; line-height:1.4; }
        .ifld-act { display:flex; align-items:center; justify-content:flex-end; gap:12px; flex-wrap:wrap; margin-top:12px; }
        .btn.ifld-start { flex:none; min-height:46px; min-width:128px; justify-content:center; font-size:14px; font-weight:700; padding:0 18px; }
        .btn.ifld-undo { min-height:36px; }
        .ifld-link { display:inline-flex; align-items:center; min-height:44px; padding:0 4px; font-size:12.5px; font-weight:700; color:var(--warning-text); cursor:pointer; margin-right:auto; }
        .ifld-go { font-size:13px; font-weight:700; color:var(--accent); white-space:nowrap; flex:none; }
        .ifld-empty { font-size:13px; color:var(--text-muted); text-align:center; padding:30px 16px; background:var(--bg-subtle); border:1px dashed var(--border); border-radius:var(--radius); }
        .ifld-days { display:flex; gap:6px; margin:0 0 8px; }
        .ifld-days .chip { flex:1; justify-content:center; min-height:38px; }
        .ifld-search { position:relative; display:flex; align-items:center; color:var(--text-faint); margin:0 0 10px; }
        .ifld-search svg { position:absolute; left:12px; pointer-events:none; }
        .ifld-search .input { padding:11px 12px 11px 34px; font-size:16px; margin:0; }
        .ifld-any { flex-direction:column; align-items:stretch; gap:8px; }
        .ifld-any .input { font-size:16px; padding:11px 12px; margin:0; }
        .ifld-any .btn { width:100%; }
      </style>
      ${fieldViewSwitcherHtml()}
      <div class="ifld-head">
        <div style="flex:1; min-width:0;">
          <h1>My inspections</h1>
          <div class="ifld-who">${name ? this.esc(name) + ' · ' : ''}inspect before the next guest</div>
        </div>
        <button type="button" class="btn btn-secondary" id="ifld-supplies" title="Request supplies for a property">${icon('package', 15)} Supplies</button>
      </div>
      <div class="emg-bar">
        <span class="emg-t">🚨 Emergency or immediate need?</span>
        <a class="emg-btn" href="tel:+16198155111">📞 Call</a>
        <a class="emg-btn" href="sms:+16198155111">💬 Text</a>
        <span class="emg-num">(619) 815-5111</span>
      </div>
      ${kpis}
      ${this.phoneQueueBanner()}
      ${hasMine ? `<div class="seg-tabs ifld-seg" role="tablist">
        <button type="button" class="seg-tab ${!this.mobShowAll ? 'active' : ''}" id="ifld-mine" role="tab" aria-selected="${!this.mobShowAll ? 'true' : 'false'}">👤 Mine</button>
        <button type="button" class="seg-tab ${this.mobShowAll ? 'active' : ''}" id="ifld-all" role="tab" aria-selected="${this.mobShowAll ? 'true' : 'false'}">👥 Everyone${hiddenN && !this.mobShowAll ? ` (+${hiddenN})` : ''}</button>
      </div>` : ''}
      <button type="button" class="btn btn-secondary ifld-wide" id="ifld-anystart">➕ Start an inspection — any property</button>
      ${this.mobStartOpen ? `
        <div class="ifld-card ifld-any">
          <input class="input" id="ifld-anyprop" list="ifld-proplist" placeholder="Type a property name…" autocomplete="off">
          <datalist id="ifld-proplist">${(this.mobListings || []).map((l: any) => `<option value="${this.esc(l.short_name || l.name || l.id)}"></option>`).join('')}</datalist>
          ${this.mobListings === null ? '<div class="ifld-card-sub" id="ifld-anyload">Loading properties…</div>' : ''}
          <button type="button" class="btn btn-primary ifld-start" id="ifld-anygo">Start basic inspection →</button>
        </div>` : ''}
      ${resume}
      <div class="ifld-sec-h">Due ${inProgress.length ? 'next' : 'today / upcoming'}</div>
      <div class="ifld-days">${[7, 14, 30].map(d => `<button type="button" class="chip ${this.queueDays === d ? 'active' : ''}" data-q-days="${d}" aria-pressed="${this.queueDays === d ? 'true' : 'false'}">${d} days</button>`).join('')}</div>
      <label class="ifld-search">
        ${icon('search', 15)}
        <input class="input" id="ifld-qsearch" type="search" placeholder="Filter by property name…" value="${this.esc(this.mobSearch)}" autocomplete="off">
      </label>
      ${queueCards}
      ${doneSec}`;
    this.bind();
  }

  // Combined clean + inspection calendar (R19 #3): each upcoming checkout shows
  // its cleaning turn (+ cleaner) and its inspection (+ assigned inspector) side
  // by side, grouped by day.
  private renderOpsCalendar(): string {
    if (!this.opsCal) return `<div style="padding:30px; text-align:center;">${LoadingSpinner.render()}</div>`;
    const items: any[] = this.opsCal.items || [];
    if (!items.length) return emptyState('Nothing scheduled', 'No checkouts (and therefore no cleans/inspections) in the next 14 days.', '📅');
    const byDay = new Map<string, any[]>();
    for (const it of items) { if (!byDay.has(it.date)) byDay.set(it.date, []); byDay.get(it.date)!.push(it); }
    const cleanPill = (s: string) => {
      const done = ['cleaned', 'verified', 'ready'].includes(s);
      const c = done ? '#16a34a' : (s === 'in_progress' ? '#b45309' : '#64748b');
      return `<span class="opscal-pill" style="color:${c}; background:${c}1a;">${this.esc(s)}</span>`;
    };
    const inspPill = (i: any) => {
      if (i.status === 'complete') return `<span class="opscal-pill" style="color:#16a34a; background:#16a34a1a;">inspected${i.grade != null ? ` · ${i.grade}` : ''}</span>`;
      if (i.status === 'in_progress') return `<span class="opscal-pill" style="color:#b45309; background:#b453091a;">in progress</span>`;
      if (i.assigned_inspector) return `<span class="opscal-pill" style="color:#1d4ed8; background:#1d4ed81a;">assigned</span>`;
      return `<span class="opscal-pill" style="color:#64748b; background:#64748b1a;">not assigned</span>`;
    };
    const days = [...byDay.keys()].sort();
    return `
      <style>
        .opscal-day { margin-bottom:16px; }
        .opscal-date { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin:0 0 8px; }
        .opscal-row { display:grid; grid-template-columns:1.4fr 1fr 1fr; gap:12px; align-items:center; padding:10px 12px; border:1px solid var(--border); border-radius:10px; margin-bottom:6px; background:var(--card,#fff); }
        .opscal-prop { font-weight:600; font-size:13.5px; }
        .opscal-lab { font-size:10px; font-weight:700; text-transform:uppercase; color:var(--text-faint); }
        .opscal-who { font-size:12.5px; }
        .opscal-pill { display:inline-block; font-size:10.5px; font-weight:700; padding:2px 8px; border-radius:999px; margin-left:4px; }
        @media (max-width:760px) { .opscal-row { grid-template-columns:1fr; gap:6px; } }
      </style>
      <div class="insp-basis" style="margin:0 0 12px;">Next 14 days · cleaning turn + inspection for each checkout, side by side.</div>
      ${days.map(d => `
        <div class="opscal-day">
          <div class="opscal-date">${this.esc(this.fmtDate(d))}${d === this.opsCal.today ? ' · today' : ''} <span style="color:var(--text-faint); font-weight:400;">${byDay.get(d)!.length} propert${byDay.get(d)!.length === 1 ? 'y' : 'ies'}</span></div>
          ${byDay.get(d)!.map(it => `
            <div class="opscal-row">
              <div><div class="opscal-prop">${this.esc(it.listing_name)}</div>${it.same_day ? '<span class="opscal-pill" style="color:#b91c1c; background:#b91c1c1a;">same-day</span>' : ''}</div>
              <div><div class="opscal-lab">🧹 Clean</div><div class="opscal-who">${this.esc((it.clean.cleaners || []).join(' + ') || 'unassigned')} ${cleanPill(it.clean.status)}</div></div>
              <div><div class="opscal-lab">🔍 Inspect</div><div class="opscal-who">${this.esc(it.inspection.assigned_inspector || '—')} ${inspPill(it.inspection)}${it.inspection.moved_from ? ` <span class="opscal-pill" style="color:var(--accent); background:var(--accent-soft);" title="Rescheduled — original date ${this.esc(it.inspection.moved_from)}">↪ moved from ${this.esc(this.fmtShort(it.inspection.moved_from))}</span>` : ''}${it.inspection.moved_to ? ` <span class="opscal-pill" style="color:var(--accent); background:var(--accent-soft);" title="This inspection was rescheduled away from the clean's date">↪ moved to ${this.esc(this.fmtShort(it.inspection.moved_to))}</span>` : ''}</div></div>
            </div>`).join('')}
        </div>`).join('')}`;
  }

  private renderQueue(): string {
    if (!this.queue) {
      return emptyState('Queue unavailable', 'GET /inspections/queue failed — check the server log.', '⚠️');
    }

    const allRows: any[] = this.queue.rows || [];
    // One fetch, three window views + an exact-date view (R46). Window counts
    // are computed by offset — not rows.length — because a picked far date can
    // widen the fetch horizon past 14d.
    // Light check-in rows (type 'checkin_light') key on checkin_date — the
    // property is already clean; the deadline is the ARRIVAL, not a checkout.
    const qDate = (r: any) => r.roll_to || (r.type === 'checkin_light' ? (r.checkin_date || r.checkout_date) : r.checkout_date);
    const inWindow = (r: any, max: number) => { const d = this.daysToCheckout(qDate(r)); return d != null && d <= max; };
    const counts = {
      today: allRows.filter(r => inWindow(r, 0)).length,
      '7d': allRows.filter(r => inWindow(r, 7)).length,
      '14d': allRows.filter(r => inWindow(r, 14)).length,
    };
    const rows = (this.queueDate ? allRows.filter(r => qDate(r) === this.queueDate)
      : this.queueTab === 'today' ? allRows.filter(r => inWindow(r, 0))
      : this.queueTab === '7d' ? allRows.filter(r => inWindow(r, 7))
      : allRows.filter(r => inWindow(r, 14)))
      // missed rollovers (checkout passed, never inspected) float first within their day
      .sort((a, b) => qDate(a) === qDate(b) ? Number(!!b.missed) - Number(!!a.missed) : 0);

    const maxDate = new Date(new Date(this.queue.as_of + 'T00:00:00Z').getTime() + 60 * 86400000).toISOString().slice(0, 10);
    // Window counts are hidden while a date is picked — the fetch is
    // date-scoped then, so window numbers would be wrong.
    const tabBar = `
      <div class="q-days">
        ${([['today', "Today's Inspections"], ['7d', 'Next 7 days'], ['14d', 'Next 14 days']] as const).map(([k, label]) =>
          `<button class="q-day-btn ${!this.queueDate && this.queueTab === k ? 'on' : ''}" data-q-tab="${k}">${label}${this.queueDate ? '' : ` · ${counts[k]}`}</button>`).join('')}
        <input type="date" class="q-day-btn ${this.queueDate ? 'on' : ''}" data-q-date min="${this.queue.as_of}" max="${maxDate}" value="${this.queueDate || ''}" title="Jump to a specific date">
      </div>`;
    const basis = `checkouts to inspect before the next guest · as of ${this.fmtDate(this.queue.as_of)}`;
    // R64 (office): AD-HOC scheduled inspection — property + date + inspector,
    // lands in that inspector's queue as a 📌 manual row ("add one for Stacy
    // to complete tomorrow"). Uses the existing assignment map w/ manual:true.
    const schedHtml = `
      <button class="q-day-btn" data-sched-toggle style="margin-left:auto;">➕ Schedule an inspection</button>
      ${this.schedOpen ? `
        <div style="flex:1 1 100%; display:flex; gap:8px; flex-wrap:wrap; align-items:center; padding:8px 0;">
          <input id="sched-prop" list="sched-props" autocomplete="off" placeholder="Property…" style="flex:1 1 200px; padding:8px 10px; border:1px solid var(--border); border-radius:8px; font-size:13px;">
          <datalist id="sched-props">${(this.mobListings || []).map((l: any) => `<option value="${this.esc(String(l.short_name || l.name || l.id))}"></option>`).join('')}</datalist>
          <input id="sched-date" type="date" min="${this.esc(this.queue.as_of)}" max="${this.esc(maxDate)}" value="${this.esc(this.queue.as_of)}" style="padding:8px 10px; border:1px solid var(--border); border-radius:8px; font-size:13px;">
          <select id="sched-insp" style="padding:8px 10px; border:1px solid var(--border); border-radius:8px; font-size:13px;">${this.inspectorOpts('')}</select>
          <button class="insp-btn primary" id="sched-go">Schedule</button>
          ${this.mobListings === null ? '<span style="font-size:11.5px; color:var(--text-muted);">Loading properties…</span>' : ''}
        </div>` : ''}`;

    if (!rows.length) {
      return `
        ${this.queueMoveCss()}${this.moveDialogHtml()}
        <div class="q-head">
          <div class="insp-basis" style="margin:0;">${this.esc(basis)}</div>
          ${tabBar}
          <div style="display:flex; flex-wrap:wrap; gap:8px; margin-top:6px;">${schedHtml}</div>
        </div>
        ${this.queueDate
          ? emptyState('No checkouts on this date', `No properties check out on ${this.fmtDate(this.queueDate)} (bookings within the next 60 days show here). Clear the date to go back to the window tabs.`, '📅')
          : this.queueTab === 'today'
          ? emptyState('No inspections today', 'No properties check out today. The 7-day tab shows what’s coming.', '✅')
          : emptyState('No checkouts in this window', 'No properties have a checkout in this window — check back as bookings firm up.', '🧹')}`;
    }

    // R114: moving an inspection is office-only (the server gate is
    // requireOfficeStrict); the queue is the office surface already.
    const canMove = InspectionsPage.isOfficeRole();
    const body = rows.map(r => {
      const light = r.type === 'checkin_light';
      const dToCo = this.daysToCheckout(qDate(r));
      const imminent = dToCo != null && dToCo <= 2;
      const coIn = dToCo == null ? '' : (dToCo <= 0 ? 'today' : `in ${dToCo}d`);
      // Light rows deadline on the ARRIVAL — show checkin_date, labelled so the
      // date column doesn't silently mean two different things.
      // R114: a rescheduled job says where it came from (like a moved clean).
      // (an early-walked job shows its checkout date — its 👣 pill tells the move)
      const movedPill = r.moved_from && !r.walked_early ? `<div><span class="q-moved" title="Rescheduled — original date ${this.esc(r.moved_from)}">↪ moved from ${this.esc(this.fmtShort(r.moved_from))}</span></div>` : '';
      const coCell = light ? `
        <div><span class="mono num">${this.fmtDate(r.checkin_date)}</span></div>
        <div class="insp-sub">check-in${coIn ? ` ${coIn}` : ''}</div>${movedPill}` : `
        <div><span class="mono num">${this.fmtDate(r.walked_early && r.post_checkout_due ? r.post_checkout_due : r.checkout_date)}</span></div>
        ${coIn ? `<div class="insp-sub" style="${imminent ? 'color:var(--danger-text, #b91c1c); font-weight:600;' : ''}">${coIn}</div>` : ''}${movedPill}`;

      const checkinCell = r.next_checkin
        ? `<span class="mono num">${this.fmtDate(r.next_checkin)}</span>`
        : '<span class="insp-sub">open</span>';

      const turn = r.turnaround_days == null
        ? '<span class="insp-sub">open-ended</span>'
        : `<span class="mono num" style="font-weight:600; ${r.turnaround_days <= 1 ? 'color:var(--danger-text, #b91c1c);' : ''}">${r.turnaround_days}d</span>`;

      const never = r.last_inspection_at == null;
      const inspCell = never
        ? '<span class="insp-sub" style="color:var(--danger-text, #b91c1c); font-weight:600;">never</span>'
        : `<span class="mono num">${this.fmtDate(r.last_inspection_at)}</span><div class="insp-sub" style="color:${this.daysHex(r.days_since_inspection)};">${r.days_since_inspection == null ? '' : `${r.days_since_inspection}d ago`}</div>`;

      // light rows have no checkout — their assignment is keyed on the
      // CHECK-IN date (the server emitter reads inspAssign[lid|checkin])
      const assignCell = `<select class="insp-assign" data-q-assign="${this.esc(r.listing_id)}|${this.esc(r.type === 'checkin_light' ? r.checkin_date : r.checkout_date)}">${this.inspectorOpts(r.assigned_inspector || '')}</select>`;
      // R26 D: an interrupted inspection surfaces as resumable right in the queue.
      const ip = this.inProgressFor(r.listing_id, (r.turnover_since || (r.type === 'checkin_light' ? null : (r.checkout_date && this.queue?.as_of && r.checkout_date > this.queue.as_of ? this.queue.as_of : r.checkout_date))));
      const ipBadge = ip ? `<div class="insp-ip-badge">↻ In progress · ${ip.n} issue${ip.n === 1 ? '' : 's'} logged</div>` : '';
      // R44 (Mark): the row must SAY what's already handled — done today,
      // skipped (guest inside), or still outstanding.
      const skipKey = `${this.esc(r.listing_id)}|${this.esc(r.checkout_date)}|${this.esc(r.next_checkin || '')}`;
      // the free-text note sits on its own wrapping line — inside the nowrap
      // pill a long one ran under the ⋯ button
      const actionCell = r.skip
        ? `<span class="insp-pill" style="background:var(--bg-subtle); color:var(--text-muted);">${r.skip.reason === 'cancelled' ? '✕ Cancelled' : '⏭ Not possible'}${r.skip.reason === 'early_checkin' ? ' — guest checked in early' : ''}</span>
           ${r.skip.reason !== 'early_checkin' && r.skip.note ? `<div class="insp-sub q-skipnote" title="${this.esc(r.skip.note)}">${this.esc(r.skip.note)}</div>` : ''}
           <div><span class="insp-link" data-q-unskip="${skipKey}">undo</span></div>`
        : r.inspected_today
          ? `<span class="insp-pill" style="background:var(--success-soft); color:var(--success-text,#166534); font-weight:700;">✓ Inspected today</span>
             ${r.last_inspection_id ? `<div><span class="insp-link" data-insp-open="${this.esc(r.last_inspection_id)}">report →</span></div>` : ''}`
          : ip
            ? `<button class="insp-btn primary" data-insp-open="${this.esc(ip.insp.id)}">Resume →</button>`
            : `<button class="insp-btn primary" data-q-start="${this.esc(r.listing_id)}|${this.esc(r.name)}${light || r.light ? '|light' : ''}"${InspectionsPage.qForAttr(r)}>Start inspection →</button>
               ${r.eci ? `<div style="margin-top:4px;"><span class="insp-link" data-q-skip="${skipKey}" style="color:#b45309;">🚪 not possible — guest inside</span></div>` : ''}`;
      // R114: the ⋯ overflow (shared Kebab) — "↪ Move to date…" opens the move
      // dialog; a moved row can also go straight back to its original date.
      const moveCell = canMove ? this.queueRowKebab(r) : '';
      // R35 #2/#3: same-day is the loud one; ECI/LCO ride alongside.
      const flagBadges = [
        r.priority ? `<span class="opscal-pill" style="color:${r.priority === 'A' ? '#b91c1c' : r.priority === 'B' ? '#b45309' : '#475569'}; background:${r.priority === 'A' ? '#b91c1c14' : r.priority === 'B' ? '#b4530914' : '#47556914'}; font-weight:700;" title="Property priority — with limited resources, A comes first (set in Admin → Properties)">🏷 ${this.esc(r.priority)}</span>` : '',
        r.est_minutes ? `<span class="opscal-pill" style="color:var(--text-muted); background:var(--bg-subtle,#f8fafc);" title="Expected inspection time — ${r.est_source === 'budget' ? 'this property\'s budget' : 'size heuristic'}">⏱ ~${this.esc(r.est_minutes)}m</span>` : '',
        r.missed ? '<span class="opscal-pill" style="color:#b91c1c; background:#b91c1c1a; font-weight:700;" title="Checkout passed with no completed inspection since — rolled into Today">⏰ missed</span>' : '',
        r.vip ? '<span class="opscal-pill" style="color:#a16207; background:#eab3081a; font-weight:700;" title="Next check-in is an owner stay">👑 VIP — owner arriving</span>' : '',
        r.type === 'manual' ? '<span class="opscal-pill" style="color:#6d28d9; background:#6d28d91a; font-weight:700;" title="Scheduled by the office — not tied to a checkout">📌 scheduled</span>' : '',
        r.orphaned ? `<span class="opscal-pill" style="color:var(--danger-text); background:var(--danger-soft); font-weight:700;" title="No stay checks out on ${this.esc(r.origin_date || r.moved_from || '')} any more — the booking was cancelled or its dates changed. ⋯ → Cancel this inspection if it's not needed.">⚠ booking changed</span>` : '',
        r.walked_early ? `<span class="opscal-pill" style="color:var(--text-muted); background:var(--bg-subtle);" title="Walked on ${this.esc(r.walked_early)}, before the guest checked out — the inspection after checkout is still owed on ${this.esc(r.post_checkout_due || '')} (⋯ → Move back puts it on that date)">👣 walked ${this.esc(this.fmtShort(r.walked_early))} · after-checkout check ${this.esc(this.fmtShort(r.post_checkout_due))}</span>` : '',
        light || r.light ? `<span class="opscal-pill" style="color:#15803d; background:#15803d1a; font-weight:700;" title="${this.esc(r.note || (r.light && r.checkin_deadline ? `Pre-arrival check — before the ${r.checkin_deadline} arrival` : 'Already inspected while empty — light pre-check-in pass'))}">🟢 Light check-in</span>` : '',
        r.guest_inside && !r.inspected_today && !r.skip ? '<span class="opscal-pill" style="color:var(--warning-text); background:var(--warning-soft); font-weight:700;" title="A stay overlaps this date — the inspection needs the guest out">🛏 guest inside that day</span>' : '',
        r.same_day ? '<span class="opscal-pill" style="color:#b91c1c; background:#b91c1c1a; font-weight:700;">⚡ same-day</span>' : '',
        r.eci ? `<span class="opscal-pill" style="color:#b45309; background:#b453091a;" title="Next guest arrives EARLY — confirm nobody is inside before entering">⏰ ECI${r.eci_time ? ` ${this.esc(r.eci_time)}` : ''}</span>` : '',
        r.lco ? `<span class="opscal-pill" style="color:#1d4ed8; background:#1d4ed81a;" title="Departing guest leaves LATE">🌙 LCO${r.lco_time ? ` ${this.esc(r.lco_time)}` : ''}</span>` : '',
      ].filter(Boolean).join(' ');
      return `
        <tr>
          <td class="insp-name" data-th="Property" title="${this.esc(r.name)}">${this.esc(r.name)}${flagBadges ? `<div style="margin-top:3px; display:flex; gap:4px; flex-wrap:wrap;">${flagBadges}</div>` : ''}${ipBadge}</td>
          <td data-th="Checkout">${coCell}</td>
          <td data-th="Next check-in">${checkinCell}</td>
          <td data-th="Turnaround" style="text-align:right;">${turn}</td>
          <td data-th="Last inspection">${inspCell}</td>
          <td data-th="Inspector">${assignCell}</td>
          <td data-th="Urgency" style="text-align:center;">${this.urgencyPill(light ? 'low' : r.urgency)}</td>
          <td style="text-align:right;"><div class="q-actrow"><div class="q-actmain">${actionCell}</div>${moveCell}</div></td>
        </tr>`;
    }).join('');

    return `
      ${this.queueMoveCss()}
      <div class="q-head">
        <div class="insp-basis" style="margin:0;">${this.esc(basis)} · ${rows.length} ${rows.length === 1 ? 'property' : 'properties'}</div>
        ${tabBar}
        <div style="display:flex; flex-wrap:wrap; gap:8px; flex:1 1 100%;">${schedHtml}</div>
      </div>
      ${this.moveDialogHtml()}
      <div class="insp-card insp-tbl-wrap" style="overflow-x:auto;">
        <table class="insp-tbl">
          <colgroup><col><col style="width:130px;"><col style="width:110px;"><col style="width:90px;"><col style="width:120px;"><col style="width:150px;"><col style="width:80px;"><col style="width:200px;"></colgroup>
          <thead><tr>
            <th>Property</th><th>Checkout</th><th>Next check-in</th><th style="text-align:right;">Turnaround</th><th>Last inspection</th><th>Inspector</th><th style="text-align:center;">Urgency</th><th></th>
          </tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>`;
  }

  // ── R114 (Madel): reschedule an inspection like a clean ─────
  // Office-only UI (the server route is requireOfficeStrict — fails closed).
  static isOfficeRole(): boolean {
    const r = InspectionsPage.fieldRole();
    return !r || ['admin', 'director', 'manager', 'remote_ops'].includes(r);
  }
  private static addDaysYmd(ymd: string, n: number): string {
    const d = new Date(`${ymd}T00:00:00Z`);
    if (isNaN(d.getTime())) return ymd;
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  }
  // "Oct 13" — chips and pills (fmtDate carries the year)
  private fmtShort(ymd: string | null | undefined): string {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(String(ymd))) return String(ymd || '—');
    return new Date(`${ymd}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  // The queue row's KEY date + kind — exactly what /inspections/assign and
  // /queue/skip key on (a light row keys on its check-in; a missed row on its
  // ORIGINAL checkout, not roll_to; a moved row on its new date).
  // Start on a 📌 / rescheduled row stamps the job's key on the new
  // inspection (for_key), so a visit made ahead of the job's date still
  // counts it done (lib/inspMove.doneRecord).
  private static qForAttr(r: any): string {
    if (!r || (r.type !== 'manual' && r.type !== 'moved') || !r.checkout_date) return '';
    const k = `${r.listing_id}|${r.checkout_date}`;
    return ` data-q-for="${Formatters.escapeHtml(k)}"`;
  }
  private static rowMoveKey(r: any): { from: string; kind: string } | null {
    const from = r.type === 'checkin_light' ? r.checkin_date : r.checkout_date;
    if (!from) return null;
    const kind = ['checkin_light', 'manual', 'moved'].includes(r.type) ? r.type : (r.missed ? 'missed' : 'checkout');
    return { from, kind };
  }
  private queueRowKebab(r: any): string {
    const k = InspectionsPage.rowMoveKey(r);
    if (!k) return '';
    const key = `${r.listing_id}|${k.from}|${k.kind}`;
    // a scheduled / moved job already done can't move (the server re-checks
    // every row kind — this only saves a round trip)
    const done = (r.type === 'manual' || r.type === 'moved') && !!(r.inspected_today || r.inspected_since_checkout);
    const origin = r.type === 'moved' ? (r.origin_date || r.moved_from) : (r.type === 'manual' ? r.moved_from : null);
    const asOf = (this.queue && this.queue.as_of) || '';
    const items: any[] = [{
      icon: '↪', label: 'Move to date…', attrs: { 'data-q-move': key }, disabled: done,
      title: done ? 'Already inspected — can\'t reschedule' : 'Reschedule this inspection — the inspector keeps it',
    }];
    if (origin && origin !== k.from && origin >= asOf && !done) {
      items.push({ icon: '↩', label: `Move back to ${this.fmtShort(origin)}`, attrs: { 'data-q-moveback': `${key}|${origin}` }, title: 'Put it back on its original date' });
    }
    // R114 review (insp-move #4): a rescheduled / scheduled job needs a way
    // off the queue (a cancelled stay, a job nobody needs). It's a skip with
    // reason 'cancelled' — greyed with undo, never an ECI flag — stamped on
    // the entry so it can't come back as "missed".
    if ((r.type === 'moved' || r.type === 'manual') && !done && !r.skip) {
      items.push({ icon: '✕', label: 'Cancel this inspection…', attrs: { 'data-q-cancel': `${r.listing_id}|${r.checkout_date}` }, title: r.orphaned ? 'Its booking changed — take it off the queue' : 'Take it off the queue (undo any time)' });
    }
    return kebabHtml(items, { title: 'Inspection', tip: 'More — move or cancel this inspection', cls: 'q-kb' });
  }
  private openMoveDialog(key: string): void {
    const [lid, from, kind] = key.split('|');
    if (!lid || !from || !kind) return;
    const r = ((this.queue && this.queue.rows) || []).find((x: any) => {
      const k = InspectionsPage.rowMoveKey(x);
      return String(x.listing_id) === lid && !!k && k.from === from;
    });
    const asOf = (this.queue && this.queue.as_of) || from;
    const max60 = InspectionsPage.addDaysYmd(asOf, 60);
    const checkin = kind === 'checkin_light' ? from : ((r && r.light && r.checkin_deadline) || '');
    const max = checkin && checkin < max60 ? checkin : max60;
    // default: the next day it could go — today for a missed one, else the
    // day after its date (or the day before, for a light check at its limit)
    let to = from < asOf ? asOf : InspectionsPage.addDaysYmd(from, 1);
    if (to > max) to = InspectionsPage.addDaysYmd(from, -1) >= asOf ? InspectionsPage.addDaysYmd(from, -1) : from;
    this.moveDlg = {
      lid, from, kind, name: (r && r.name) || lid, inspector: (r && r.assigned_inspector) || '',
      movedFrom: (r && r.moved_from) || '', to, max, checkin, busy: false, err: '',
    };
    closeKebab();
    this.draw();
    (this.container.querySelector('#qmv-date') as HTMLInputElement | null)?.focus();
  }
  private moveDialogHtml(): string {
    const d = this.moveDlg;
    if (!d || !this.queue) return '';
    const note = [
      d.inspector ? `${this.esc(d.inspector)} keeps it.` : 'Nobody is assigned yet — pick an inspector after the move.',
      d.checkin ? `It's a pre-arrival check, so it has to happen on or before the ${this.esc(this.fmtShort(d.checkin))} arrival.` : '',
      d.kind !== 'manual' ? 'The original date drops off the queue — one job, on the new day.' : '',
    ].filter(Boolean).join(' ');
    return `
      <div class="insp-modal-bg q-mv-bg" data-qmv-bg>
        <div class="insp-modal q-mv" role="dialog" aria-modal="true" aria-labelledby="qmv-ttl">
          <div class="insp-modal-head">
            <h3 id="qmv-ttl">↪ Move inspection</h3>
            <button type="button" class="btn btn-ghost btn-sm q-mv-x" data-qmv-close aria-label="Close">✕</button>
          </div>
          <div class="q-mv-prop">${this.esc(d.name)}</div>
          <div class="q-mv-sub">Now on ${this.esc(this.fmtDate(d.from))}${d.movedFrom && d.movedFrom !== d.from ? ` · originally ${this.esc(this.fmtDate(d.movedFrom))}` : ''}</div>
          <label class="q-mv-lab" for="qmv-date">New date</label>
          <input type="date" class="input q-mv-date" id="qmv-date" min="${this.esc(this.queue.as_of)}" max="${this.esc(d.max)}" value="${this.esc(d.to)}" ${d.busy ? 'disabled' : ''}>
          <div class="q-mv-note">${note}</div>
          ${d.err ? `<div class="q-mv-err" role="alert">${this.esc(d.err)}</div>` : ''}
          <div class="insp-modal-actions q-mv-actions">
            <button type="button" class="btn btn-secondary" data-qmv-close ${d.busy ? 'disabled' : ''}>Cancel</button>
            <button type="button" class="btn btn-primary" id="qmv-go" ${d.busy ? 'disabled' : ''}>${d.busy ? 'Moving…' : '↪ Move'}</button>
          </div>
        </div>
      </div>`;
  }
  private queueMoveCss(): string {
    return `<style>
      .q-moved { display:inline-block; margin-top:3px; font-size:10.5px; font-weight:700; padding:1px 8px; border-radius:var(--radius-pill); background:var(--accent-soft); color:var(--accent); white-space:nowrap; }
      .q-actrow { display:flex; align-items:flex-start; justify-content:flex-end; gap:6px; }
      .q-actmain { min-width:0; }
      .q-skipnote { max-width:220px; margin:3px 0 0 auto; white-space:normal; overflow-wrap:anywhere; line-height:1.35; }
      .q-actmain .insp-btn { white-space:nowrap; }
      .q-actrow .kb-btn.q-kb { min-width:32px; height:32px; padding:0 8px; font-size:15px; font-weight:700; letter-spacing:.05em; border-radius:var(--radius-sm); flex:none; }
      .q-mv-bg { align-items:center; }
      .insp-modal.q-mv { width:min(420px, 94vw); gap:10px; box-shadow:var(--shadow-pop); }
      .q-mv .q-mv-x { min-width:36px; min-height:36px; }
      .q-mv-prop { font-size:15px; font-weight:700; color:var(--text); }
      .q-mv-sub { font-size:12.5px; color:var(--text-muted); margin-top:-6px; }
      .q-mv-lab { font-size:var(--fs-label); font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-faint); margin-top:4px; }
      .q-mv-date { font-size:16px; padding:10px 12px; min-height:44px; width:100%; box-sizing:border-box; margin:0; }
      .q-mv-note { font-size:12.5px; color:var(--text-muted); line-height:1.45; }
      .q-mv-err { font-size:12.5px; color:var(--danger-text); background:var(--danger-soft); border:1px solid var(--danger-border); border-radius:var(--radius-sm); padding:8px 10px; }
      .q-mv-actions { justify-content:flex-end; }
      .q-mv-actions .btn { min-height:40px; }
      @media (max-width: 640px) { .q-actrow .kb-btn.q-kb { min-width:38px; height:38px; } .q-mv-actions .btn { flex:1; justify-content:center; } }
    </style>`;
  }
  // POST /inspections/queue/move with the 409 needs_confirm → confirm →
  // override handshake (guest inside / 6-day streak). Never throws.
  private async postMove(b: { listing_id: string; from: string; to: string; kind: string }): Promise<{ ok: boolean; j?: any; err: string; status?: number }> {
    const by = InspectionsPage.profileName() || 'office';
    const send = async (override: boolean): Promise<{ r: Response; j: any } | null> => {
      try {
        const r = await fetch(`${API}/inspections/queue/move`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...b, by, ...(override ? { override: true } : {}) }),
        });
        return { r, j: await r.json().catch(() => ({} as any)) };
      } catch { return null; }
    };
    let res = await send(false);
    if (res && res.r.status === 409 && res.j && res.j.needs_confirm) {
      if (!window.confirm(String(res.j.error || 'Move anyway?'))) return { ok: false, err: '' };
      res = await send(true);
    }
    if (!res) return { ok: false, err: 'No connection — nothing moved. Try again when you have signal.' };
    if (!res.r.ok) return { ok: false, err: String((res.j && res.j.error) || `Couldn't move it (${res.r.status}).`), status: res.r.status };
    return { ok: true, j: res.j, err: '' };
  }
  private async submitMove(): Promise<void> {
    const d = this.moveDlg;
    if (!d || d.busy || !this.queue) return;
    const inp = this.container.querySelector('#qmv-date') as HTMLInputElement | null;
    const to = String((inp && inp.value) || '').trim();
    d.to = to;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(to) || (inp && !inp.checkValidity()) || to < this.queue.as_of || to > d.max) {
      d.err = `Pick a date from ${this.fmtDate(this.queue.as_of)} to ${this.fmtDate(d.max)}.`;
      this.draw(); return;
    }
    if (to === d.from) { d.err = 'It\'s already on that date — pick another day.'; this.draw(); return; }
    d.busy = true; d.err = '';
    this.draw();
    const out = await this.postMove({ listing_id: d.lid, from: d.from, to, kind: d.kind });
    if (!out.ok) {
      if (this.moveDlg === d) {
        d.busy = false; d.err = out.err;
        if (out.status === 404 || out.status === 409) d.stale = true;   // the row changed under us — refresh on close
        this.draw();
      }
      return;
    }
    if (this.moveDlg === d) this.moveDlg = null;
    await this.reloadQueue();
    this.draw();
    this.afterMove(d.name, to, out.j);
  }
  // R114 review (insp-move #4): ⋯ → "✕ Cancel this inspection…" on a
  // rescheduled / scheduled row. Office-only (the server re-checks).
  private async cancelQueued(spec: string): Promise<void> {
    const sep = spec.lastIndexOf('|');
    const lid = spec.slice(0, sep);
    const date = spec.slice(sep + 1);
    if (!lid || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    const r = ((this.queue && this.queue.rows) || []).find((x: any) => String(x.listing_id) === lid && x.checkout_date === date);
    const name = (r && r.name) || lid;
    closeKebab();
    const suggested = r && r.orphaned ? `booking changed — no stay checks out on ${r.origin_date || r.moved_from || 'its original date'}` : '';
    const note = window.prompt(`Cancel the ${name} inspection on ${this.fmtDate(date)}? It stays on the queue greyed out as ✕ Cancelled (undo any time), so the inspector knows not to go.${r && r.type === 'moved' ? ' Its original date stays off the queue too.' : ''}\n\nReason (optional):`, suggested);
    if (note === null) return;
    const j = await this.post('/inspections/queue/skip', {
      listing_id: lid, checkout_date: date, reason: 'cancelled', note: note.trim() || undefined, by: InspectionsPage.profileName() || 'office',
    });
    if (j) { await this.reloadQueue(); this.draw(); }
  }
  private async moveBack(spec: string): Promise<void> {
    const [lid, from, kind, origin] = spec.split('|');
    if (!lid || !from || !origin) return;
    const r = ((this.queue && this.queue.rows) || []).find((x: any) => String(x.listing_id) === lid);
    const name = (r && r.name) || lid;
    closeKebab();
    if (!window.confirm(`Move the ${name} inspection back to ${this.fmtDate(origin)}?`)) return;
    const out = await this.postMove({ listing_id: lid, from, to: origin, kind });
    if (!out.ok) {
      if (out.err) alert(out.err);
      if (out.status === 404 || out.status === 409) { await this.reloadQueue(); this.draw(); }
      return;
    }
    await this.reloadQueue();
    this.draw();
    this.afterMove(name, origin, out.j);
  }
  // A move can land outside the visible tab — say so (HK's
  // noteIfBeyondHorizon idea) instead of letting it look like a lost save.
  private afterMove(name: string, to: string, j: any): void {
    const who = j && j.entry && j.entry.inspector ? ` — ${j.entry.inspector} keeps it` : '';
    let hint = '';
    if (this.queueDate) {
      if (to !== this.queueDate) hint = `You're viewing ${this.fmtDate(this.queueDate)} only — pick ${this.fmtDate(to)} (or clear the date) to see it.`;
    } else {
      const dt = this.daysToCheckout(to);
      const lim = this.queueTab === 'today' ? 0 : (this.queueTab === '7d' ? 7 : 14);
      if (dt != null && dt > lim) hint = dt <= 7 ? 'Switch to Next 7 days to see it.' : (dt <= 14 ? 'Switch to Next 14 days to see it.' : `Pick ${this.fmtDate(to)} in the date box to see it.`);
    }
    const msg = `Moved ${name} to ${this.fmtShort(to)}${who}.`;
    if (hint) alert(`${msg} ${hint}`);
    else this.toast(msg);
  }
  private toast(msg: string, ms = 2600): void {
    document.querySelectorAll('.insp-toast').forEach(el => el.remove());
    const el = document.createElement('div');
    el.className = 'insp-toast';
    el.setAttribute('role', 'status');
    el.style.cssText = 'position:fixed; left:50%; bottom:calc(24px + env(safe-area-inset-bottom, 0px)); transform:translateX(-50%); z-index:1300; background:var(--primary); color:var(--card); font-size:13px; font-weight:600; padding:9px 16px; border-radius:var(--radius-pill); box-shadow:var(--shadow-pop); pointer-events:none; max-width:92vw; text-align:center;';
    el.textContent = msg;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), ms);
  }

  // ── tab 1: overview ─────────────────────────────────────────
  private renderOverview(): string {
    if (!this.analytics || !this.board) return emptyState('Overview unavailable', 'GET /inspections/analytics or /board failed — check the server log.', '⚠️');
    // Phase 4 B: Overview reads GET /inspections/analytics (coverage / throughput
    // / per-inspector quality). Denominators are stated inline; nulls stay honest.
    const a = this.analytics;
    const cov = a.coverage || {};
    const tp = a.throughput || {};
    const days = a.days || 90;
    const inspectors: any[] = Array.isArray(a.per_inspector) ? a.per_inspector : [];
    const pctTxt = cov.pct == null ? 'n/a' : `${cov.pct}%`;
    // Coverage denominator stated inline: 'N of M active properties inspected · 90d'.
    const covInline = cov.active_count
      ? `${cov.inspected_count ?? 0} of ${cov.active_count} active properties inspected · ${days}d`
      : 'no active properties — coverage % cannot be computed';

    const inspectorRows = inspectors.length
      ? inspectors.map(r => {
          // Avg grade — over GRADED inspections only; basis stated, honest null.
          const gradeCell = r.avg_grade == null
            ? `<span class="insp-sub" title="${this.esc(r.avg_grade_basis || 'no graded inspections in window')}">no graded</span>`
            : `<span class="insp-grade" style="background:${this.gradeSoft(r.avg_grade)}; color:${this.gradeHex(r.avg_grade)};">${r.avg_grade}</span> <span class="insp-sub" title="${this.esc(r.avg_grade_basis || '')}">basis</span>`;
          // AI agreement % — over AI-graded items; the denominator rides in a tooltip + sub.
          const agreeCell = r.agreement_pct == null
            ? `<span class="insp-sub" title="${this.esc(r.agreement_basis || 'no AI-graded items in window')}">no AI items</span>`
            : `<b>${r.agreement_pct}%</b> <span class="insp-sub" title="${this.esc(r.agreement_basis || '')}">over ${r.graded_items} item${r.graded_items === 1 ? '' : 's'}</span>`;
          // AI fails passed — the accountability column; red when > 0.
          const failsCell = r.fails_overridden > 0
            ? `<span class="insp-grade" style="background:var(--danger-soft); color:var(--danger-text, #b91c1c);" title="AI flagged a fail, the inspector marked it pass">${r.fails_overridden}</span>`
            : `<span class="insp-sub">0</span>`;
          // Missed required cadence checks — the accountability loop; red when > 0.
          const missed = r.missed_checks ?? 0;
          const missedCell = missed > 0
            ? `<span class="insp-grade" style="background:var(--danger-soft); color:var(--danger-text, #b91c1c);" title="${this.esc(r.missed_checks_basis || 'required cadence checks skipped')}">${missed}</span>`
            : `<span class="insp-sub" title="${this.esc(r.missed_checks_basis || 'required cadence checks skipped')}">0</span>`;
          return `
          <tr>
            <td class="insp-name">${this.esc(r.inspector)}</td>
            <td class="mono num" style="text-align:right;">${r.count}</td>
            <td style="text-align:right;">${gradeCell}</td>
            <td style="text-align:right;">${agreeCell}</td>
            <td style="text-align:right;">${failsCell}</td>
            <td style="text-align:right;">${missedCell}</td>
          </tr>`;
        }).join('')
      : `<tr><td colspan="6" class="insp-sub" style="padding:12px;">No completed inspections in this window yet — per-inspector quality (avg grade, AI agreement, AI fails passed, missed required checks) appears once inspections are marked complete in the last ${days} days.</td></tr>`;

    const boardRows = (this.board.rows || []).map((r: any) => {
      const never = r.last_inspection_at == null;
      return `
        <tr class="click" data-insp-history="${this.esc(r.listing_id)}|${this.esc(r.listing_name)}" title="Open condition history for ${this.esc(r.listing_name)}">
          <td class="insp-name" title="${this.esc(r.listing_name)}">${this.esc(r.listing_name)}</td>
          <td>${never ? '<span class="insp-sub">never inspected</span>' : `<span class="mono num">${this.fmtDate(r.last_inspection_at)}</span>`}</td>
          <td style="text-align:right;"><span class="mono num" style="font-weight:600; color:${this.daysHex(r.days_since)};">${r.days_since == null ? '—' : `${r.days_since}d`}</span></td>
          <td style="text-align:right;">${r.last_grade == null
            ? '<span class="insp-sub">ungraded</span>'
            : `<span class="insp-grade" style="background:${this.gradeSoft(r.last_grade)}; color:${this.gradeHex(r.last_grade)};">${r.last_grade}</span>`}</td>
        </tr>`;
    }).join('');

    return `
      <div class="kpi-grid cols-3">
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">${days}-day coverage</span><span class="kpi-card-ic">${icon('target', 17)}</span></div>
          <div class="kpi-card-value">${pctTxt}</div>
          <div class="kpi-card-cap">${this.esc(covInline)}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Completed · ${days}d</span><span class="kpi-card-ic">${icon('file', 17)}</span></div>
          <div class="kpi-card-value">${tp.completed_in_window ?? 0}</div>
          <div class="kpi-card-cap">${tp.completed_this_month ?? 0} this month · inspections marked complete</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Active inspectors</span><span class="kpi-card-ic">${icon('users', 17)}</span></div>
          <div class="kpi-card-value">${inspectors.length}</div>
          <div class="kpi-card-cap">credited on a completed inspection in the last ${days}d</div>
        </div>
      </div>
      <div class="insp-basis">Per-inspector quality over the last ${days} days · avg grade averages only AI-graded inspections (basis on hover); checklist-only inspections count toward volume but not grade.</div>
      <div class="insp-card" style="overflow-x:auto; margin-bottom:8px;">
        <table class="insp-tbl">
          <colgroup><col><col style="width:80px;"><col style="width:130px;"><col style="width:150px;"><col style="width:110px;"><col style="width:120px;"></colgroup>
          <thead><tr><th>Inspector</th><th style="text-align:right;">Inspections</th><th style="text-align:right;">Avg grade</th><th style="text-align:right;">AI agreement</th><th style="text-align:right;" title="AI flagged a fail, the inspector passed it">AI fails passed</th><th style="text-align:right;" title="Required cadence checks that came due on a visit but were skipped">Missed required checks</th></tr></thead>
          <tbody>${inspectorRows}</tbody>
        </table>
      </div>
      <div class="insp-basis" style="margin-bottom:18px;">AI agreement = how often the inspector's final call matched the AI's; AI fails passed = times the inspector marked something the AI flagged as a fail.</div>
      <div class="q-head">
        <div class="insp-basis" style="margin:0;">Board · one row per active property · days since last completed inspection (green ≤ 90d · amber ≤ 180d · red &gt; 180d or never) · grade from the last graded inspection · click a row for its condition history.</div>
        <div style="display:flex; gap:6px; align-items:flex-end;">
          <label class="insp-fld" style="text-transform:none; letter-spacing:0;">Condition history
            <select id="insp-hist-pick" style="min-width:200px;">
              <option value="">— pick a property —</option>
              ${(this.board.rows || []).map((r: any) => `<option value="${this.esc(r.listing_id)}|${this.esc(r.listing_name)}">${this.esc(r.listing_name)}</option>`).join('')}
            </select>
          </label>
        </div>
      </div>
      <div class="insp-card" style="overflow-x:auto;">
        <table class="insp-tbl">
          <colgroup><col><col style="width:120px;"><col style="width:110px;"><col style="width:90px;"></colgroup>
          <thead><tr><th>Property</th><th>Last inspection</th><th style="text-align:right;">Days since</th><th style="text-align:right;">Grade</th></tr></thead>
          <tbody>${boardRows}</tbody>
        </table>
      </div>`;
  }

  // ── tab 2: inspections list ─────────────────────────────────
  // ── R114: Recovered photos (office) ────────────────────────────────────
  // Photos a phone uploaded for an inspection that no longer exists (the
  // 2026-09-29 wipe), with no inspection id at all, or that looked misfiled —
  // held by the server (ao_inspection_orphan_frames), never dropped. A small
  // card on the Inspections tab: one row per inspection, "Attach to…" an
  // existing inspection of that property. Hidden when nothing is held; the
  // routes are office-only (requireOfficeStrict).
  private held: any = undefined;            // GET /inspections/orphan-frames (null = unavailable)
  private heldBusy: string | null = null;
  private heldNote: { text: string; kind: 'ok' | 'err' } | null = null;
  private heldBound = false;
  // R114 fix2: per group — the inspection the office picked (kept across
  // redraws) and its "search every inspection" text (present = search open)
  private heldPick = new Map<string, string>();
  private heldFind = new Map<string, string>();
  // every inspection this page knows, as the card's search pool
  private heldSearchPool(): HeldBrief[] {
    return (this.inspections || []).filter((i: any) => i && i.id).map((i: any) => ({
      id: String(i.id), listing_id: i.listing_id != null ? String(i.listing_id) : null, listing_name: i.listing_name || null,
      status: i.status || null, inspector: i.inspector || null,
      visit_day: (i.status === 'complete' ? this.localDay(this.visitAt(i)) : this.localDay(i.created_at)) || null,
    }));
  }
  private heldOptionGroups(g: any): HeldOptionGroup[] {
    return heldTargetGroups(g, this.held || {}, {
      all: this.heldSearchPool(),
      query: this.heldFind.get(g.key) || '',
      selected: this.heldPick.has(g.key) ? this.heldPick.get(g.key) : undefined,
      fmtDay: (d: string) => this.fmtDate(d),
    });
  }
  private heldOptionsHtml(g: any): string {
    return [`<option value="">Attach to…</option>`].concat(this.heldOptionGroups(g).map(og =>
      `<optgroup label="${this.esc(og.label)}">${og.items.map(o => `<option value="${this.esc(o.id)}"${o.selected ? ' selected' : ''}${o.disabled ? ' disabled' : ''}>${this.esc(o.text)}</option>`).join('')}</optgroup>`)).join('');
  }
  // "3 matches — at the top of the list" under an open search box
  private heldFindNote(g: any): string {
    const q = (this.heldFind.get(g.key) || '').trim();
    if (!q) return '';
    const hit = this.heldOptionGroups(g).find(og => og.label.startsWith('Search results'));
    const n = hit ? hit.items.filter(o => !o.disabled).length : 0;
    return n ? `${n} match${n === 1 ? '' : 'es'} — at the top of the list` : 'No inspection matches — try the property name';
  }
  private async loadHeld(): Promise<void> {
    try {
      const r = await fetch(`${API}/inspections/orphan-frames`);
      this.held = r.ok ? await r.json() : null;
    } catch (_) { this.held = null; }
    if (this.tab === 'inspections' && !this.view) this.draw();
  }
  private heldPhotosCard(): string {
    if (!this.heldBound) {
      this.heldBound = true;
      // delegated once on the page container (survives every redraw)
      this.container.addEventListener('click', (e) => {
        const t = e.target as HTMLElement;
        const go = t.closest<HTMLElement>('[data-held-attach]');
        if (go && this.container.contains(go)) { void this.attachHeld(go.dataset.heldAttach || ''); return; }
        const v = t.closest<HTMLElement>('[data-held-view]');
        if (v && this.container.contains(v)) {
          const [k, i] = String(v.dataset.heldView).split('|');
          const g = ((this.held && this.held.groups) || []).find((x: any) => x.key === k);
          const urls = g ? g.rows.map((r: any) => r.url).filter(Boolean) : [];
          if (urls.length) openLightbox(urls, Number(i) || 0, { caption: (n) => `${g.listing_name || 'Unknown property'} — held photo ${n + 1} of ${urls.length}` });
          return;
        }
        // R114 fix2: "Not listed? Search every inspection" opens the group's search box
        const f = t.closest<HTMLElement>('[data-held-find-toggle]');
        if (f && this.container.contains(f)) {
          const k = f.dataset.heldFindToggle || '';
          if (this.heldFind.has(k)) this.heldFind.delete(k); else this.heldFind.set(k, '');
          this.draw();
          if (this.heldFind.has(k)) this.container.querySelector<HTMLInputElement>(`[data-held-find="${CSS.escape(k)}"]`)?.focus();
        }
      });
      // typing narrows that group's list in place (the box keeps focus); a pick is remembered across redraws
      this.container.addEventListener('input', (e) => {
        const inp = (e.target as HTMLElement).closest?.('[data-held-find]') as HTMLInputElement | null;
        if (!inp || !this.container.contains(inp)) return;
        const k = inp.dataset.heldFind || '';
        this.heldFind.set(k, inp.value);
        const g = ((this.held && this.held.groups) || []).find((x: any) => x.key === k);
        const sel = this.container.querySelector<HTMLSelectElement>(`[data-held-target="${CSS.escape(k)}"]`);
        if (g && sel) sel.innerHTML = this.heldOptionsHtml(g);
        const note = this.container.querySelector<HTMLElement>(`[data-held-find-n="${CSS.escape(k)}"]`);
        if (g && note) note.textContent = this.heldFindNote(g);
      });
      this.container.addEventListener('change', (e) => {
        const sel = (e.target as HTMLElement).closest?.('[data-held-target]') as HTMLSelectElement | null;
        if (sel && this.container.contains(sel)) this.heldPick.set(sel.dataset.heldTarget || '', sel.value);
      });
    }
    if (this.held === undefined) { this.held = null; void this.loadHeld(); return ''; }
    const h = this.held;
    if (!h || !h.total) return this.heldNote ? `<div class="insp-basis" style="color:var(--success-text);">${this.esc(this.heldNote.text)}</div>` : '';
    const reasonWord = (r: string) => r === 'possible misfile' ? 'looks misfiled' : r === 'unknown inspection' ? 'no inspection on the phone' : 'its inspection was deleted';
    const groups: any[] = h.groups || [];
    // R114 fix2: the suggestion is pre-selected but never narrows the list —
    // suggested · queued-under · this property (and any other property the
    // photos point at) · the newest across the portfolio · a search over
    // every inspection (lib/heldPhotoOptions.mjs)
    const rows = groups.slice(0, 8).map((g: any) => {
      const thumbs = g.rows.slice(0, 4).map((r: any, i: number) => `<img src="${this.esc(r.url)}" alt="" loading="lazy" data-held-view="${this.esc(g.key)}|${i}">`).join('');
      const finding = this.heldFind.has(g.key);
      const guess = g.listing_basis === 'suggested' ? ' · property guessed from when they were taken' : '';
      return `
        <div class="held-row">
          <div class="held-main">
            <div class="held-ttl">${this.esc(g.listing_name || (g.listing_id ? `Listing ${g.listing_id}` : 'Unknown property'))}</div>
            <div class="insp-sub">${g.count} photo${g.count === 1 ? '' : 's'}${g.visit_day ? ` · visit ~${this.esc(this.fmtDate(g.visit_day))}` : ''} · ${this.esc((g.reasons || []).map(reasonWord).join(', '))}${guess}</div>
            <div class="held-thumbs">${thumbs}${g.count > 4 ? `<span class="insp-link" data-held-view="${this.esc(g.key)}|4">+${g.count - 4}</span>` : ''}</div>
          </div>
          <div class="held-act">
            <select class="held-sel" data-held-target="${this.esc(g.key)}" aria-label="Inspection to attach these photos to">${this.heldOptionsHtml(g)}</select>
            <button type="button" class="btn btn-primary btn-sm" data-held-attach="${this.esc(g.key)}" ${this.heldBusy === g.key ? 'disabled' : ''}>${this.heldBusy === g.key ? 'Attaching…' : 'Attach'}</button>
          </div>
          <div class="held-findrow">
            ${finding ? `<input type="search" class="held-find" data-held-find="${this.esc(g.key)}" value="${this.esc(this.heldFind.get(g.key) || '')}" placeholder="Search every inspection — property, inspector or date" aria-label="Search every inspection" autocomplete="off">` : ''}
            <button type="button" class="held-find-btn" data-held-find-toggle="${this.esc(g.key)}">${finding ? 'Close search' : 'Not in the list? Search every inspection'}</button>
            ${finding ? `<span class="insp-sub" data-held-find-n="${this.esc(g.key)}" aria-live="polite">${this.esc(this.heldFindNote(g))}</span>` : ''}
          </div>
        </div>`;
    }).join('');
    return `
      <style>
        .held-card { padding:14px 16px; margin-bottom:16px; }
        .held-h { display:flex; align-items:center; gap:8px; font-size:14px; font-weight:700; color:var(--text); }
        .held-n { font-size:11px; font-weight:700; padding:2px 9px; border-radius:var(--radius-pill); background:var(--warning-soft); color:var(--warning-text); }
        .held-row { display:flex; gap:12px; align-items:center; justify-content:space-between; padding:10px 0; border-top:1px solid var(--border-soft, var(--border)); flex-wrap:wrap; }
        .held-main { flex:1; min-width:220px; }
        .held-ttl { font-size:13px; font-weight:700; color:var(--text); }
        .held-thumbs { display:flex; gap:6px; align-items:center; margin-top:6px; }
        .held-thumbs img { width:44px; height:44px; object-fit:cover; border-radius:6px; border:1px solid var(--border); cursor:pointer; }
        .held-act { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
        .held-sel { min-height:36px; max-width:260px; padding:6px 8px; border:1px solid var(--border); border-radius:var(--radius-sm, 8px); background:var(--card); color:var(--text); font:inherit; font-size:12px; }
        .held-act .btn { min-height:36px; }
        .held-sel { min-width:0; }
        .held-findrow { flex-basis:100%; display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-top:-2px; }
        .held-find { flex:1 1 220px; min-width:0; max-width:480px; min-height:36px; padding:6px 10px; border:1px solid var(--border); border-radius:var(--radius-sm, 8px); background:var(--card); color:var(--text); font:inherit; font-size:12.5px; box-sizing:border-box; }
        .held-find:focus { outline:none; border-color:var(--accent); box-shadow:0 0 0 3px var(--accent-soft); }
        .held-find-btn { min-height:36px; padding:0 2px; border:0; background:none; color:var(--accent); font:inherit; font-size:12.5px; font-weight:600; cursor:pointer; }
        .held-find-btn:hover { text-decoration:underline; }
        @media (max-width:640px) { .held-main { min-width:0; flex-basis:100%; } .held-act { width:100%; flex-wrap:nowrap; } .held-sel { flex:1 1 0; width:0; max-width:none; } .held-act .btn { flex:none; } .held-find { flex-basis:100%; max-width:none; } }
      </style>
      <div class="insp-card held-card">
        <div class="held-h">📥 Recovered photos <span class="held-n">${h.total} held</span></div>
        <div class="insp-basis" style="margin:4px 0 6px;">Photos phones uploaded for an inspection that no longer exists, with no inspection, or that looked misfiled. Nothing is lost — each group is one visit; attach it to that visit’s inspection (added to a completed one as “added after completion”).</div>
        ${this.heldNote ? `<div class="insp-sub" style="margin-bottom:6px; color:${this.heldNote.kind === 'ok' ? 'var(--success-text)' : 'var(--danger-text)'};">${this.esc(this.heldNote.text)}</div>` : ''}
        ${rows}
        ${groups.length > 8 ? `<div class="insp-sub" style="padding-top:6px;">+ ${groups.length - 8} more group${groups.length - 8 === 1 ? '' : 's'} — attach these first.</div>` : ''}
      </div>`;
  }
  private async attachHeld(key: string): Promise<void> {
    if (!key || this.heldBusy) return;
    const sel = this.container.querySelector<HTMLSelectElement>(`[data-held-target="${CSS.escape(key)}"]`);
    const target = sel ? sel.value : '';
    if (!target) { this.heldNote = { text: 'Pick the inspection to attach these photos to first.', kind: 'err' }; this.draw(); return; }
    // R114 fix: attach exactly the photos this card showed (by id) — a group
    // key re-selected on the server could sweep in a photo held after the page
    // loaded, or one from another visit
    const g = ((this.held && this.held.groups) || []).find((x: any) => x.key === key);
    const ids: string[] = g ? (g.rows || []).map((r: any) => String(r.id)).filter(Boolean) : [];
    if (!ids.length) { this.heldNote = { text: 'Nothing to attach in that group — reload the page.', kind: 'err' }; this.draw(); return; }
    this.heldBusy = key; this.heldNote = null; this.draw();
    try {
      const r = await fetch(`${API}/inspections/orphan-frames/attach`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids, target_id: target }) });
      const j = await r.json().catch(() => ({} as any));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      this.heldNote = { text: `Attached ${j.attached} photo${j.attached === 1 ? '' : 's'}${j.late ? ' — marked “added after completion”' : ''}.`, kind: 'ok' };
      await this.reloadInspections();
    } catch (e: any) {
      this.heldNote = { text: `Couldn’t attach — ${e.message}`, kind: 'err' };
    }
    this.heldBusy = null;
    if (this.heldNote && this.heldNote.kind === 'ok') { this.heldPick.delete(key); this.heldFind.delete(key); }
    this.held = undefined;   // re-fetch on the redraw
    this.draw();
  }

  private renderInspections(): string {
    const tplOpts = this.templates.map(t => `<option value="${this.esc(t.id)}">${this.esc(t.name)}</option>`).join('');
    const listingOpts = this.boardListings.map(l => `<option value="${this.esc(l.listing_id)}">${this.esc(l.listing_name)}</option>`).join('');
    const form = `
      <div class="insp-card insp-form card-elev" id="insp-new-form">
        <label>Property
          <select id="insp-nf-listing">${listingOpts || '<option value="">no active listings</option>'}</select>
        </label>
        <label>Template
          <select id="insp-nf-tpl">${tplOpts || '<option value="">no templates</option>'}</select>
        </label>
        <label>Inspection type
          <select id="insp-nf-type">
            <option value="condition"${this.newInspectionType === 'condition' ? ' selected' : ''}>Condition</option>
            <option value="audit"${this.newInspectionType === 'audit' ? ' selected' : ''}>Audit (grade the team)</option>
          </select>
        </label>
        <label>Form
          <select id="insp-nf-form">
            <option value="basic">Basic (4 pillars)</option>
            <option value="deep">Deep (sections A–H)</option>
            <option value="audit">Audit form (17 sub-scores)</option>
            <option value="">None (free capture)</option>
          </select>
        </label>
        <label>Inspector
          <input id="insp-nf-inspector" placeholder="name" style="width:140px;">
        </label>
        <button id="insp-nf-add">+ New inspection</button>
      </div>
      <div class="insp-basis" style="margin-top:-6px;">New inspections open a capture-first walkthrough — record &amp; narrate, or snap room photos; the AI fills the checklist. The manual checklist stays available per row. An <b>Audit</b> grades the property's assigned property manager (PM); a <b>Condition</b> check is the routine visit.</div>`;

    if (!this.inspections.length) {
      return form + emptyState('No inspections yet', 'Create one above — capture a walkthrough and the AI grades it against the template.', '🔍');
    }

    // R58: date / date-range filter (Jesse: "see all the inspections on a date
    // or date range"). Filters on the day the Date column shows — the VISIT
    // for a completed one (Stacy 2026-10-07), else the day it was started.
    const todayYmd = new Date().toLocaleDateString('en-CA');
    const dateOf = (i: any) => i.status === 'complete' ? this.localDay(this.visitAt(i)) : this.localDay(i.created_at);
    let filtered = this.inspections;
    if (this.inspFrom || this.inspTo) {
      filtered = filtered.filter(i => {
        const d = dateOf(i);
        return d && (!this.inspFrom || d >= this.inspFrom) && (!this.inspTo || d <= this.inspTo);
      });
    }
    const filterRow = `
      <div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin:10px 0;">
        <span style="font-size:12px; font-weight:600; color:var(--text-muted);">Date:</span>
        <button class="insp-btn sm${!this.inspFrom && !this.inspTo ? ' primary' : ''}" data-insp-dchip="all">All</button>
        <button class="insp-btn sm${this.inspFrom === todayYmd && this.inspTo === todayYmd ? ' primary' : ''}" data-insp-dchip="today">Today</button>
        <button class="insp-btn sm" data-insp-dchip="7d">Last 7 days</button>
        <input type="date" data-insp-from value="${this.esc(this.inspFrom)}" style="padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-size:12px;"/>
        <span style="color:var(--text-muted);">→</span>
        <input type="date" data-insp-to value="${this.esc(this.inspTo)}" style="padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-size:12px;"/>
        ${(this.inspFrom || this.inspTo) ? `<span class="insp-sub">${filtered.length} of ${this.inspections.length} shown</span>` : ''}
      </div>`;

    const sorted = [...filtered].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    const rows = sorted.map(i => {
      const completed = i.status === 'complete';
      const statusPill = completed
        ? `<span class="insp-pill" style="background:var(--success-soft); color:var(--success-text, #166534);">complete</span>`
        : `<span class="insp-pill" style="background:var(--warning-soft); color:var(--warning-text, #92400e);">in progress</span>`;
      const grade = i.grade == null
        ? (i.mode === 'checklist' ? '<span class="insp-sub">checklist</span>' : '<span class="insp-sub">ungraded</span>')
        : `<span class="insp-grade" style="background:${this.gradeSoft(i.grade)}; color:${this.gradeHex(i.grade)};">${i.grade}</span>`;
      const dateShown = completed ? this.visitAt(i) : i.created_at;   // the visit day
      const analyzed = i.grade != null || (i.items || []).some((it: any) => it.source === 'ai') || !!i.submitted_at || !!i.ai;   // AI-unblock: submitted counts, graded or not
      const openLbl = analyzed ? 'Report card' : 'Capture';
      return `
        <tr class="click" data-insp-open="${this.esc(i.id)}">
          <td class="insp-name" title="${this.esc(i.listing_name)}">${this.esc(i.listing_name)}</td>
          <td><span class="mono num">${this.fmtDate(dateShown)}</span><div class="insp-sub">${completed ? (this.localDay(this.visitAt(i)) !== this.localDay(i.completed_at) ? `inspected · completed ${this.fmtDate(i.completed_at)}` : 'completed') : 'started'}</div></td>
          <td>${i.inspector ? this.esc(i.inspector) : '<span class="insp-sub">unassigned</span>'}</td>
          <td><span class="insp-chip">${openLbl}</span> <span class="insp-link" data-insp-manual="${this.esc(i.id)}" title="Open the manual checklist instead">manual</span> <span class="insp-link" data-insp-report="${this.esc(i.id)}" title="Open the photo-forward owner report — Print / Save as PDF from there">PDF</span> <span class="insp-link" data-insp-creport="${this.esc(i.id)}" title="Printable feedback page for the CLEANER — issue photos + polished notes">cleaner&nbsp;PDF</span></td>
          <td style="text-align:right;">${statusPill}</td>
          <td style="text-align:right;">${grade}</td>
        </tr>`;
    }).join('');

    return `
      ${this.heldPhotosCard()}
      ${form}
      <div class="insp-basis">Newest first · date is the completion date for finished inspections, otherwise the start date · click a row to open the report card (or resume capture); 'manual' opens the checklist detail.</div>
      ${filterRow}
      <div class="insp-card" style="overflow-x:auto;">
        <table class="insp-tbl">
          <colgroup><col><col style="width:120px;"><col style="width:120px;"><col style="width:140px;"><col style="width:110px;"><col style="width:80px;"></colgroup>
          <thead><tr><th>Property</th><th>Date</th><th>Inspector</th><th>Open</th><th style="text-align:right;">Status</th><th style="text-align:right;">Grade</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }

  // ── inspection detail modal ─────────────────────────────────
  private renderDetailModal(): void {
    const insp = this.inspections.find(i => i.id === this.openId);
    if (!insp) { this.openId = null; return; }
    const completed = insp.status === 'complete';

    // group items by area
    const byArea: Record<string, any[]> = {};
    for (const it of insp.items || []) { (byArea[it.area || 'General'] = byArea[it.area || 'General'] || []).push(it); }

    const checklistHtml = Object.keys(byArea).map(area => `
      <div class="insp-area">${this.esc(area)}</div>
      ${byArea[area].map(it => {
        const seg = ITEM_STATES.map(s =>
          `<button class="${s} ${it.status === s ? 'on' : ''}" data-insp-item="${this.esc(insp.id)}|${this.esc(it.key)}|${s}" ${completed ? 'disabled' : ''}>${s}</button>`).join('');
        const photoReqUnmet = it.photo_required && !it.photo;
        const photo = it.photo
          ? `<a href="${this.esc(it.photo)}" target="_blank" rel="noopener"><img class="insp-thumb" src="${this.esc(it.photo)}" alt="photo"></a>`
          : (completed
              ? (it.photo_required ? '<span class="insp-photoreq missing">📷 photo missing</span>' : '')
              : `<span class="insp-link${photoReqUnmet ? ' insp-photoreq' : ''}" data-insp-itemphoto="${this.esc(insp.id)}|${this.esc(it.key)}">${photoReqUnmet ? '📷 Photo required' : '+ photo'}</span>`);
        const mustDo = it.required ? '<span class="insp-must" title="Must-do — required check for this property">MUST-DO</span>' : '';
        return `
          <div class="insp-item${it.required ? ' insp-item-req' : ''}">
            <div>
              <div class="ttl">${this.esc(it.title)} ${mustDo}</div>
              ${it.note ? `<div class="note">${this.esc(it.note)}</div>` : ''}
              ${completed ? '' : `<span class="insp-link" data-insp-itemnote="${this.esc(insp.id)}|${this.esc(it.key)}">${it.note ? 'edit note' : '+ note'}</span>`}
            </div>
            <div style="display:flex; align-items:center; gap:8px;">
              ${photo}
              <span class="insp-seg">${seg}</span>
            </div>
          </div>`;
      }).join('')}
    `).join('');

    const photosHtml = (insp.photos && insp.photos.length)
      ? `<div class="insp-media">${insp.photos.map((p: string) => `<a href="${this.esc(p)}" target="_blank" rel="noopener"><img src="${this.esc(p)}" alt="photo"></a>`).join('')}</div>`
      : '<div class="insp-sub" style="margin-top:6px;">No photos attached yet.</div>';

    const mediaSection = (insp.mode === 'photo' || insp.mode === 'video') ? `
      <div style="display:flex; flex-direction:column; gap:10px;">
        <label class="insp-fld">Video URL (walkthrough)
          <input id="insp-d-video" value="${this.esc(insp.video_url || '')}" placeholder="https://…" ${completed ? 'disabled' : ''}>
        </label>
        <div>
          <div class="insp-area" style="margin-top:0;">Photos</div>
          ${photosHtml}
          ${completed ? '' : `<div style="margin-top:8px;"><span class="insp-link" data-insp-photos="${this.esc(insp.id)}">+ add photo</span></div>`}
        </div>
        <label class="insp-fld">Inspector transcript / notes (fed to the AI grader)
          <textarea id="insp-d-transcript" placeholder="Describe what the walkthrough shows; the grader uses this plus the media." ${completed ? 'disabled' : ''}></textarea>
        </label>
        ${completed ? '' : `<div><button class="insp-btn primary" id="insp-d-grade">Grade with AI</button> <span class="insp-sub">grades 0–100 against the template; degrades gracefully if AI is unconfigured</span></div>`}
        <div id="insp-grade-out">${this.gradeBlock(insp)}</div>
      </div>` : `<div id="insp-grade-out">${this.gradeBlock(insp)}</div>`;

    const overlay = document.createElement('div');
    overlay.className = 'insp-modal-bg';
    overlay.id = 'insp-detail-modal';
    overlay.innerHTML = `
      <div class="insp-modal" data-insp-stop>
        <div class="insp-modal-head">
          <div>
            <h3>${this.esc(insp.listing_name)}</h3>
            <div class="insp-sub" style="margin-top:3px;">
              <span class="insp-chip">${this.modeLabel(insp.mode)}</span>
              · ${this.esc(this.tplName(insp.template_id))}
              · ${insp.inspector ? this.esc(insp.inspector) : 'unassigned'}
              · ${completed ? this.doneLabel(insp) : `started ${this.fmtDate(insp.created_at)}`}
              ${insp.grade != null ? ` · grade <b style="color:${this.gradeHex(insp.grade)};">${insp.grade}</b>` : ''}
            </div>
          </div>
          <button class="insp-x" data-insp-close>×</button>
        </div>
        ${completed ? '<div class="insp-basis" style="margin:0;">This inspection is complete — read-only.</div>' : ''}

        ${insp.mode === 'checklist'
          ? `<div>${checklistHtml || '<div class="insp-sub">No items on this inspection.</div>'}</div>`
          : mediaSection}

        <div class="insp-modal-actions" style="margin-top:6px;">
          ${completed ? '' : `<button class="insp-btn primary" data-insp-complete="${this.esc(insp.id)}">Complete inspection</button>`}
          <button class="insp-btn" data-insp-report="${this.esc(insp.id)}" title="Open the photo-forward owner report — Print / Save as PDF from there">Owner report <span class="insp-pdf-tag">PDF</span></button>
        </div>
      </div>`;
    this.container.appendChild(overlay);
    this.bindDetail(insp);
  }

  private gradeBlock(insp: any): string {
    if (insp.grade == null && !insp.feedback) return '';
    const missed = (insp.missed || []).length
      ? `<div style="margin-top:6px;"><span class="miss">Missed / failing: </span>${(insp.missed as string[]).map(m => this.esc(m)).join(', ')}</div>` : '';
    const fb = insp.feedback
      ? `<div style="margin-top:6px;">${(String(insp.feedback).split('\n').filter(Boolean).map(l => `<div>• ${this.esc(l)}</div>`).join(''))}</div>` : '';
    const head = insp.grade != null
      ? `<span class="insp-grade" style="background:${this.gradeSoft(insp.grade)}; color:${this.gradeHex(insp.grade)}; font-size:13px;">Grade ${insp.grade}</span>`
      : '<b>AI notes</b>';
    return `<div class="insp-ai" style="margin-top:10px;">${head}${missed}${fb}</div>`;
  }

  private bindDetail(insp: any): void {
    const overlay = document.getElementById('insp-detail-modal');
    if (!overlay) return;
    const close = () => { this.openId = null; overlay.remove(); };
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
    overlay.querySelector('[data-insp-close]')?.addEventListener('click', close);

    // checklist: status segment
    overlay.querySelectorAll<HTMLElement>('[data-insp-item]').forEach(el =>
      el.addEventListener('click', async () => {
        const [id, key, status] = el.dataset.inspItem!.split('|');
        const j = await this.post(`/inspections/${id}`, { items: [{ key, status }] });
        if (j?.inspection) { this.replaceInsp(j.inspection); this.draw(); }
      }));
    // checklist: note
    overlay.querySelectorAll<HTMLElement>('[data-insp-itemnote]').forEach(el =>
      el.addEventListener('click', async () => {
        const [id, key] = el.dataset.inspItemnote!.split('|');
        const cur = insp.items.find((it: any) => it.key === key)?.note || '';
        const note = prompt('Note for this item:', cur);
        if (note === null) return;
        const j = await this.post(`/inspections/${id}`, { items: [{ key, note }] });
        if (j?.inspection) { this.replaceInsp(j.inspection); this.draw(); }
      }));
    // checklist: per-item photo
    overlay.querySelectorAll<HTMLElement>('[data-insp-itemphoto]').forEach(el =>
      el.addEventListener('click', () => {
        const [id, key] = el.dataset.inspItemphoto!.split('|');
        this.uploadPhoto(id, key);
      }));
    // photo/video: add inspection-level photo
    overlay.querySelector<HTMLElement>('[data-insp-photos]')?.addEventListener('click', () => {
      this.uploadPhoto(insp.id, null);
    });
    // grade with AI
    overlay.querySelector('#insp-d-grade')?.addEventListener('click', async () => {
      // persist video URL + transcript context first if changed
      const video = (overlay.querySelector('#insp-d-video') as HTMLInputElement | null)?.value ?? null;
      const transcript = (overlay.querySelector('#insp-d-transcript') as HTMLTextAreaElement | null)?.value || '';
      if (video !== (insp.video_url || '')) {
        const upd = await this.post(`/inspections/${insp.id}`, { video_url: video });
        if (upd?.inspection) this.replaceInsp(upd.inspection);
      }
      const out = overlay.querySelector('#insp-grade-out');
      if (out) out.innerHTML = `<div class="insp-ai" style="margin-top:10px;">Grading…</div>`;
      const j = await this.post(`/inspections/${insp.id}/grade`, { transcript });
      if (!j) { if (out) out.innerHTML = this.gradeBlock(this.inspections.find(i => i.id === insp.id)); return; }
      if (j.inspection) this.replaceInsp(j.inspection);
      const fresh = this.inspections.find(i => i.id === insp.id);
      if (j.graded) {
        if (out) out.innerHTML = this.gradeBlock(fresh);
        this.draw();
      } else {
        // not graded — show the honest message (AI unconfigured / nothing to grade / unparseable)
        if (out) out.innerHTML = `<div class="insp-ai" style="margin-top:10px;"><b>${j.configured === false ? 'AI not configured' : 'Not graded'}</b><div style="margin-top:4px;">${this.esc(j.message || 'Could not grade.')}</div>${fresh?.feedback ? `<div style="margin-top:6px;">${this.esc(fresh.feedback)}</div>` : ''}</div>`;
      }
    });
    // complete
    overlay.querySelector<HTMLElement>('[data-insp-complete]')?.addEventListener('click', async () => {
      // persist any open video URL before completing
      const video = (overlay.querySelector('#insp-d-video') as HTMLInputElement | null)?.value;
      const body: any = { status: 'complete' };
      if (video != null && video !== (insp.video_url || '')) body.video_url = video;
      const j = await this.post(`/inspections/${insp.id}`, body);
      if (j?.inspection) {
        this.replaceInsp(j.inspection);
        this.openId = null;
        overlay.remove();
        await this.reloadStatsAndBoard();
        this.draw();
      }
    });
    // owner report (new tab)
    overlay.querySelector<HTMLElement>('[data-insp-report]')?.addEventListener('click', () => {
      void openApiDoc(`${API}/inspections/${insp.id}/report`, 'Inspection report');
    });
  }

  private replaceInsp(updated: any): void {
    const idx = this.inspections.findIndex(i => i.id === updated.id);
    // R26: only GET /inspections attaches logged_issues — every other endpoint
    // returns the raw stored record. Preserve the tickets we already have, or
    // the report card would say "No issues" right after a tickets-only submit.
    // Stacy 2026-10-07: same for performed_at (only GET /inspections computes it)
    // — dropping it would re-date the visit to created_at until the next reload.
    if (idx >= 0 && updated.performed_at === undefined && this.inspections[idx]?.performed_at !== undefined) {
      const hadPhotos = this.evidenceCount(this.inspections[idx]) > 0;
      // a first photo just landed on a photo-less record → the visit is now
      if (hadPhotos || this.evidenceCount(updated) === 0) updated.performed_at = this.inspections[idx].performed_at;
    }
    if (idx >= 0 && updated.logged_issues === undefined && this.inspections[idx]?.logged_issues !== undefined) {
      updated.logged_issues = this.inspections[idx].logged_issues;
    }
    // R120: same for issues[].disposition (GET /inspections merges the board's
    // dismissals into it) — kept while the finding's own action is unchanged
    if (idx >= 0 && Array.isArray(updated.issues) && updated !== this.inspections[idx]) updated.issues = this.keepDispositions(this.inspections[idx].issues, updated.issues);
    if (idx >= 0) this.inspections[idx] = updated; else this.inspections.push(updated);
  }
  private keepDispositions(prev: any[] | undefined, next: any[]): any[] {
    if (!Array.isArray(next)) return next;
    const byId = new Map((Array.isArray(prev) ? prev : []).filter(Boolean).map((x: any) => [x.id, x]));
    return next.map((x: any) => {
      if (!x || x.disposition !== undefined) return x;
      const p: any = byId.get(x.id);
      if (!p || p.disposition === undefined) return x;
      // a dismiss / restore made just now changed the action — derive afresh
      if (JSON.stringify(p.action || null) !== JSON.stringify(x.action || null)) return x;
      // review R120 #17: so does a fix marked on the spot, unless the office
      // decided it on the board (only the server's overlay knows that, and the
      // office's call outranks the record's own state)
      if ((!p.disposition || p.disposition.via !== 'board') && !!p.fixed !== !!x.fixed) return x;
      return { ...x, disposition: p.disposition };
    });
  }
  // Review R120 #17: /issue-update and /issues answer the RAW stored issues
  // (no disposition). Every response like that is adopted HERE, through
  // keepDispositions — assigning it straight onto the local record (then
  // replaceInsp on that same object) dropped the office's dismissal of a
  // sibling finding, which then read as open with its buttons back.
  private adoptIssues(insp: any, next: any): void {
    if (!insp || !Array.isArray(next)) return;
    insp.issues = this.keepDispositions(insp.issues, next);
  }

  // multipart photo upload — server uses upload.any(); item_key optional.
  private uploadPhoto(id: string, itemKey: string | null): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const fd = new FormData();
      const tk = this.takenAt(file); if (tk) fd.append('taken_at', tk);
      fd.append('photo', file);
      if (itemKey) fd.append('item_key', itemKey);
      const r = await fetch(`${API}/inspections/${id}/photo`, { method: 'POST', body: fd });
      const j = await r.json().catch(() => ({} as any));
      if (!r.ok) { alert(j.error || 'upload failed'); return; }
      if (j.inspection) this.replaceInsp(j.inspection);
      this.draw();
    };
    input.click();
  }

  // ════════════════════════════════════════════════════════════
  //  CAPTURE-FIRST FLOW + AI REPORT CARD (Phase 1)
  // ════════════════════════════════════════════════════════════

  // Photos are the DEFAULT capture mode (R17 PDF) — video stays one tap away.
  private captureMode: 'record' | 'photo' = 'photo';
  // Structured form defs (basic/deep/audit) — served by GET /inspections/forms
  // so client + server never drift.
  private formDefs: any = null;
  // Sections the inspector has open — redraws (incl. async photo-upload draws)
  // must not collapse them mid-workflow.
  private openFormSecs = new Set<string>();
  // Unsent form answers, merged across failed saves and retried on the next
  // save — a dead zone must not silently drop the form.
  // AI-unblock review: answers held offline are keyed by inspection — a flush
  // must never write inspection A's scores onto B, and B's Submit must not
  // wait on A's held answers.
  private formPending: Record<string, any> = {};

  // The full-page surface that replaces the tab board during capture/report.
  // It owns #insp-body, so it carries the shared <style> block (styleBlock()).
  private stripSeededFor: string | null = null;
  private drawCaptureSurface(body: HTMLElement): void {
    const insp = this.activeInsp();
    if (!insp) { this.exitCapture(); this.draw(); return; }
    // Seed the photo-mode room strip from the persisted classification ONCE per
    // open, so an auto-sorted inspection still shows its rooms after a reload.
    if (this.stripSeededFor !== insp.id) { this.seedPhotoStrip(insp); this.stripSeededFor = insp.id; }
    body.innerHTML = `${this.styleBlock()}${this.view === 'report' ? this.renderReportCard(insp) : this.renderCapture(insp)}`;
    if (this.view === 'report') this.bindReport(insp); else this.bindCapture(insp);
  }

  // Rebuild the room-strip tally/thumbs from EVERY frame, so it survives
  // reloads instead of being session-only state. Frames with persisted
  // frame_meta group by their AI-sorted room; frames in capture.frame_urls
  // WITHOUT meta (legacy inspections predate per-frame meta) land in the
  // '📥 Unsorted' group — the strip must never show only the classified
  // subset (the "100 uploaded, 20 shown" complaint).
  private static readonly UNSORTED = 'Unsorted';
  private seedPhotoStrip(insp: any): void {
    this.photoTags = {};
    this.photoThumbs = {};
    const meta = (insp && insp.capture && Array.isArray(insp.capture.frame_meta)) ? insp.capture.frame_meta : [];
    const areaByUrl = new Map<string, string>();
    for (const m of meta) {
      if (!m || !m.url) continue;
      areaByUrl.set(String(m.url), String(m.area || InspectionsPage.UNSORTED));
    }
    const urls: string[] = ((insp && insp.capture && insp.capture.frame_urls) || []).filter(Boolean).map(String);
    const seen = new Set<string>();
    for (const u of urls) {
      seen.add(u);
      const area = areaByUrl.get(u) || InspectionsPage.UNSORTED;
      (this.photoThumbs[area] = this.photoThumbs[area] || []).push(u);
      this.photoTags[area] = (this.photoTags[area] || 0) + 1;
    }
    // Meta rows whose url never landed in frame_urls (shouldn't happen) stay
    // visible anyway — the strip errs on showing every photo we know about.
    for (const m of meta) {
      if (!m || !m.url || !m.area || seen.has(String(m.url))) continue;
      (this.photoThumbs[m.area] = this.photoThumbs[m.area] || []).push(m.url);
      this.photoTags[m.area] = (this.photoTags[m.area] || 0) + 1;
    }
  }

  private exitCapture(): void {
    this.stopRecognition();
    this.stopStream();
    // R26: kill the embedded logger too — its own mic/dictation would
    // otherwise stay hot after leaving the capture view.
    if (this.qliInstance) { this.qliInstance.destroy(); this.qliInstance = null; this.qliFor = null; }
    this.cleanCtx = null; this.cleanCtxFor = null;   // R35: never carry a clean-context across inspections
    this.recorder = null; this.recChunks = []; this.recordedBlob = null;
    this.transcriptFinal = ''; this.capNote = null; this.syncNote = null;
    this.view = null; this.activeId = null;
    this.stripSeededFor = null; // re-seed the strip from frame_meta on next open
  }

  private stopStream(): void {
    if (this.mediaStream) { this.mediaStream.getTracks().forEach(t => t.stop()); this.mediaStream = null; }
  }
  private stopRecognition(): void {
    if (this.recognition) { try { this.recognition.onend = null; this.recognition.stop(); } catch (_) {} this.recognition = null; }
  }

  private frameCount(insp: any): number { return (insp?.capture?.frame_urls || []).length; }
  // every on-site upload (frames, loose photos, item photos) — what dates a visit
  private evidenceCount(insp: any): number {
    return this.frameCount(insp) + (insp?.photos || []).length + (insp?.items || []).filter((t: any) => t && t.photo).length;
  }
  // when the photo was TAKEN, for the server's visit date (lib/inspectionDates.js):
  // a camera-roll pick carries its own time; a live shot is ~now
  private takenAt(file: Blob): string | null {
    const lm = file instanceof File ? file.lastModified : NaN;
    return Number.isFinite(lm) && lm > Date.now() - 30 * 86400000 && lm <= Date.now() + 5 * 60000 ? String(lm) : null;
  }
  private hasTranscript(insp: any): boolean { return !!(this.curTranscript(insp).trim()); }
  // Live committed transcript (record mode) merged with whatever's persisted.
  private curTranscript(insp: any): string {
    const persisted = (insp?.capture?.transcript || '').trim();
    const live = this.transcriptFinal.trim();
    return live || persisted;
  }

  // ── capture view ────────────────────────────────────────────
  private renderCapture(insp: any): string {
    const tpl = this.templates.find(t => t.id === insp.template_id);
    const areas: string[] = tpl ? [...new Set((tpl.items || []).map((it: any) => it.area || 'General'))] as string[] : [];
    const frames = this.frameCount(insp);
    // R26: logged tickets alone are enough to submit a basic inspection —
    // an issues-only walkthrough with pillar scores shouldn't be trapped.
    const hasTickets = (this.activeLoggedFor === insp.id && this.activeLoggedCount > 0) || (insp.logged_issues || []).length > 0;
    const scored = insp.form_type === 'basic' && Object.keys((insp.form && insp.form.scores) || {}).length > 0;   // AI-unblock: a clean home with nothing wrong
    const canAnalyze = frames > 0 || this.hasTranscript(insp) || (insp.form_type === 'basic' && hasTickets) || scored;

    const modeToggle = CAN_RECORD ? `
      <div class="cap-modes" role="tablist">
        <button class="cap-mode-btn ${this.captureMode === 'record' ? 'on' : ''}" data-cap-mode="record">Record walkthrough</button>
        <button class="cap-mode-btn ${this.captureMode === 'photo' ? 'on' : ''}" data-cap-mode="photo">Photo mode</button>
      </div>` : '';

    const recordPane = this.captureMode === 'record' && CAN_RECORD ? this.renderRecordPane(insp) : '';
    const photoPane = (this.captureMode === 'photo' || !CAN_RECORD) ? this.renderPhotoPane(insp, areas) : '';
    const noRecNote = !CAN_RECORD
      ? `<div class="cap-msg warn">This device/browser can’t record video (no camera or MediaRecorder). Use Photo mode and the notes field below — the AI grades from frames + notes.</div>`
      : '';

    // Common blocks reused by both layouts.
    const captureCard = `
        ${noRecNote}
        ${modeToggle}
        <div class="cap-card card-elev">
          ${recordPane}
          ${photoPane}
        </div>
        <div class="cap-card card-elev">
          <div class="cap-section-h">Import from a link</div>
          <div class="cap-link-row">
            <input type="url" class="cap-link-input" id="cap-link-url" placeholder="https://drive.google.com/file/d/…/view">
            <button class="cap-link-btn" id="cap-link-import">Import</button>
          </div>
          <div class="cap-hint">Public Google Drive (“anyone with the link”) or a direct file URL. Images grade; a video is stored for review.</div>
          <div id="cap-link-msg"></div>
        </div>
        <div class="cap-card card-elev">
          <div class="cap-section-h">Inspector notes / transcript</div>
          <textarea class="cap-transcript" id="cap-transcript" placeholder="Speak during recording (auto-fills here) or type what the walkthrough shows.">${this.esc(this.curTranscript(insp))}</textarea>
          <div class="cap-hint">${SpeechRec ? 'Live speech-to-text runs while recording; you can still edit here.' : 'Live speech-to-text isn’t available in this browser — type notes here; the AI uses them with the frames.'}</div>
        </div>`;
    const framesTally = this.framesTallyHtml(insp);   // R114: this inspection vs earlier ones, from the phone's queue
    const analyzeBar = `
        <div class="cap-analyze-bar">
          <button class="cap-analyze-btn" id="cap-analyze" ${canAnalyze ? '' : 'disabled'}>Submit Inspection →</button>
          ${canAnalyze ? '' : '<div class="cap-hint" style="text-align:center;">Capture a photo, add notes or score the clean to submit.</div>'}
        </div>`;
    const head = `
        <div class="cap-top" style="display:flex; justify-content:space-between; align-items:center; gap:10px;">
          <button class="cap-back" data-cap-back>← Inspections</button>
          ${insp.form_type === 'basic' ? '' : '<button class="cap-back" data-cap-logissue style="color:var(--accent); font-weight:700;">＋ Log an issue</button>'}
        </div>
        <div class="emg-bar">
          <span class="emg-t">🚨 Emergency or immediate need?</span>
          <a class="emg-btn" href="tel:+16198155111">📞 Call</a>
          <a class="emg-btn" href="sms:+16198155111">💬 Text</a>
          <span class="emg-num">(619) 815-5111</span>
        </div>
        <div style="margin-bottom:14px;">
          <h2 class="cap-title">${this.esc(insp.listing_name)}</h2>
          <div class="cap-meta">${this.esc(this.tplName(insp.template_id))} · ${insp.inspector ? this.esc(insp.inspector) : 'unassigned'} · capture the property, the AI fills the checklist</div>
        </div>`;
    const foot = `
        <div id="cap-msg">${this.capNoteHtml(insp)}</div>
        <div style="text-align:center; margin-top:12px;">
          <span class="insp-link" data-insp-manual="${this.esc(insp.id)}">open manual checklist instead</span>
        </div>`;

    // R25: BASIC inspections run STEPWISE — the inspection always STARTS with
    // issues (report as you walk, growing ticket list, mark fixed / follow-up),
    // THEN moves to scoring (found → left per pillar) and submit.
    if (insp.form_type === 'basic') {
      // R26: issue count = quick-logged tickets (live via embed onCount) +
      // legacy R25 inspector tickets still stored on insp.issues.
      if (this.activeLoggedFor !== insp.id) {
        this.activeLoggedFor = insp.id;
        this.activeLoggedCount = (insp.logged_issues || []).length;
      }
      const legacyN = (insp.issues || []).filter((i: any) => i.source === 'inspector').length;
      const nIssues = this.activeLoggedCount + legacyN;
      const stepBar = `
        <div class="cap-stepbar">
          <button class="cap-step ${this.captureStep === 1 ? 'on' : ''}" data-cap-step="1">Step 1 · Issues${nIssues ? ` (${nIssues})` : ''}</button>
          <span class="cap-step-sep">→</span>
          <button class="cap-step ${this.captureStep === 2 ? 'on' : ''}" data-cap-step="2">Step 2 · Scoring & submit</button>
        </div>`;
      if (this.captureStep === 1) {
        // R26 E: no adaptive-checklist card here — inspectors go straight to
        // required checks + issue logging.
        return `
      <div class="cap-wrap">
        ${head}
        ${stepBar}
        ${this.renderFocusPanel(insp)}
        ${this.renderReferencePanel(insp)}
        ${this.renderInventoryPanel(insp)}
        ${this.renderStockCheck(insp)}
        ${this.renderIssuesStep(insp)}
        ${captureCard}
        ${framesTally}
        <div class="cap-analyze-bar cap-bar-2">
          <button class="cap-exit-btn" data-cap-saveexit title="${this.esc(this.saveExitTitle(insp))}">💾 Save &amp; exit</button>
          <button class="cap-analyze-btn" data-cap-step="2">Continue to scoring →</button>
        </div>
        ${foot}
      </div>`;
      }
      return `
      <div class="cap-wrap">
        ${head}
        ${stepBar}
        ${this.renderIssuesSummary(insp)}
        ${this.renderFormCard(insp)}
        ${framesTally}
        ${analyzeBar}
        <div style="text-align:center; margin-top:8px; display:flex; justify-content:center; gap:18px; flex-wrap:wrap;">
          <span class="insp-link" data-cap-step="1">← back to issues</span>
          <span class="insp-link" data-cap-saveexit title="${this.esc(this.saveExitTitle(insp))}">💾 Save &amp; exit — finish later</span>
        </div>
        ${foot}
      </div>`;
    }

    // Deep / audit / unstructured: original single-page layout.
    return `
      <div class="cap-wrap">
        ${head}
        ${this.renderSuggestCard(insp)}
        ${this.renderFocusPanel(insp)}
        ${this.renderReferencePanel(insp)}
        ${this.renderInventoryPanel(insp)}
        ${this.renderFormCard(insp)}
        ${captureCard}
        ${framesTally}
        ${analyzeBar}
        ${foot}
      </div>`;
  }

  // R26 Step 1 — the quick-submit logger, embedded. Same rapid capture as the
  // Log Issues page (photo dump, inline edit, per-row photos, delete), scoped
  // to THIS inspection and persistent: reopening shows everything logged.
  // The embed mounts in bindCapture (needs a live DOM node).
  private renderIssuesStep(insp: any): string {
    // Legacy R25 tickets (created via the old composer into insp.issues) stay
    // visible + editable below the logger until those inspections age out.
    const legacy: any[] = (insp.issues || []).filter((i: any) => i.source === 'inspector');
    const sevHex: Record<string, string> = { high: 'var(--danger-text, #b91c1c)', med: 'var(--warning-text, #a16207)', low: 'var(--text-muted)' };
    const FUS: [string, string][] = [['', 'Needs follow-up?'], ['before_next_checkin', 'Before next check-in'], ['during_stay', 'During guest stay'], ['after_checkout', 'After checkout']];
    const legacyRows = legacy.map(iss => {
      const id = this.esc(String(iss.id || iss.note));
      const badge = iss.fixed
        ? '<span class="iss-step-badge fixed">✔ fixed during inspection</span>'
        : iss.follow_up
          ? `<span class="iss-step-badge fu">⏰ ${this.esc(String(iss.follow_up).replace(/_/g, ' '))}</span>`
          : '<span class="iss-step-badge open">open</span>';
      return `
        <div class="iss-step-row">
          <div class="iss-step-main">
            <span style="font-weight:700; color:${sevHex[iss.severity] || 'var(--text-muted)'}; font-size:11px; text-transform:uppercase;">${this.esc(iss.severity || 'med')}</span>
            <span class="iss-step-type">${this.esc(iss.type || '')}</span>
            <div class="iss-step-note">${this.esc(iss.note)}</div>
            ${badge}
          </div>
          <div class="iss-step-acts">
            <button class="insp-btn ${iss.fixed ? 'primary' : ''}" data-iss-fixed="${id}|${iss.fixed ? 0 : 1}">${iss.fixed ? '✔ Fixed' : 'Mark fixed'}</button>
            <select class="iss-step-fu" data-iss-fu="${id}">${FUS.map(([v, l]) => `<option value="${v}" ${String(iss.follow_up || '') === v ? 'selected' : ''}>${l}</option>`).join('')}</select>
          </div>
        </div>`;
    }).join('');
    return `
      <div class="cap-card card-elev" style="border-left:3px solid var(--accent);">
        <div class="cap-section-h">Step 1 · Identify &amp; report issues</div>
        <div class="cap-hint" style="margin-bottom:10px;">Walk the home and log every issue — photo dump, quick note, or both. This list stays with the inspection: come back any time to add photos, set the room, change urgency, mark fixed, or delete.</div>
        <div id="iss-qli-host"><div class="cap-hint">Loading your issue list…</div></div>
        ${legacyRows ? `<div class="iss-step-list" style="margin-top:10px;"><div class="cap-hint" style="margin-bottom:4px;">Earlier tickets on this inspection:</div>${legacyRows}</div>` : ''}
      </div>
      <div id="insp-cleanctx"></div>`;
  }

  // Step 2 header — compact recap of the issue step (counts by state).
  // R26: counts span BOTH stores — quick-logged tickets + legacy insp.issues.
  private renderIssuesSummary(insp: any): string {
    const logged: any[] = insp.logged_issues || [];
    const legacy: any[] = (insp.issues || []).filter((i: any) => i.source === 'inspector');
    const all = [...logged, ...legacy];
    if (!all.length) return '';
    const fixed = all.filter(i => i.fixed).length;
    const fu = all.filter(i => !i.fixed && i.follow_up).length;
    const open = all.length - fixed - fu;
    return `
      <div class="cap-card card-elev" style="padding:10px 14px;">
        <span style="font-size:13px; font-weight:600;">Issues from step 1:</span>
        <span class="iss-step-badge open" style="margin-left:8px;">${open} open</span>
        <span class="iss-step-badge fixed" style="margin-left:6px;">${fixed} fixed</span>
        <span class="iss-step-badge fu" style="margin-left:6px;">${fu} follow-up</span>
        <span class="insp-link" data-cap-step="1" style="margin-left:10px;">edit</span>
      </div>`;
  }

  // ── Feature B: cadence FOCUS items — "Required this visit" ──────
  // Fetch the DUE focus set for the active inspection's property. Cached by
  // listing_id so re-renders don't re-fetch; ticked ids reset per capture.
  private async loadFocus(insp: any): Promise<void> {
    const lid = String(insp.listing_id);
    if (this.focusFor === lid && this.focus) return;
    this.focusFor = lid;
    this.focus = null;
    this.focusDone = new Set();
    this.focusSkip = new Set();
    const j = await this.get(`/inspections/focus/${encodeURIComponent(lid)}`);
    this.focus = Array.isArray(j?.focus) ? j.focus : [];
    // Redraw in BOTH capture and report views — the focus panel shows on the
    // report card too (e.g. opening an analyzed inspection straight from the
    // board sets view='report'), and the panel must not hang on "loading…".
    if ((this.view === 'capture' || this.view === 'report') && this.activeId === insp.id) this.draw();
  }

  // "Required this visit" panel: the DUE cadence items with overdue badges + a
  // checkbox the inspector ticks when addressed. Honest empty state when none.
  // R58: complete/route a focus item's linked work order from either view.
  private async focusWoAction(focusId: string, action: string, btn: HTMLButtonElement): Promise<void> {
    const insp = this.activeInsp();
    if (!insp) return;
    btn.disabled = true;
    const r = await fetch(`${API}/inspections/focus-action`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ listing_id: insp.listing_id, focus_id: focusId, action, by: insp.inspector || '' }),
    }).catch(() => null);
    btn.disabled = false;
    if (!r || !r.ok) { const j = r ? await r.json().catch(() => ({})) : null; alert((j && j.error) || 'Action failed — try again.'); return; }
    const j = await r.json().catch(() => ({}));
    const f = (this.focus || []).find(x => x.id === focusId);
    if (f) {
      f.wo_id = j.wo_id || f.wo_id;
      f.wo_status = action === 'wo-done' ? 'done' : (f.wo_status || 'open');
    }
    this.draw();
  }

  // R64 (Stacy: "can't see what to submit") — the PDS reference photos +
  // quirks as a capture-view panel, same source the cleaner job sheet uses.
  // Lazy-fetched per listing; photos tap to full size.
  private renderReferencePanel(insp: any): string {
    const lid = String(insp.listing_id || '');
    if (!lid) return '';
    if (this.propBriefFor !== lid) {
      this.propBriefFor = lid;
      this.propBrief = null;
      fetch(`${API}/inspections/property-brief/${encodeURIComponent(lid)}`)
        .then(r => r.ok ? r.json() : null)
        .then(j => { if (j && this.propBriefFor === lid) { this.propBrief = j; this.draw(); } })
        .catch(() => { /* panel just stays empty */ });
      return `<div class="cap-card" style="padding:10px 14px; font-size:12px; color:var(--text-muted);">📸 Loading reference photos…</div>`;
    }
    const b = this.propBrief;
    if (!b) return '';
    const refs = (b.references || []).filter((r: any) => (r.photos || []).length || r.caption);
    const quirks = (b.quirks || []).filter((q: any) => q.note || q.label);
    const checks = (b.checks || []).filter((c: any) => c.label);   // R82: explicit property checks
    const gtkAll = (b.good_to_know || []).filter((g: any) => g.label);   // R83 #1: dedicated info section
    const gtkSel = gtkAll.filter((g: any) => g.arrival);
    const gtk = gtkSel.length ? gtkSel : gtkAll;   // R87 #1.7: curated arrival lines lead; uncurated shows all
    const oreq = (b.owner_requirements || []).filter((o: any) => o.label);   // R87 #1.7
    if (!refs.length && !quirks.length && !checks.length && !gtk.length && !oreq.length) return '';
    // Shared 84px thumb strip (same markup as references) for checks + good-to-know.
    const thumbStrip = (photos: any) => (Array.isArray(photos) && photos.length)
      ? `<div style="display:flex; gap:6px; overflow-x:auto; -webkit-overflow-scrolling:touch; padding:3px 0 2px;">${photos.map((u: string) => `<img src="${this.esc(u)}" data-refb-photo="${this.esc(u)}" alt="" loading="lazy" decoding="async" style="width:84px; height:84px; object-fit:cover; border-radius:8px; border:1px solid var(--border); flex:none; cursor:pointer;">`).join('')}</div>`
      : '';
    return `
      <details class="cap-card" id="cap-refpanel" ${this.refPanelOpen ? 'open' : ''} style="padding:0;">
        <summary style="cursor:pointer; padding:12px 14px; font-size:13px; font-weight:700; list-style:none;">📸 Reference — what it should look like <span style="font-weight:400; color:var(--text-muted);">(${refs.length} spot${refs.length === 1 ? '' : 's'}${quirks.length ? ` · ${quirks.length} quirk${quirks.length === 1 ? '' : 's'}` : ''})</span></summary>
        <div style="padding:0 14px 12px;">
          ${refs.map((r: any) => `
            <div style="margin-bottom:10px;">
              <div style="font-size:12.5px; font-weight:600;">${this.esc(r.label)}</div>
              ${r.caption ? `<div style="font-size:11.5px; color:var(--text-muted); margin:1px 0 4px;">${this.esc(r.caption)}</div>` : ''}
              ${(r.photos || []).length ? `<div style="display:flex; gap:6px; overflow-x:auto; -webkit-overflow-scrolling:touch; padding-bottom:2px;">
                ${(r.photos || []).map((u: string) => `<img src="${this.esc(u)}" data-refb-photo="${this.esc(u)}" alt="" loading="lazy" decoding="async" style="width:84px; height:84px; object-fit:cover; border-radius:8px; border:1px solid var(--border); flex:none; cursor:pointer;">`).join('')}
              </div>` : ''}
            </div>`).join('')}
          ${checks.length ? `<div style="font-size:12px; font-weight:700; margin:8px 0 4px;">☑ Property checklist — verify each</div>
            ${checks.map((c: any) => `<div style="font-size:12px; margin-bottom:3px;"><strong>${this.esc(c.label)}</strong>${c.note ? ` — ${this.esc(c.note)}` : ''}${thumbStrip(c.photos)}</div>`).join('')}` : ''}
          ${oreq.length ? `<div style="font-size:12px; font-weight:700; margin:8px 0 4px;">👑 Owner requirements — follow each</div>
            ${oreq.map((o: any) => `<div style="font-size:12px; margin-bottom:3px;"><strong>${this.esc(o.label)}</strong>${o.note ? ` — ${this.esc(o.note)}` : ''}${thumbStrip(o.photos)}</div>`).join('')}` : ''}
          ${gtk.length ? `<div style="font-size:12px; font-weight:700; margin:8px 0 4px;">📌 Good to know</div>
            ${gtk.map((g: any) => `<div style="font-size:12px; margin-bottom:3px;">${g.about === 'owner' ? '<span title="About the owner">👤 Owner: </span>' : ''}<strong>${this.esc(g.label)}</strong>${g.note ? ` — ${this.esc(g.note)}` : ''}${thumbStrip(g.photos)}</div>`).join('')}` : ''}
          ${quirks.length ? `<div style="font-size:12px; font-weight:700; margin:8px 0 4px;">⚠ Quirks to know</div>
            ${quirks.map((q: any) => `<div style="font-size:12px; margin-bottom:3px;"><strong>${this.esc(q.label)}</strong>${q.note ? ` — ${this.esc(q.note)}` : ''}</div>`).join('')}` : ''}
        </div>
      </details>`;
  }

  // R79 #3: the monthly inventory count on the inspection task — the same
  // block the HK job sheet carries (property-brief serves it); either party
  // completes it and the due flag clears everywhere (property-level stamp).
  private renderInventoryPanel(insp: any): string {
    const inv = this.propBrief && this.propBrief.inventory;
    if (!inv || !Array.isArray(inv.items) || !inv.items.length) return '';
    const open = this.invPanelOpen != null ? this.invPanelOpen : !!inv.due;
    const daysAgo = inv.counted_at ? Math.floor((Date.now() - Date.parse(inv.counted_at)) / 86400000) : null;
    const badge = inv.due
      ? '<span style="font-size:10.5px; font-weight:700; color:var(--warning-text,#92400e); background:var(--warning-soft,#fef3c7); border-radius:999px; padding:1px 8px;">DUE</span>'
      : `<span style="font-size:10.5px; color:var(--success-text,#166534); background:var(--success-soft,#f0fdf4); border-radius:999px; padding:1px 8px;">✓ ${daysAgo != null ? `${daysAgo}d ago` : 'done'}</span>`;
    const byCat = new Map<string, any[]>();
    for (const it of inv.items) { if (!byCat.has(it.category)) byCat.set(it.category, []); byCat.get(it.category)!.push(it); }
    return `
      <details class="cap-card" id="cap-invpanel" ${open ? 'open' : ''} style="padding:0;">
        <summary style="cursor:pointer; padding:12px 14px; font-size:13px; font-weight:700; list-style:none; display:flex; gap:8px; align-items:center;">📦 Inventory count ${badge} <span style="font-weight:400; color:var(--text-muted);">monthly${inv.counted_at ? ` · last ${this.esc(String(inv.counted_at).slice(0, 10))}` : ' · never completed'}</span></summary>
        <div style="padding:0 14px 12px;">
          <div style="font-size:11.5px; color:var(--text-muted); margin-bottom:6px;">Count what's actually in the home — "expect" is the PAR rule for this property. Completing stamps the month for housekeeping, inspections and the office alike.</div>
          ${[...byCat.entries()].map(([cat, items]) => `
            <div style="font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted); margin:8px 0 4px;">${this.esc(cat)}</div>
            ${items.map((it: any) => {
              const short = it.expected != null && it.qty != null && it.qty < it.expected;
              return `<div style="display:flex; gap:8px; align-items:center; padding:5px 8px; border:1px solid var(--border-soft,#f1f5f9); border-radius:8px; margin-bottom:4px; background:${short ? 'var(--warning-soft,#fffbeb)' : 'var(--bg-subtle,#f8fafc)'};">
                <span style="flex:1; font-size:12.5px;">${this.esc(it.item)}</span>
                ${it.expected != null ? `<span style="font-size:11px; color:${short ? 'var(--warning-text,#92400e)' : 'var(--text-muted)'};">expect ${this.esc(it.expected)}${(Array.isArray(it.placements) && it.placements.some((p: any) => p && p.label && p.count != null)) ? ` <span style="opacity:.85;">(incl. ${it.placements.filter((p: any) => p && p.label && p.count != null).map((p: any) => `${this.esc(p.count)} ${this.esc(p.label)}`).join(' · ')})</span>` : ''}${parSizesText(it) ? ` <span style="opacity:.85;" title="Per bed size — from this home's beds (PDS)">(incl. ${this.esc(parSizesText(it))})</span>` : ''}</span>` : ''}
                <input type="number" min="0" max="9999" inputmode="numeric" placeholder="qty" value="${it.qty == null ? '' : this.esc(it.qty)}" data-invb-qty="${this.esc(it.item)}" style="width:60px; padding:5px 7px; border:1px solid var(--border); border-radius:8px; font-size:12.5px; text-align:right;">
              </div>`;
            }).join('')}`).join('')}
          <button class="cap-btn" data-invb-done style="margin-top:8px;">✓ Count complete</button>
        </div>
      </details>`;
  }

  private renderFocusPanel(insp: any): string {
    if (this.focus == null) {
      // Kick off the fetch (once) and show a light loading row.
      void this.loadFocus(insp);
      return `
        <div class="cap-card card-elev">
          <div class="cap-section-h">Required this visit</div>
          <div class="insp-sub">Checking which cadence items are due…</div>
        </div>`;
    }
    const due = (this.focus || []).filter(f => f.due);
    if (!due.length) {
      return `
        <div class="cap-card card-elev">
          <div class="cap-section-h">Required this visit</div>
          <div class="insp-sub">Nothing on a cadence is due for this property right now — no required focus items this visit.</div>
        </div>`;
    }
    const rows = due.map(f => {
      const checked = this.focusDone.has(f.id);
      const skipped = this.focusSkip.has(f.id);
      const overdue = (f.days_overdue != null && f.days_overdue > 0)
        ? `<span class="foc-badge over">${f.days_overdue} day${f.days_overdue === 1 ? '' : 's'} overdue</span>`
        : (f.last_done == null
            ? `<span class="foc-badge new">never logged</span>`
            : `<span class="foc-badge due">due now</span>`);
      const missBadge = (f.misses > 0)
        ? `<span class="foc-badge miss" title="times this required check was skipped before">${f.misses} prior miss${f.misses === 1 ? '' : 'es'}</span>`
        : '';
      const quirkBadge = f.source === 'quirk' ? `<span class="foc-badge quirk" title="From a Property Details quirk note">Quirk</span>` : '';
      const photoBadge = f.photo_required ? `<span class="foc-badge photo" title="Take a photo as proof">📷 photo</span>` : '';
      // R58: the card was title-only ("doesn't really show anything") — show
      // the source issue's photo + note, and act on the linked WO in place.
      const detail = (f.photo || f.description) ? `
        <div style="display:flex; gap:8px; margin:6px 0 2px 26px; align-items:flex-start;">
          ${f.photo ? `<img src="${this.esc(f.photo)}" alt="" style="width:64px; height:64px; object-fit:cover; border-radius:6px; cursor:pointer; flex:none;" data-foc-photo="${this.esc(f.photo)}"/>` : ''}
          ${f.description ? `<div style="font-size:12px; color:var(--text-muted); line-height:1.4;">${this.esc(f.description)}</div>` : ''}
        </div>` : '';
      const woActions = f.wo_id
        ? (f.wo_status === 'done'
          ? `<span class="insp-sub" style="margin-left:26px;">✓ WO done</span>`
          : `<div style="display:flex; gap:6px; margin:4px 0 2px 26px;">
               <button type="button" class="insp-btn sm" data-foc-wodone="${this.esc(f.id)}">✓ Complete WO</button>
               <button type="button" class="insp-btn sm" data-foc-assignmn="${this.esc(f.id)}">→ Assign to maintenance</button>
             </div>`)
        : `<div style="margin:4px 0 2px 26px;"><button type="button" class="insp-btn sm" data-foc-assignmn="${this.esc(f.id)}">→ Assign to maintenance</button></div>`;
      return `
        <div class="foc-row${checked ? ' on' : ''}${skipped ? ' skip' : ''}">
          <div class="foc-head">
            <label class="foc-pick">
              <input type="checkbox" data-foc-tick="${this.esc(f.id)}" ${checked ? 'checked' : ''} ${skipped ? 'disabled' : ''}>
              <span class="foc-main">
                <span class="foc-ttl">${this.esc(f.title)} ${quirkBadge}${photoBadge}</span>
                <span class="foc-sub">${this.esc(f.area || 'General')} · ${overdue} ${missBadge}</span>
              </span>
            </label>
            <button type="button" class="foc-skip${skipped ? ' on' : ''}" data-foc-skip="${this.esc(f.id)}" title="Not applicable this visit — won't count as a miss or nag next time">${skipped ? 'N/A ✓' : 'N/A'}</button>
          </div>
          ${detail}
          ${woActions}
        </div>`;
    }).join('');
    return `
      <div class="cap-card card-elev">
        <div class="cap-section-h">Required this visit <span class="foc-count">${due.length}</span></div>
        <div class="insp-sub" style="margin-bottom:8px;">Cadence checks that have come due for this property. Tick each as you address it — skipped items open a re-check ticket and count against this visit. Mark <b>N/A</b> if it doesn't apply (no miss, won't nag next visit).</div>
        ${rows}
      </div>`;
  }

  // ── Phase 3: "Suggest checklist for this property" ──────────────
  // A collapsible card in the capture view. Fetches /suggest-template/:listing_id,
  // shows the proposed items grouped by area, the signals that drove each (as
  // chips), and the honest note. Two actions: use these items for THIS inspection
  // (no template saved), or save them as the property's default.
  private renderSuggestCard(insp: any): string {
    const saved = this.savedPropTemplate(insp.listing_id);
    const savedLine = saved
      ? `<div class="sg-saved">Using <b>${this.esc(saved.name)}</b> — this property's saved checklist</div>`
      : '';

    if (!this.suggestOpen) {
      return `
        <div class="cap-card card-elev">
          <div class="sg-bar">
            <div>
              <div class="cap-section-h" style="margin:0;">Adaptive checklist</div>
              <div class="cap-hint" style="margin-top:4px;">Tailor the items to this property from its details on file.</div>
            </div>
            <button class="insp-btn primary" id="sg-open">Suggest checklist for this property</button>
          </div>
          ${savedLine ? `<div style="margin-top:10px;">${savedLine}</div>` : ''}
        </div>`;
    }

    let inner: string;
    if (this.suggestLoading) {
      inner = `<div style="padding:14px 0; text-align:center;">${LoadingSpinner.render()}</div>`;
    } else if (!this.suggest) {
      inner = `<div class="cap-msg err">Couldn’t load a suggestion — GET /suggest-template failed. Try again.</div>`;
    } else {
      const s = this.suggest;
      const items: any[] = s.items || [];
      const base: number = s.base_count || 0;
      // Mark which items came from a signal (i.e. beyond the standard base set):
      // any item whose title appears in a signals_used.added list is "new".
      const addedTitles = new Set<string>();
      for (const sig of (s.signals_used || [])) for (const t of (sig.added || [])) addedTitles.add(t);

      const chips = (s.signals_used || []).map((sig: any) => {
        const n = (sig.added || []).length;
        return `<span class="sg-chip add">+ ${this.esc(sig.signal)}${n ? ` · ${n} item${n === 1 ? '' : 's'}` : ''}</span>`;
      }).join('');
      const baseChip = `<span class="sg-chip">${base} standard checklist item${base === 1 ? '' : 's'}</span>`;

      const byArea: Record<string, any[]> = {};
      for (const it of items) (byArea[it.area || 'General'] = byArea[it.area || 'General'] || []).push(it);
      const itemsHtml = Object.keys(byArea).map(area => `
        <div class="sg-area-h">${this.esc(area)}</div>
        ${byArea[area].map(it => {
          const sc = this.sourceChip(it.source);
          const mustDo = it.required ? '<span class="sg-must" title="Must-do — required check">MUST-DO</span>' : '';
          const photoReq = it.photo_required ? '<span class="sg-photo" title="A photo is required for this item">📷 photo</span>' : '';
          return `
          <div class="sg-item">
            ${addedTitles.has(it.title) ? '<span class="new">new</span>' : ''}
            <span style="flex:1;">${this.esc(it.title)}</span>
            ${mustDo}${photoReq}
            <span class="sg-src ${sc.cls}" title="${this.esc(sc.title)}">${this.esc(sc.label)}</span>
          </div>`;
        }).join('')}
      `).join('');

      inner = `
        <div class="sg-note">${this.esc(s.note || '')}</div>
        <div class="sg-chips">${baseChip}${chips}</div>
        <div class="cap-hint">${items.length} item${items.length === 1 ? '' : 's'} total · areas: ${Object.keys(byArea).map(a => this.esc(a)).join(', ')}</div>
        <div style="margin-top:6px;">${itemsHtml}</div>
        <div class="sg-additem">
          <input id="sg-add-title" placeholder="add a custom check — e.g. Pool key + remote on entryway table">
          <input id="sg-add-area" placeholder="area" style="width:110px; flex:none;">
          <label class="sg-add-flag"><input type="checkbox" id="sg-add-req"> must-do</label>
          <label class="sg-add-flag"><input type="checkbox" id="sg-add-photo"> 📷</label>
          <button class="insp-btn" id="sg-add-btn">+ Add</button>
        </div>
        <div class="cap-hint">Add property-specific checks here, then “Save as this property’s default” to keep them. (Whole-property walkthrough video now lives on the Property Details sheet → Onboarding.)</div>
        <div class="sg-actions">
          <button class="insp-btn primary" id="sg-use">Use for this inspection</button>
          <button class="insp-btn" id="sg-save">Save as this property's default</button>
          <button class="insp-btn" id="sg-cancel">Cancel</button>
        </div>
        <div class="cap-hint" style="margin-top:8px;">“Use for this inspection” runs these items once without saving anything. “Save as this property’s default” keeps them as this property’s checklist, so future inspections preselect it.</div>`;
    }

    return `
      <div class="cap-card card-elev">
        <div class="sg-bar">
          <div class="cap-section-h" style="margin:0;">Suggested checklist for this property</div>
          <button class="cap-back" id="sg-close" style="padding:0;">close ✕</button>
        </div>
        ${savedLine ? `<div style="margin-top:8px;">${savedLine}</div>` : ''}
        <div style="margin-top:8px;">${inner}</div>
      </div>`;
  }

  // Map an item's provenance `source` → a short labelled, colour-coded chip so
  // the manager can SEE where each suggested item came from (the checklist gets
  // visibly smarter as more sources contribute).
  private sourceChip(source: string): { cls: string; label: string; title: string } {
    switch (source) {
      case 'hostaway': return { cls: 'hostaway', label: 'Hostaway', title: 'From listing facts (bed/bath/property type)' };
      case 'pds-appliance': return { cls: 'pds', label: 'PDS', title: 'From a PDS appliance on file' };
      case 'pds-amenity': return { cls: 'pds', label: 'PDS', title: 'From a PDS amenity / fact scan' };
      case 'baseline-video': return { cls: 'baseline', label: 'Baseline', title: 'From the baseline walkthrough video' };
      case 'recurring-issue': return { cls: 'recurring', label: 'Recurring', title: 'Recurring issue from inspection history' };
      case 'quirk': return { cls: 'quirk', label: 'Quirk', title: 'From a Property Details quirk / must-know note' };
      case 'manual': return { cls: 'manual', label: 'Custom', title: 'Manually added for this property' };
      case 'standard':
      default: return { cls: 'standard', label: 'Standard', title: 'From the standard checklist' };
    }
  }

  // "Add a baseline walkthrough": a video file input that extracts frames on the
  // device and POSTs them to /inspections/baseline/:listing_id; on success we
  // re-fetch the suggestion so the new baseline-video items appear inline.
  private renderBaselineAffordance(): string {
    const msg = this.baselineMsg
      ? `<div class="sg-baseline-msg ${this.baselineMsg.kind}">${this.esc(this.baselineMsg.text)}</div>`
      : '';
    const body = this.baselineProcessing
      ? `<div class="sg-baseline-msg warn">Processing baseline walkthrough — extracting frames &amp; analyzing…</div>`
      : `<label class="lbl">⬆ Add a baseline walkthrough
           <input type="file" accept="video/*" id="sg-baseline-video" hidden>
         </label>
         <div class="cap-hint" style="margin-top:4px;">Upload a one-time walkthrough video; the AI extracts property-specific checks generic lists miss. Frames are extracted on your device.</div>`;
    return `<div class="sg-baseline">${body}${msg}</div>`;
  }

  // Extract frames from the chosen video on-device, POST them (multipart) to the
  // baseline endpoint, then re-fetch the suggestion (now baseline-aware). Handles
  // the graceful configured:false path.
  private async handleBaselineUpload(insp: any, file: File): Promise<void> {
    this.baselineProcessing = true;
    this.baselineMsg = null;
    this.draw();
    try {
      // Cap at 4 frames: this batch POSTs all frames in ONE request, and the
      // shared multer instance allows max 5 files/request; callClaude analyses at
      // most 4 images anyway, so 4 is the no-waste max (avoids a 500 + dead storage).
      const frames = (await this.extractFrames(file, 4)).slice(0, 4);
      const fd = new FormData();
      for (let i = 0; i < frames.length; i++) fd.append('photo', frames[i], `baseline_${i + 1}.jpg`);
      const r = await fetch(`${API}/inspections/baseline/${encodeURIComponent(insp.listing_id)}`, { method: 'POST', body: fd });
      const j = await r.json().catch(() => ({} as any));
      this.baselineProcessing = false;
      if (j && j.configured === false) {
        this.baselineMsg = { text: j.message || 'AI not configured — baseline analysis is unavailable on this server.', kind: 'warn' };
        this.draw();
        return;
      }
      if (!r.ok || !j || j.ok !== true) {
        this.baselineMsg = { text: (j && j.message) || (j && j.error) || 'Baseline analysis failed — try again.', kind: 'err' };
        this.draw();
        return;
      }
      const n = (j.baseline && Array.isArray(j.baseline.items)) ? j.baseline.items.length : 0;
      this.baselineMsg = { text: `Baseline analyzed — added ${n} property-specific item${n === 1 ? '' : 's'}.`, kind: 'ok' };
      // Re-fetch the suggestion so the new baseline-video items appear.
      await this.loadSuggest(insp);
    } catch (e: any) {
      this.baselineProcessing = false;
      this.baselineMsg = { text: `Couldn't process that video: ${e?.message || e}.`, kind: 'err' };
      this.draw();
    }
  }

  private async loadSuggest(insp: any): Promise<void> {
    this.suggestLoading = true;
    this.suggest = null;
    this.draw();
    const j = await this.get(`/inspections/suggest-template/${encodeURIComponent(insp.listing_id)}`);
    this.suggest = j;
    this.suggestLoading = false;
    this.draw();
  }

  // The server materializes inspection items only by copying a template (the
  // create + update endpoints don't accept raw item arrays / new keys). So to
  // put the suggested set onto an inspection we round-trip through a template
  // and create a fresh inspection from it (status na), then switch the capture
  // view to that inspection. The just-emptied original in-progress inspection
  // is left untouched (there's no delete endpoint) — it never got captured.
  private async createInspectionFromTemplate(insp: any, templateId: string): Promise<void> {
    const j = await this.post('/inspections', {
      listing_id: insp.listing_id,
      listing_name: insp.listing_name,
      inspector: insp.inspector || '',
      mode: insp.mode || 'video',
      template_id: templateId,
      // the replacement keeps the structured form + audit standing of the
      // inspection it supersedes — switching checklists must not drop them
      form_type: insp.form_type || null,
      inspection_type: insp.inspection_type || 'condition',
    });
    if (j?.inspection) {
      this.replaceInsp(j.inspection);
      await this.reloadInspections();
      this.activeId = j.inspection.id;
      this.view = 'capture';
      this.captureStep = 1;   // stepwise basic flow always starts at Issues
      this.suggestOpen = false;
      this.suggest = null;
      this.draw();
    }
  }

  // "Use for this inspection": save the suggested set as a property template and
  // "Use for this inspection": one-off — start a fresh inspection straight from
  // these items with NO template saved and the property's default unchanged
  // (server accepts a raw items[] body on create).
  private async useSuggestForInspection(insp: any): Promise<void> {
    const items: any[] = (this.suggest?.items) || [];
    if (!items.length) { alert('No suggested items to use.'); return; }
    await this.createInspectionFromItems(insp, items);
  }

  // Create an inspection directly from a raw item set (adhoc; no saved template).
  private async createInspectionFromItems(insp: any, items: any[]): Promise<void> {
    const j = await this.post('/inspections', {
      listing_id: insp.listing_id,
      listing_name: insp.listing_name,
      inspector: insp.inspector || '',
      mode: insp.mode || 'video',
      items,
      form_type: insp.form_type || null,
      inspection_type: insp.inspection_type || 'condition',
    });
    if (j?.inspection) {
      this.replaceInsp(j.inspection);
      await this.reloadInspections();
      this.activeId = j.inspection.id;
      this.view = 'capture';
      this.captureStep = 1;   // stepwise basic flow always starts at Issues
      this.suggestOpen = false;
      this.suggest = null;
      this.draw();
    }
  }

  // "Save as this property's default": POST /property-template (creates the
  // template + sets ao_property_templates[listing_id]), then start a fresh
  // inspection from it; this + future inspections preselect it.
  private async saveSuggestAsDefault(insp: any): Promise<void> {
    const items: any[] = (this.suggest?.items) || [];
    if (!items.length) { alert('No suggested items to save.'); return; }
    const name = `Adaptive — ${this.suggest?.name || insp.listing_name}`;
    const j = await this.post(`/inspections/property-template/${encodeURIComponent(insp.listing_id)}`, { items, name });
    if (!j?.ok || !j.template) return;
    this.upsertTemplate(j.template);
    await this.createInspectionFromTemplate(insp, j.template.id);
  }

  // Reflect a newly-saved template in the client list so the "saved checklist"
  // banner shows immediately and savedPropTemplate() can find it.
  private upsertTemplate(template: any): void {
    const idx = this.templates.findIndex(t => t.id === template.id);
    if (idx >= 0) this.templates[idx] = template; else this.templates.push(template);
  }

  private renderRecordPane(insp: any): string {
    const recording = !!this.recorder && this.recorder.state === 'recording';
    const haveVideo = !!insp?.capture?.video_url || !!this.recordedBlob;
    return `
      <div class="cap-section-h">Record walkthrough</div>
      <video class="cap-video" id="cap-video" autoplay playsinline muted></video>
      <button class="cap-big-btn ${recording ? 'stop' : 'rec'}" id="cap-rec-toggle">
        ${recording ? '<span class="cap-recdot"></span> Stop &amp; process' : '● Start recording'}
      </button>
      <div class="cap-hint">${recording
        ? 'Walk room to room and narrate. On stop, ~8–10 frames are extracted and uploaded with the video.'
        : haveVideo ? 'Walkthrough captured. Record again to replace, or Analyze below.' : 'Tap to start; narrate as you walk. Frames are extracted on your device — nothing leaves until you stop.'}</div>
      ${this.uploadVideoControl()}`;
  }

  // Upload a pre-recorded video (e.g. one shot on a phone, or a Director/COO's
  // own walkthrough) — runs the SAME on-device frame-extraction + analyze
  // pipeline as a live recording, so it works even where recording can't.
  private uploadVideoControl(): string {
    return `
      <div class="cap-upload-row">
        <label class="cap-upload-btn">⬆ Upload a video file
          <input type="file" accept="video/*" id="cap-upload-video" hidden>
        </label>
        <span class="cap-upload-hint">Already shot a walkthrough? Upload it — frames are extracted on your device.</span>
      </div>`;
  }

  // R80 #2: the strip's rooms are the per-home SLOTS (Bedroom 1/2/3… from the
  // listing's real counts, admin-labeled, each with a reference photo so the
  // inspector can tell which room is which). Slot labels tag every frame, so
  // Bedroom 2's photos are always the same physical room. Falls back to the
  // template areas until the brief lands / for a server without slots.
  private photoRoomSlots(): Array<{ key: string; label: string; kind: string; ref_photo: string | null; ref_photos?: string[] }> | null {
    const pr = this.propBrief && this.propBrief.photo_rooms;
    return (pr && Array.isArray(pr.rooms) && pr.rooms.length) ? pr.rooms : null;
  }
  private renderPhotoPane(insp: any, areas: string[]): string {
    const U = InspectionsPage.UNSORTED;
    const tagged = this.photoTags; // { area: count } — client-side strip bookkeeping
    const slots = this.photoRoomSlots();
    const base: string[] = slots ? slots.map(r => r.label) : (areas.length ? areas : ['General']);
    // R113 #2: a room carries ref_photos[] (cover first) — the strip shows the
    // cover and a "+N" that opens the shared viewer over all of them.
    const refBySlot = new Map<string, { key: string; ref: string | null; refs: string[] }>((slots || []).map(r => [r.label, { key: r.key, ref: r.ref_photo, refs: (Array.isArray(r.ref_photos) && r.ref_photos.length) ? r.ref_photos : (r.ref_photo ? [r.ref_photo] : []) }]));
    const extras = Object.keys(this.photoThumbs)
      .filter(a => a !== U && !base.includes(a) && (this.photoThumbs[a] || []).length > 0);
    // R114: a room's photos still on THIS phone (saved, not yet on the server)
    const pend = pendingByArea(insp.id);
    const lateMap: Record<string, any> = (insp.capture && insp.capture.late) || {};
    const rooms = [...base, ...extras].map(area => {
      const thumbs = (this.photoThumbs[area] || []);
      const waiting = pend[area] || 0;
      const lit = thumbs.length > 0 || (tagged[area] || 0) > 0 || waiting > 0;
      const slot = refBySlot.get(area);
      const refCell = slot
        ? (slot.ref
          ? `<span style="position:relative; flex:none; display:inline-block;"><img src="${this.esc(slot.ref)}" alt="reference" data-refb-room="${this.esc(slot.key)}" title="Reference — which room this is${slot.refs.length > 1 ? ` (${slot.refs.length} photos — tap to view all)` : ' (tap to zoom)'}" style="width:44px; height:44px; object-fit:cover; border-radius:6px; border:2px solid var(--accent,#2563eb); cursor:pointer; display:block;">${slot.refs.length > 1 ? `<button type="button" data-refb-room="${this.esc(slot.key)}" title="View all ${slot.refs.length} reference photos" style="position:absolute; left:-4px; bottom:-6px; font-size:9px; font-weight:700; padding:0 5px; border-radius:999px; border:1px solid #fff; background:var(--accent,#2563eb); color:#fff; cursor:pointer; line-height:1.5;">+${slot.refs.length - 1}</button>` : ''}</span>`
          : `<label title="No reference photo yet — snap one so everyone knows which room this is" style="width:44px; height:44px; border:1.5px dashed var(--border,#e2e8f0); border-radius:6px; display:flex; align-items:center; justify-content:center; font-size:9px; color:var(--text-muted); flex:none; cursor:pointer; text-align:center; line-height:1.2;">＋ref<input type="file" accept="image/*" capture="environment" data-cap-refphoto="${this.esc(slot.key)}" hidden></label>`)
        : '';
      return `
        <div class="cap-room ${lit ? 'lit' : ''}">
          ${refCell}
          <div class="cap-room-info">
            <div class="cap-room-area">${this.esc(area)}</div>
            <div class="cap-room-ttl">${lit ? `${thumbs.length} photo${thumbs.length === 1 ? '' : 's'}${waiting ? ` · <span class="cap-pending">⏳ ${waiting} uploading</span>` : ''}` : 'No photo yet'}</div>
            ${thumbs.length ? `<div class="cap-room-thumbs">${thumbs.map(u => `<img src="${this.esc(u)}" alt=""${lateMap[u] ? ' class="late" title="Added after completion"' : ''}>`).join('')}</div>` : ''}
          </div>
          <div class="cap-add">
            <label>+ Photo<input type="file" accept="image/*" capture="environment" data-cap-photo="${this.esc(area)}"></label>
          </div>
        </div>`;
    }).join('');
    // Every frame that has no room yet — legacy inspections (no frame_meta) and
    // frames the server filed under 'Unsorted'. Tap a thumb to assign its room
    // (same client-side photoTags/photoThumbs bookkeeping as the per-room taps).
    const unThumbs = this.photoThumbs[U] || [];
    const unN = Math.max(unThumbs.length, tagged[U] || 0);
    const unsortedCard = unN ? `
        <div class="cap-room lit" style="border-style:dashed;">
          <div class="cap-room-info">
            <div class="cap-room-area">📥 Unsorted (${unN})</div>
            <div class="cap-room-ttl">Uploaded but not sorted into a room — tap a photo to assign it.</div>
            ${unThumbs.length ? `<div class="cap-room-thumbs">${unThumbs.map(u => `<img src="${this.esc(u)}" alt="" data-strip-move="${this.esc(u)}" title="Tap to assign a room" style="cursor:pointer;">`).join('')}</div>` : ''}
          </div>
        </div>` : '';
    // Reconciliation — frameCount() is the truth for what's uploaded; the strip
    // must account for every one of those photos, sorted or not.
    const total = this.frameCount(insp);
    const un = Math.min(total, unN);
    const recon = total
      ? `<div class="cap-hint" style="margin:0 0 8px; font-weight:600;">${total} photo${total === 1 ? '' : 's'} uploaded · ${total - un} sorted into rooms · ${un} unsorted</div>`
      : '';
    return `
      <div class="cap-section-h">Photo mode — guided by room</div>
      <div class="cap-bulk">
        <label class="cap-bulk-btn">📷 Upload all photos<input type="file" accept="image/*" multiple id="cap-bulk-input"></label>
        <div class="cap-hint" style="margin-top:6px;">Pick a batch of photos at once — they upload as walkthrough frames and the AI auto-sorts each into its room. Or use the per-room <b>+ Photo</b> buttons below.</div>
        <div id="cap-bulk-msg"></div>
      </div>
      ${recon}
      <div class="cap-rooms">${rooms}${unsortedCard}</div>
      <div class="cap-hint">Snap each room; photos upload as walkthrough frames the AI grades against the template.</div>
      ${this.uploadVideoControl()}`;
  }

  // client-side bookkeeping so the room strip lights without re-fetching
  private photoThumbs: Record<string, string[]> = {};
  private photoTags: Record<string, number> = {};

  private bindCapture(insp: any): void {
    this.bindFormCard(insp);
    const root = this.container;
    // R64: reference panel — tap a photo for full size; remember open state
    // across the capture view's frequent redraws. Bound HERE (not bind()) —
    // the capture surface never runs bind() (review: listeners were dead and
    // a collapsed panel popped back open on every redraw).
    root.querySelectorAll<HTMLElement>('[data-refb-photo]').forEach(el =>
      el.addEventListener('click', () => openExternal(el.dataset.refbPhoto!)));
    // R113 #2: the strip's room reference (cover / "+N") → the shared viewer over every reference photo of that room.
    root.querySelectorAll<HTMLElement>('[data-refb-room]').forEach(el =>
      el.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        const key = el.dataset.refbRoom!;
        const room = (this.photoRoomSlots() || []).find(r => r.key === key);
        if (!room) return;
        const urls: string[] = (Array.isArray(room.ref_photos) && room.ref_photos.length) ? room.ref_photos : (room.ref_photo ? [room.ref_photo] : []);
        if (urls.length) openLightbox(urls, 0, { caption: (i) => `${room.label} — reference ${i + 1} of ${urls.length}${i === 0 ? ' (cover)' : ''}` });
      }));
    const refPanel = root.querySelector('#cap-refpanel') as HTMLDetailsElement | null;
    refPanel?.addEventListener('toggle', () => { this.refPanelOpen = refPanel.open; });
    // R79 #3: inventory count — bound HERE, not bind() (the R64 capture-view
    // lesson). Qty saves serialize; completion stamps the property.
    const invPanel = root.querySelector('#cap-invpanel') as HTMLDetailsElement | null;
    invPanel?.addEventListener('toggle', () => { this.invPanelOpen = invPanel.open; });
    root.querySelectorAll<HTMLInputElement>('[data-invb-qty]').forEach(el => el.addEventListener('change', (e) => {
      const item = el.dataset.invbQty!;
      const raw = (e.currentTarget as HTMLInputElement).value.trim();
      const qty = raw === '' ? null : Number(raw);
      if (qty != null && !(qty >= 0 && qty <= 9999)) { alert('Quantity must be 0–9999.'); return; }
      const inv = this.propBrief && this.propBrief.inventory;
      const row = inv?.items?.find((x: any) => x.item === item);
      const prev = row ? row.qty : null;
      if (row) row.qty = qty;
      this.invChain = this.invChain.then(async () => {
        try {
          const r = await fetch('/api/ob/inventory-count', {
            method: 'POST', headers: { 'content-type': 'application/json' },
            // R104 #6: the inspection context lets the server tell a staging miss from a stock-out
            body: JSON.stringify({ listing_id: String(insp.listing_id), item, qty, by: insp.inspector || 'inspector', inspection_id: insp.id }),
          });
          if (!r.ok) { const j = await r.json().catch(() => ({} as any)); throw new Error(j.error || `HTTP ${r.status}`); }
          const jOk2 = await r.json().catch(() => ({} as any));
          if (jOk2.auto_request) alert(`📦 Below the MIN — a supply request was created automatically:\n${jOk2.auto_request.note || ''}`);   // R87 #1.8
          else if (jOk2.cleaning_miss) {   // R104 #6
            const cm = jOk2.cleaning_miss;
            alert(`🧹 Below the MIN right after a clean — logged as a CLEANING MISS on this inspection (${cm.counted} of ${cm.expected != null ? cm.expected + ' expected, ' : ''}min ${cm.min}), not ordered.\n\nRoute it to the cleaner from the report card. If the closet is actually empty, mark "closet empty" there to order.`);
            // review R104 #25: the capture closures hold THIS object — merge the new issue onto it, never replace the list
            try { const j3 = await this.get('/inspections'); const fresh = (j3?.inspections || []).find((i: any) => i.id === insp.id); if (fresh && Array.isArray(fresh.issues)) insp.issues = fresh.issues; } catch (_) { /* the report card refetches anyway */ }
          }
        } catch (err: any) {
          alert(`Count didn't save — ${err.message}`);
          if (row) row.qty = prev;
          const inp = root.querySelector(`[data-invb-qty="${CSS.escape(item)}"]`) as HTMLInputElement | null;
          if (inp) inp.value = prev == null ? '' : String(prev);
        }
      });
    }));
    root.querySelector('[data-invb-done]')?.addEventListener('click', async () => {
      await this.invChain;
      try {
        const r = await fetch('/api/ob/inventory-count-complete', {
          method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ listing_id: String(insp.listing_id), by: insp.inspector || 'inspector' }),
        });
        const j = await r.json().catch(() => ({} as any));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        const inv = this.propBrief && this.propBrief.inventory;
        if (inv) { inv.counted_at = j.counted_at; inv.counted_by = j.by; inv.due = false; }
        this.draw();
      } catch (err: any) { alert(`Couldn't complete the count — ${err.message}`); }
    });
    root.querySelector('[data-cap-back]')?.addEventListener('click', () => { this.exitCapture(); this.draw(); });
    root.querySelector('[data-cap-logissue]')?.addEventListener('click', () => {
      QuickLogIssue.open({
        sourceContext: 'inspection', listingId: String(insp.listing_id), listingName: insp.listing_name,
        inspectionId: insp.id, createdBy: insp.inspector || '',
        // R26: refresh so tickets added via the modal appear on this view too
        onLogged: async (n) => { if (n) { try { await this.reloadInspections(); } catch (_) { /* stale view ok */ } this.draw(); } },
      });
    });
    root.querySelector('[data-insp-manual]')?.addEventListener('click', () => { this.openId = insp.id; this.renderDetailModal(); });

    // mode toggle
    root.querySelectorAll<HTMLElement>('[data-cap-mode]').forEach(el =>
      el.addEventListener('click', () => {
        const next = el.dataset.capMode as 'record' | 'photo';
        if (next === this.captureMode) return;
        // leaving record mode tears down any preview stream
        if (next === 'photo') { this.stopRecognition(); }
        this.captureMode = next;
        // draw() → bindCapture() starts the preview itself — a second call here
        // would double-fire getUserMedia and leak a live camera+mic stream.
        this.draw();
      }));

    // keep typed transcript edits
    const ta = root.querySelector('#cap-transcript') as HTMLTextAreaElement | null;
    ta?.addEventListener('input', () => { this.transcriptFinal = ta.value; });

    // R25 stepwise flow — step navigation + the Step-1 issues board.
    root.querySelectorAll<HTMLElement>('[data-cap-step]').forEach(el => el.addEventListener('click', async () => {
      const s = Number(el.dataset.capStep) === 2 ? 2 : 1;
      if (s === this.captureStep) return;
      // Leaving step 1 with the camera rolling would strand a headless recorder
      // (step 2 has no record pane/stop button) and submit would grade without
      // its frames — finish the recording first, THEN switch.
      if (s === 2 && this.recorder && this.recorder.state === 'recording') void this.stopRecording();
      // R26: moving to scoring — the live embed's in-memory list is the
      // authority (it already knows every resolved create, no refetch race).
      if (s === 2 && insp.form_type === 'basic') {
        if (this.qliInstance && this.qliFor === insp.id) {
          insp.logged_issues = this.qliInstance.getLogged();
          this.activeLoggedCount = insp.logged_issues.length;
        } else {
          try {
            const j = await fetch(`${API}/issues/log?inspection_id=${encodeURIComponent(insp.id)}`).then(r => r.ok ? r.json() : null);
            if (j && Array.isArray(j.issues)) { insp.logged_issues = j.issues; this.activeLoggedCount = j.issues.length; }
          } catch (_) { /* recap shows the last known counts */ }
        }
      }
      this.captureStep = s;
      this.stepChangedAt = Date.now();   // R37: shield the freshly-drawn Submit button
      this.draw();
    }));

    // R41: stocking & safety check — collapse toggle + per-item persistence.
    root.querySelector('[data-stock-toggle]')?.addEventListener('click', () => { this.stockOpen = !this.stockOpen; this.draw(); });
    root.querySelectorAll<HTMLInputElement>('[data-stock-item]').forEach(el => el.addEventListener('change', () => {
      const k = el.dataset.stockItem!;
      if (!insp.form) insp.form = {};
      if (!insp.form.checks) insp.form.checks = {};
      insp.form.checks[k] = el.checked;   // optimistic — the header count redraws live
      void this.saveForm(insp, { checks: { [k]: el.checked } });
      this.draw();
    }));

    // R26: Save & exit — tickets and form taps already persist; a LIVE
    // recording is the one thing that doesn't, so finish it first (it uploads
    // its frames + transcript), then tear down and land on the queue.
    root.querySelectorAll<HTMLElement>('[data-cap-saveexit]').forEach(el => el.addEventListener('click', async () => {
      if (this.saveExitBusy) return;
      this.saveExitBusy = true;
      const leavingId = this.activeId;
      const seq = this.viewSeq;   // R114 fix2: this visit to the capture screen
      try {
        if (this.recorder && this.recorder.state === 'recording') {
          try { await this.stopRecording(); } catch (_) { /* frames beat losing everything */ }
        }
        // R114 fix3 (D3): they left this screen WHILE the frames were being
        // extracted (no wait existed yet for the view change to settle). A
        // stale handler must never start its video wait on whatever screen is
        // showing now — a re-opened capture screen would get a "Leave now"
        // that can't leave and a Save & exit swallowed behind it. The video
        // keeps uploading; the re-opened screen's own Save & exit waits for it.
        if (this.viewSeq !== seq) return;
        // R114 fix: frames are safe on the phone; the walkthrough video is not —
        // finish it before leaving (unless the inspector taps "Leave now")
        if (this.videoUploadP) await this.waitForVideoOrLeave(this.videoUploadP);
        // they already left another way — even if they have since come back to
        // this inspection (a new visit: never eject them from it, never drop
        // a new recording or a half-typed note)
        if (this.viewSeq !== seq || this.view !== 'capture' || this.activeId !== leavingId) return;
        this.exitCapture();
        this.tab = 'queue';
        try { await this.reloadInspections(); } catch (_) { /* queue still renders */ }
        this.draw();
      } finally { this.saveExitBusy = false; }
    }));

    // R26: embedded quick-submit logger IS the Step-1 capture surface.
    // The instance persists across page redraws (reattach), so half-typed
    // notes, attached photos, live dictation, and upload badges all survive.
    const qliHost = root.querySelector<HTMLElement>('#iss-qli-host');
    if (qliHost) {
      if (this.qliInstance && this.qliFor === insp.id) {
        this.qliInstance.reattach(qliHost);
      } else {
        this.qliInstance?.destroy();
        this.qliFor = insp.id;
        this.qliInstance = QuickLogIssue.embed(qliHost, {
          sourceContext: 'inspection',
          listingId: String(insp.listing_id),
          listingName: insp.listing_name,
          inspectionId: insp.id,
          createdBy: insp.inspector || '',
          preload: true,
          onCount: (n) => {
            // Liveness guard: a slow response from a replaced embed (or another
            // inspection) must never write into the current capture's count.
            if (this.activeId !== insp.id) return;
            // DOM-update only — a full draw() here would remount the embed and
            // re-trigger preload in a loop.
            this.activeLoggedCount = n;
            this.activeLoggedFor = insp.id;
            const legacyN = (insp.issues || []).filter((i: any) => i.source === 'inspector').length;
            const total = n + legacyN;
            const b = this.container.querySelector('.cap-stepbar [data-cap-step="1"]');
            if (b) b.textContent = `Step 1 · Issues${total ? ` (${total})` : ''}`;
          },
        });
      }
    }
    // R35 #10: what the cleaner already documented on the day's clean.
    void this.loadCleanContext(insp.id, root);
    const addIssue = async () => {
      const note = (root.querySelector('#iss-new-note') as HTMLInputElement | null)?.value.trim() || '';
      if (!note) return;
      const type = (root.querySelector('#iss-new-type') as HTMLSelectElement | null)?.value || 'maintenance';
      const severity = (root.querySelector('#iss-new-sev') as HTMLSelectElement | null)?.value || 'med';
      const j = await this.post(`/inspections/${insp.id}/issues`, { note, type, severity });
      if (j && j.issues) { this.adoptIssues(insp, j.issues); this.draw(); }
    };
    root.querySelector('[data-iss-add]')?.addEventListener('click', () => void addIssue());
    (root.querySelector('#iss-new-note') as HTMLInputElement | null)?.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); void addIssue(); }
    });
    root.querySelectorAll<HTMLElement>('[data-iss-fixed]').forEach(el => el.addEventListener('click', async () => {
      const [issueId, to] = el.dataset.issFixed!.split('|');
      const j = await this.post(`/inspections/${insp.id}/issue-update`, { issue_id: issueId, fixed: to === '1' });
      if (j && j.issues) { this.adoptIssues(insp, j.issues); this.draw(); }
    }));
    root.querySelectorAll<HTMLSelectElement>('[data-iss-fu]').forEach(el => el.addEventListener('change', async () => {
      const j = await this.post(`/inspections/${insp.id}/issue-update`, { issue_id: el.dataset.issFu, follow_up: el.value || null });
      if (j && j.issues) { this.adoptIssues(insp, j.issues); this.draw(); }
    }));

    // Feature B — tick a required-this-visit cadence item as addressed. We track
    // the ticked set locally (no draw, so the textarea/recorder aren't disrupted);
    // it's submitted on "Complete inspection" → POST /:id/complete.
    root.querySelectorAll<HTMLInputElement>('[data-foc-tick]').forEach(inp =>
      inp.addEventListener('change', () => {
        const id = inp.dataset.focTick!;
        if (inp.checked) this.focusDone.add(id); else this.focusDone.delete(id);
        inp.closest('.foc-row')?.classList.toggle('on', inp.checked);
      }));
    // N/A / skip toggle (mutually exclusive with done) — re-render the panel.
    root.querySelectorAll<HTMLElement>('[data-foc-skip]').forEach(btn =>
      btn.addEventListener('click', () => {
        const id = btn.dataset.focSkip!;
        if (this.focusSkip.has(id)) this.focusSkip.delete(id);
        else { this.focusSkip.add(id); this.focusDone.delete(id); }
        this.draw();
      }));
    // R58: act on a focus item's linked WO right from the card.
    root.querySelectorAll<HTMLElement>('[data-foc-photo]').forEach(img =>
      img.addEventListener('click', () => openExternal(img.dataset.focPhoto!, '_blank')));
    root.querySelectorAll<HTMLButtonElement>('[data-foc-wodone]').forEach(btn =>
      btn.addEventListener('click', () => void this.focusWoAction(btn.dataset.focWodone!, 'wo-done', btn)));
    root.querySelectorAll<HTMLButtonElement>('[data-foc-assignmn]').forEach(btn =>
      btn.addEventListener('click', () => void this.focusWoAction(btn.dataset.focAssignmn!, 'assign-maintenance', btn)));

    // record toggle
    root.querySelector('#cap-rec-toggle')?.addEventListener('click', () => {
      if (this.recorder && this.recorder.state === 'recording') this.stopRecording();
      else this.startRecording();
    });

    // photo inputs
    root.querySelectorAll<HTMLInputElement>('[data-cap-photo]').forEach(inp =>
      inp.addEventListener('change', () => {
        const area = inp.dataset.capPhoto || 'General';
        const file = inp.files?.[0];
        // R114: ids taken NOW (never this.activeId after an await); persisted first
        if (file) void this.captureFrame(String(insp.id), insp.listing_id != null ? String(insp.listing_id) : null, area, file);
        inp.value = '';   // re-arm (a same-file re-pick must fire change)
      }));
    // R80: fill an EMPTY room slot's reference photo from the field (replacing
    // an existing reference stays office-only — server enforces it).
    root.querySelectorAll<HTMLInputElement>('[data-cap-refphoto]').forEach(inp =>
      inp.addEventListener('change', async () => {
        const key = inp.dataset.capRefphoto!;
        const file = inp.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append('listing_id', String(insp.listing_id));
        fd.append('key', key);
        fd.append('by', insp.inspector || 'inspector');
        fd.append('photo', file);
        try {
          const r = await fetch(`${API}/inspections/photo-rooms/ref-photo`, { method: 'POST', body: fd });
          const j = await r.json().catch(() => ({} as any));
          if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
          if (this.propBrief && this.propBrief.photo_rooms) this.propBrief.photo_rooms.rooms = j.rooms;
          this.draw();
        } catch (e: any) { alert(`Reference photo didn't save — ${e.message}`); inp.value = ''; }   // re-arm — a same-file re-pick must fire change (R67 class)
      }));

    // Unsorted strip: tap a frame → assign its room (the same client-side
    // photoTags/photoThumbs bookkeeping the per-room tags use).
    root.querySelectorAll<HTMLElement>('[data-strip-move]').forEach(el =>
      el.addEventListener('click', () => {
        const url = el.dataset.stripMove!;
        const slots = this.photoRoomSlots();
        const tpl = this.templates.find(t => t.id === insp.template_id);
        const tplAreas: string[] = tpl ? [...new Set((tpl.items || []).map((it: any) => String(it.area || 'General')))] as string[] : [];
        const opts = slots ? slots.map(r => r.label) : (tplAreas.length ? tplAreas : ['General']);
        const pick = window.prompt(`Assign this photo to a room:\n${opts.map((a, i) => `${i + 1}. ${a}`).join('\n')}\n\nType the number or the room name:`, '');
        if (pick == null || !pick.trim()) return;
        const n = Number(pick.trim());
        const area = (Number.isFinite(n) && n >= 1 && n <= opts.length)
          ? opts[n - 1]
          : opts.find(a => a.toLowerCase() === pick.trim().toLowerCase());
        if (!area) { alert('No matching room — type one of the listed numbers or names.'); return; }
        const U = InspectionsPage.UNSORTED;
        this.photoThumbs[U] = (this.photoThumbs[U] || []).filter(u => u !== url);
        this.photoTags[U] = Math.max(0, (this.photoTags[U] || 1) - 1);
        (this.photoThumbs[area] = this.photoThumbs[area] || []).push(url);
        this.photoTags[area] = (this.photoTags[area] || 0) + 1;
        this.draw();
        // PERSIST the manual sort (review fix: session-only bookkeeping meant
        // every hand assignment silently reverted to Unsorted on the next
        // reload/bulk-upload). src:'manual' server-side survives AI re-runs.
        void fetch(`${API}/inspections/${insp.id}/classify`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ manual: [{ url, area }] }),
        }).then(r => r.ok ? r.json() : null).then(j => { if (j && j.inspection) this.replaceInsp(j.inspection); }).catch(() => { /* local state stands; next reload re-syncs */ });
      }));

    // bulk photo upload → many frames in one pick, then AI auto-sort to rooms
    root.querySelector<HTMLInputElement>('#cap-bulk-input')?.addEventListener('change', (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length) this.bulkUpload(insp, files);
    });

    // analyze — the R37 shield ignores a tap that lands within a beat of the
    // step switch (it was aimed at "Continue to scoring", not Submit).
    root.querySelector('#cap-analyze')?.addEventListener('click', () => {
      if (Date.now() - this.stepChangedAt < 700) return;
      void this.runAnalyze(insp);
    });

    // sync offline-saved frames
    root.querySelector('#cap-sync')?.addEventListener('click', () => void this.syncPending());

    // upload a pre-recorded video → same frame-extraction + analyze pipeline
    root.querySelector<HTMLInputElement>('#cap-upload-video')?.addEventListener('change', (e) => {
      const f = (e.target as HTMLInputElement).files?.[0];
      if (f) this.handleVideoUpload(f);
    });

    // E3: import a file from a public Drive link (or any direct public URL)
    root.querySelector('#cap-link-import')?.addEventListener('click', () => this.importLink(insp));
    root.querySelector<HTMLInputElement>('#cap-link-url')?.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); this.importLink(insp); }
    });

    // Phase 3: suggest-checklist panel
    root.querySelector('#sg-open')?.addEventListener('click', () => { this.suggestOpen = true; this.baselineMsg = null; this.loadSuggest(insp); });
    root.querySelector('#sg-close')?.addEventListener('click', () => { this.suggestOpen = false; this.suggest = null; this.baselineMsg = null; this.draw(); });
    root.querySelector('#sg-cancel')?.addEventListener('click', () => { this.suggestOpen = false; this.suggest = null; this.baselineMsg = null; this.draw(); });
    root.querySelector('#sg-use')?.addEventListener('click', () => this.useSuggestForInspection(insp));
    root.querySelector('#sg-save')?.addEventListener('click', () => this.saveSuggestAsDefault(insp));
    // Manually add a custom check for this property (R4 #3). Appends to the
    // suggested set so it flows through "Use"/"Save as default" like any item.
    root.querySelector('#sg-add-btn')?.addEventListener('click', () => {
      const titleEl = root.querySelector('#sg-add-title') as HTMLInputElement | null;
      const title = (titleEl?.value || '').trim();
      if (!title) { titleEl?.focus(); return; }
      const area = ((root.querySelector('#sg-add-area') as HTMLInputElement | null)?.value || '').trim() || 'Custom';
      const required = (root.querySelector('#sg-add-req') as HTMLInputElement | null)?.checked || false;
      const photo_required = (root.querySelector('#sg-add-photo') as HTMLInputElement | null)?.checked || false;
      if (!this.suggest) return;
      if (!Array.isArray(this.suggest.items)) this.suggest.items = [];
      const key = 'custom_' + title.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40) + '_' + this.suggest.items.length;
      this.suggest.items.push({ key, title, area, source: 'manual', required, photo_required });
      this.draw();
    });

    // start a camera preview when entering record mode. When a recording is
    // ALREADY running, the redraw just destroyed the wired <video> element —
    // re-attach the live stream to the fresh node or the preview goes black
    // for the whole recording (the reported "video is just all black" bug).
    if (this.captureMode === 'record' && CAN_RECORD) {
      if (this.recorder && this.recorder.state === 'recording' && this.mediaStream) {
        const vid = this.container.querySelector('#cap-video') as HTMLVideoElement | null;
        if (vid && vid.srcObject !== this.mediaStream) {
          vid.srcObject = this.mediaStream;
          vid.muted = true;
          void vid.play().catch(() => {});
        }
      } else {
        this.startPreview();
      }
    }
  }

  // ── structured form (R17): render + bind + save ─────────────
  private renderFormCard(insp: any): string {
    const def = this.formDefs && insp.form_type ? this.formDefs[insp.form_type] : null;
    if (!def) return '';
    const f = insp.form || {};
    const seg = (key: string, cur: any, attr = 'data-form-score') => [1, 2, 3].map(n =>
      `<button class="frm-seg ${Number(cur) === n ? 'on' : ''}" ${attr}="${this.esc(key)}|${n}">${n}</button>`).join('');
    // Basic inspections score each pillar TWICE (R25): how the home was FOUND on
    // arrival, and how it was LEFT for the incoming guest (the score of record).
    const dual = insp.form_type === 'basic';
    const pillars = (def.pillars || []).map((p: any) => dual ? `
      <div class="frm-row">
        <div class="frm-row-t">${this.esc(p.title)}${p.required ? ' <span class="frm-req">*</span>' : ''}</div>
        <div class="frm-dual"><span class="frm-dual-l">Found</span><div class="frm-seg-row">${seg(p.key, f.scores_found?.[p.key], 'data-form-found')}</div></div>
        <div class="frm-dual"><span class="frm-dual-l">Left</span><div class="frm-seg-row">${seg(p.key, f.scores?.[p.key])}</div></div>
      </div>` : `
      <div class="frm-row">
        <div class="frm-row-t">${this.esc(p.title)}${p.required ? ' <span class="frm-req">*</span>' : ''}</div>
        <div class="frm-seg-row">${seg(p.key, f.scores?.[p.key])}</div>
      </div>`).join('');
    const sections = (def.sections || []).map((s: any) => {
      const checked: number[] = ((f.checks?.[s.key]) || []).map((n: any) => Number(n));
      return `
      <details class="frm-sec" data-frm-sec="${this.esc(s.key)}" ${this.openFormSecs.has(s.key) ? 'open' : ''}>
        <summary><span class="frm-sec-letter">${this.esc(s.letter)}</span> ${this.esc(s.title)} <span class="frm-sec-n" data-frm-sec-n="${this.esc(s.key)}">${checked.length}/${s.checks.length}</span></summary>
        ${s.hint ? `<div class="frm-hint">${this.esc(s.hint)}</div>` : ''}
        ${s.checks.map((c: string, i: number) => `
          <label class="frm-check"><input type="checkbox" data-form-check="${this.esc(s.key)}|${i}" ${checked.includes(i) ? 'checked' : ''}> <span>${this.esc(c)}</span></label>`).join('')}
        <input class="frm-note" data-form-note="${this.esc(s.key)}" placeholder="${this.esc(s.note)}" value="${this.esc(f.notes?.[s.key] || '')}">
        <div class="frm-photo-hint">📷 ${this.esc(s.photo)} — attach in Photo mode below</div>
      </details>`;
    }).join('');
    const fields = (def.fields || []).map((fd: any) => fd.kind === 'choice' ? `
      <div class="frm-row">
        <div class="frm-row-t">${this.esc(fd.title)}${fd.required ? ' <span class="frm-req">*</span>' : ''}</div>
        <div class="frm-seg-row">${(fd.options || []).map((o: string) => `<button class="frm-seg wide ${f.fields?.[fd.key] === o ? 'on' : ''}" data-form-field="${this.esc(fd.key)}|${this.esc(o)}">${this.esc(o)}</button>`).join('')}</div>
      </div>` : `
      <div class="frm-row">
        <div class="frm-row-t">${this.esc(fd.title)}</div>
        <input class="frm-note" data-form-ftext="${this.esc(fd.key)}" value="${this.esc(f.fields?.[fd.key] || '')}" placeholder="Notes…">
      </div>`).join('');
    const ten = (key: string, title: string, cur: any, guide?: string) => `
      <div class="frm-row">
        <div class="frm-row-t">${this.esc(title)}</div>
        <div class="frm-seg-row wrap">${Array.from({ length: 10 }, (_, i) => i + 1).map(n => `<button class="frm-seg ten ${Number(cur) === n ? 'on' : ''}" data-form-ten="${key}|${n}">${n}</button>`).join('')}</div>
        ${guide ? `<div class="frm-hint">${this.esc(guide)}</div>` : ''}
      </div>`;
    const photoGuides = (def.photo_guides || []).map((p: any) => `<div class="frm-photo-hint">📷 ${this.esc(p.title)}</div>`).join('');
    const comp = insp.completeness;
    return `
      <div class="cap-card card-elev">
        <div class="cap-section-h">${this.esc(def.title)} <span id="frm-completeness" class="frm-comp">${comp && comp.score != null ? `${comp.score}% complete` : ''}</span></div>
        <div class="cap-hint" style="margin-bottom:10px;">Score 1 = needs work · 2 = acceptable · 3 = excellent. Everything saves as you go.</div>
        ${pillars}
        ${sections}
        ${fields}
        ${def.overall ? ten('overall', def.overall.title, f.overall, def.overall.guide) : ''}
        ${def.xfactor ? ten('xfactor', def.xfactor.title, f.xfactor) : ''}
        ${photoGuides}
      </div>`;
  }

  private bindFormCard(insp: any): void {
    if (!insp.form_type || !this.formDefs) return;
    // <details> open state survives redraws — async photo-upload draws must not
    // collapse the section the inspector is working in.
    this.container.querySelectorAll<HTMLDetailsElement>('[data-frm-sec]').forEach(d => d.addEventListener('toggle', () => {
      const k = d.dataset.frmSec!;
      if (d.open) this.openFormSecs.add(k); else this.openFormSecs.delete(k);
    }));
    // Text inputs commit to LOCAL state on every keystroke, so any redraw —
    // including the async draws photo uploads fire — re-renders the current
    // text instead of destroying it. Network save still happens on blur.
    this.container.querySelectorAll<HTMLInputElement>('[data-form-note]').forEach(el => el.addEventListener('input', () => {
      insp.form = insp.form || {}; insp.form.notes = insp.form.notes || {}; insp.form.notes[el.dataset.formNote!] = el.value;
    }));
    this.container.querySelectorAll<HTMLInputElement>('[data-form-ftext]').forEach(el => el.addEventListener('input', () => {
      insp.form = insp.form || {}; insp.form.fields = insp.form.fields || {}; insp.form.fields[el.dataset.formFtext!] = el.value;
    }));
    const collectText = () => {
      insp.form = insp.form || {};
      this.container.querySelectorAll<HTMLInputElement>('[data-form-note]').forEach(inp => {
        insp.form.notes = insp.form.notes || {}; insp.form.notes[inp.dataset.formNote!] = inp.value;
      });
      this.container.querySelectorAll<HTMLInputElement>('[data-form-ftext]').forEach(inp => {
        insp.form.fields = insp.form.fields || {}; insp.form.fields[inp.dataset.formFtext!] = inp.value;
      });
    };
    this.container.querySelectorAll<HTMLElement>('[data-form-score]').forEach(el => el.addEventListener('click', () => {
      const [key, n] = el.dataset.formScore!.split('|');
      collectText();
      insp.form.scores = { ...(insp.form.scores || {}), [key]: Number(n) };
      void this.saveForm(insp, { scores: { [key]: Number(n) }, notes: insp.form.notes, fields: insp.form.fields });
      this.draw();
    }));
    // "Found on arrival" score (R25 dual scoring — basic form only)
    this.container.querySelectorAll<HTMLElement>('[data-form-found]').forEach(el => el.addEventListener('click', () => {
      const [key, n] = el.dataset.formFound!.split('|');
      collectText();
      insp.form.scores_found = { ...(insp.form.scores_found || {}), [key]: Number(n) };
      void this.saveForm(insp, { scores_found: { [key]: Number(n) }, notes: insp.form.notes, fields: insp.form.fields });
      this.draw();
    }));
    this.container.querySelectorAll<HTMLInputElement>('[data-form-check]').forEach(el => el.addEventListener('change', () => {
      const [sec, idxS] = el.dataset.formCheck!.split('|'); const idx = Number(idxS);
      insp.form = insp.form || {}; insp.form.checks = insp.form.checks || {};
      const cur: number[] = (insp.form.checks[sec] || []).map((x: any) => Number(x));
      insp.form.checks[sec] = el.checked ? [...new Set([...cur, idx])] : cur.filter(x => x !== idx);
      void this.saveForm(insp, { checks: { [sec]: insp.form.checks[sec] } });
      // count chip updates in place — no redraw, so the <details> stays open
      const nEl = this.container.querySelector(`[data-frm-sec-n="${sec}"]`);
      const total = (this.formDefs[insp.form_type]?.sections || []).find((s: any) => s.key === sec)?.checks?.length;
      if (nEl && total != null) nEl.textContent = `${insp.form.checks[sec].length}/${total}`;
    }));
    this.container.querySelectorAll<HTMLInputElement>('[data-form-note]').forEach(el => el.addEventListener('change', () => {
      insp.form = insp.form || {}; insp.form.notes = { ...(insp.form.notes || {}), [el.dataset.formNote!]: el.value };
      void this.saveForm(insp, { notes: { [el.dataset.formNote!]: el.value } });
    }));
    this.container.querySelectorAll<HTMLInputElement>('[data-form-ftext]').forEach(el => el.addEventListener('change', () => {
      insp.form = insp.form || {}; insp.form.fields = { ...(insp.form.fields || {}), [el.dataset.formFtext!]: el.value };
      void this.saveForm(insp, { fields: { [el.dataset.formFtext!]: el.value } });
    }));
    this.container.querySelectorAll<HTMLElement>('[data-form-field]').forEach(el => el.addEventListener('click', () => {
      const [key, val] = el.dataset.formField!.split('|');
      collectText();
      insp.form.fields = { ...(insp.form.fields || {}), [key]: val };
      void this.saveForm(insp, { fields: { [key]: val }, notes: insp.form.notes });
      this.draw();
    }));
    this.container.querySelectorAll<HTMLElement>('[data-form-ten]').forEach(el => el.addEventListener('click', () => {
      const [key, n] = el.dataset.formTen!.split('|');
      collectText();
      (insp.form as any)[key] = Number(n);
      void this.saveForm(insp, { [key]: Number(n), notes: insp.form.notes, fields: insp.form.fields });
      this.draw();
    }));
  }

  // R41: collapsible "Stocking & safety check" — the CEO's bare-minimum
  // standard for every home. Collapsed by default with live x/N progress on
  // the header so the step never gets longer unless the inspector opens it;
  // each tap persists through the existing /form checks merge.
  private renderStockCheck(insp: any): string {
    if (!insp.form_type) return '';   // checks ride on the structured form
    const checks = (insp.form && insp.form.checks) || {};
    // R120: the groups + items from lib/stockCheck (Flashlight after Fire
    // extinguisher on inspections it applies to — never on an older one)
    const groups = applicableGroups(insp);
    const all = groups.flatMap(g => g.items);
    const done = all.filter(it => !!checks[it.key]).length;
    const header = `
      <div class="cap-section-h" data-stock-toggle style="cursor:pointer; display:flex; justify-content:space-between; align-items:center;">
        <span>📦 Stocking &amp; safety check</span>
        <span style="font-weight:600; color:${done === all.length ? 'var(--success-text,#166534)' : 'var(--text-muted)'};">${done}/${all.length} ${this.stockOpen ? '▾' : '▸'}</span>
      </div>`;
    if (!this.stockOpen) return `<div class="cap-card card-elev">${header}</div>`;
    const row = (it: { key: string; label: string }) => `
      <label style="display:flex; align-items:center; gap:10px; padding:8px 2px; min-height:36px; border-bottom:1px solid var(--border-soft,#f1f5f9); font-size:14px; cursor:pointer;">
        <input type="checkbox" data-stock-item="${this.esc(it.key)}" ${checks[it.key] ? 'checked' : ''} style="width:19px; height:19px;">
        <span>${this.esc(it.label)}</span>
      </label>`;
    return `
      <div class="cap-card card-elev">
        ${header}
        <div style="font-size:12px; color:var(--text-muted); margin:2px 0 6px;">The bare-minimum standard for every home — tick what's stocked; anything left unticked shows as a gap on the report. The AI grader sees your ticks too.</div>
        ${groups.map((g, gi) => `
        <div style="font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted); margin-top:${gi ? 12 : 6}px;">${this.esc(g.label)}</div>
        ${g.items.map(row).join('')}`).join('')}
      </div>`;
  }

  // Missing stock/safety items for the report card — null when the check was
  // never started (an untouched check reads "not done", not "19 missing").
  // R120: lib/stockCheck stockGaps (the same since-rule as the card).
  private stockGaps(insp: any): { missing: string[]; done: number; total: number } | null {
    return stockGapsOf(insp);
  }

  // R120 (team: "flashlight"): a tick that answered (or, unticked,
  // un-answered) AI findings comes back with them (contract C10) — fold
  // them into the local copy so the report card shows it without a reload.
  private applyStockResponse(insp: any, j: any): boolean {
    if (!j || !Array.isArray(j.issues)) return false;
    insp.issues = this.keepDispositions(insp.issues, j.issues);
    if (Array.isArray(j.items)) {
      const byKey = new Map(j.items.map((t: any) => [t && t.key, t]));
      insp.items = (insp.items || []).map((t: any) => {
        const r: any = byKey.get(t.key);
        if (!r) return t;
        const next = { ...t };
        if (r.stock_answered) next.stock_answered = r.stock_answered; else delete next.stock_answered;
        return next;
      });
    }
    if (j.coverage_stock_answered && typeof j.coverage_stock_answered === 'object') insp.coverage_stock_answered = j.coverage_stock_answered;
    return true;
  }

  private async saveForm(insp: any, patch: any): Promise<void> {
    // Merge into the pending buffer first — a failed earlier save rides along
    // with the next tap instead of being silently lost.
    const p = this.formPending[insp.id] || {};
    for (const k of ['scores', 'scores_found', 'checks', 'notes', 'fields']) {
      if (patch[k]) p[k] = { ...(p[k] || {}), ...patch[k] };
    }
    if (patch.overall != null) p.overall = patch.overall;
    if (patch.xfactor != null) p.xfactor = patch.xfactor;
    if (!Object.keys(p).length) { delete this.formPending[insp.id]; return; }   // nothing held, nothing to send
    this.formPending[insp.id] = p;
    try {
      const r = await fetch(`${API}/inspections/${encodeURIComponent(insp.id)}/form`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(p),
      });
      // 400 (no structured form) / 404 (inspection gone) are the server REFUSING
      // these answers — drop them (and say so) rather than block Submit /
      // Complete forever. Anything else (401/403 while a session refreshes,
      // 408, 429, 5xx) is transient: keep them held, like offline.
      if (r.status === 400 || r.status === 404) {
        delete this.formPending[insp.id];
        const warn = this.container.querySelector('#frm-save-warn');
        if (warn) warn.textContent = '⚠ those answers couldn’t be saved on this inspection';
        else this.container.querySelector('#frm-completeness')?.insertAdjacentHTML('afterend', `<span id="frm-save-warn" style="float:right; font-size:11px; font-weight:700; color:#b91c1c; margin-right:8px;">⚠ those answers couldn’t be saved on this inspection</span>`);
        return;
      }
      const j = r.ok ? await r.json() : null;
      if (!j) throw new Error(`HTTP ${r.status}`);
      delete this.formPending[insp.id];
      if (j.completeness) {
        insp.completeness = j.completeness;
        if (j.form) insp.form = j.form;
        const chip = this.container.querySelector('#frm-completeness');
        if (chip && j.completeness.score != null) chip.textContent = `${j.completeness.score}% complete`;
      }
      const warn = this.container.querySelector('#frm-save-warn'); if (warn) warn.remove();
      // R120: the tick's findings changes → redraw (the report card / recap
      // read insp.issues) — never under a field the inspector is typing in
      if (this.applyStockResponse(insp, j)) {
        this.replaceInsp(insp);
        const typing = document.activeElement && this.container.contains(document.activeElement) && /^(TEXTAREA|SELECT)$|^INPUT$/.test(document.activeElement.tagName) && (document.activeElement as HTMLInputElement).type !== 'checkbox';
        if (!typing && this.activeId === insp.id) this.draw();
      }
    } catch (_) {
      // keep pending; surface it — the card claims "everything saves as you go"
      if (!this.container.querySelector('#frm-save-warn')) {
        const chip = this.container.querySelector('#frm-completeness');
        chip?.insertAdjacentHTML('afterend', `<span id="frm-save-warn" style="float:right; font-size:11px; font-weight:700; color:#b45309; margin-right:8px;">⚠ offline — answers held, retrying on your next tap</span>`);
      }
    }
  }

  // show a live camera preview (no recording yet)
  private async startPreview(): Promise<void> {
    const vid = this.container.querySelector('#cap-video') as HTMLVideoElement | null;
    if (!vid) return;
    try {
      if (!this.mediaStream) {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: true });
      }
      vid.srcObject = this.mediaStream;
      await vid.play().catch(() => {});
    } catch (e: any) {
      this.showCapMsg(`Camera unavailable: ${e?.message || e}. Use Photo mode or type notes.`, 'err');
    }
  }

  private async startRecording(): Promise<void> {
    try {
      if (!this.mediaStream) {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: true });
      }
      const vid = this.container.querySelector('#cap-video') as HTMLVideoElement | null;
      if (vid) { vid.srcObject = this.mediaStream; vid.muted = true; await vid.play().catch(() => {}); }
      this.recChunks = [];
      const MR = (window as any).MediaRecorder;
      const mime = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4']
        .find(m => MR.isTypeSupported && MR.isTypeSupported(m)) || '';
      this.recorder = mime ? new MR(this.mediaStream, { mimeType: mime }) : new MR(this.mediaStream);
      this.recorder.ondataavailable = (e: any) => { if (e.data && e.data.size) this.recChunks.push(e.data); };
      this.recorder.start();
      this.startRecognition();
      this.draw(); // re-render toggle into "Stop" state
    } catch (e: any) {
      this.showCapMsg(`Couldn’t start recording: ${e?.message || e}`, 'err');
    }
  }

  private startRecognition(): void {
    if (!SpeechRec) return;
    try {
      const rec = new SpeechRec();
      rec.continuous = true; rec.interimResults = true; rec.lang = 'en-US';
      rec.onresult = (ev: any) => {
        let interim = '';
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const r = ev.results[i];
          if (r.isFinal) this.transcriptFinal += (this.transcriptFinal ? ' ' : '') + r[0].transcript.trim();
          else interim += r[0].transcript;
        }
        const ta = this.container.querySelector('#cap-transcript') as HTMLTextAreaElement | null;
        if (ta) ta.value = (this.transcriptFinal + (interim ? ' ' + interim : '')).trim();
      };
      rec.onerror = () => {}; // degrade silently
      rec.onend = () => { if (this.recorder && this.recorder.state === 'recording') { try { rec.start(); } catch (_) {} } };
      this.recognition = rec;
      rec.start();
    } catch (_) { /* degrade silently — manual transcript still works */ }
  }

  private async stopRecording(): Promise<void> {
    this.stopRecognition();
    const rec = this.recorder;
    if (!rec) return;
    // R114: the inspection this walkthrough belongs to — taken NOW, before
    // any await (Save & exit finishes a live recording and then leaves).
    const insp0 = this.activeInsp();
    const inspId = insp0 ? String(insp0.id) : (this.activeId || '');
    const listingId = insp0 && insp0.listing_id != null ? String(insp0.listing_id) : null;
    const done = new Promise<void>(resolve => { rec.onstop = () => resolve(); });
    try { rec.stop(); } catch (_) {}
    await done;
    this.stopStream();
    const blob = new Blob(this.recChunks, { type: rec.mimeType || 'video/webm' });
    this.recordedBlob = blob;
    this.recorder = null;
    this.showCapMsg('Processing walkthrough — extracting frames…', 'warn', true);
    try {
      const frames = await this.extractFrames(blob, FRAME_SAMPLES);
      const willStoreVideo = blob.size <= VIDEO_MAX_BYTES;
      // Frames carry the grade — each is saved on the phone FIRST and uploads
      // from the queue; the raw video is best-effort and skipped when it's
      // over the server cap, so a long walkthrough still works.
      for (let i = 0; i < frames.length; i++) await this.captureFrame(inspId, listingId, `Frame ${i + 1}`, frames[i]);
      // persist live transcript onto the inspection's capture
      const ta = this.container.querySelector('#cap-transcript') as HTMLTextAreaElement | null;
      if (ta && this.activeId === inspId) this.transcriptFinal = ta.value;
      if (this.activeId === inspId) this.clearCapMsg();
      this.draw();
      if (this.activeId === inspId && frames.length) this.showCapMsg(willStoreVideo
        ? `Saved ${frames.length} frames on this phone — they upload in the background.`
        : `Saved ${frames.length} frames on this phone for grading — the full video wasn’t uploaded (we grade from frames, so any length works).`, 'ok');
      if (willStoreVideo) this.startVideoUpload(inspId, blob);   // review only; Save & exit waits for it (with a Leave now)
    } catch (e: any) {
      if (this.activeId === inspId) this.showCapMsg(`Frame extraction failed: ${this.esc(e?.message || e)}. You can still add photos or analyze from notes.`, 'err');
      this.draw();
    }
  }

  // Process an uploaded video exactly like a stopped recording: extract frames
  // ON-DEVICE and upload only those (+ a best-effort video when it's small enough)
  // — then it's ready to Analyze. The raw file never has to upload, so a COO can
  // drop in a 30-minute walkthrough shot on their phone with no giant upload.
  private async handleVideoUpload(file: File): Promise<void> {
    if (!this.activeId) { this.showCapMsg('Start the inspection first, then upload.', 'err'); return; }
    // R114: ids taken at entry — never this.activeId after an await
    const insp0 = this.activeInsp();
    const inspId = String(this.activeId);
    const listingId = insp0 && insp0.listing_id != null ? String(insp0.listing_id) : null;
    this.recordedBlob = file;
    this.showCapMsg('Processing uploaded video — extracting frames on your device…', 'warn', true);
    try {
      const frames = await this.extractFrames(file, FRAME_SAMPLES);
      const willStoreVideo = file.size <= VIDEO_MAX_BYTES;
      // Frames first — they carry the grade and are tiny; each is saved on the
      // phone and uploads from the queue. The raw video uploads only when it's
      // under the server cap (we grade from frames, so any source size works).
      for (let i = 0; i < frames.length; i++) await this.captureFrame(inspId, listingId, `Frame ${i + 1}`, frames[i]);
      if (this.activeId === inspId) this.clearCapMsg();
      this.draw();
      if (this.activeId === inspId && frames.length) this.showCapMsg(willStoreVideo
        ? `Saved ${frames.length} frames from your video on this phone — they upload in the background.`
        : `Saved ${frames.length} frames from your video on this phone for grading — the full file wasn’t uploaded (we grade from frames, so a 30-minute walkthrough is no problem).`, 'ok');
      if (willStoreVideo) this.startVideoUpload(inspId, file);   // review only; Save & exit waits for it (with a Leave now)
    } catch (e: any) {
      if (this.activeId === inspId) this.showCapMsg(`Couldn't process that video: ${this.esc(e?.message || e)}. Try a different file or use Photo mode.`, 'err');
      this.draw();
    }
  }

  // Load a recorded Blob into a hidden <video>, seek across its duration, draw
  // each timestamp to a <canvas>, export JPEG blobs. Client-side, no upload yet.
  private extractFrames(blob: Blob, count: number): Promise<Blob[]> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const v = document.createElement('video');
      v.muted = true; v.playsInline = true; v.src = url;
      const frames: Blob[] = [];
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      let times: number[] = [];
      let idx = 0;
      const cleanup = () => { URL.revokeObjectURL(url); };
      const fail = (e: any) => { cleanup(); reject(e); };

      v.onloadedmetadata = () => {
        let dur = v.duration;
        if (!isFinite(dur) || dur <= 0) dur = 0; // some webm report Infinity until seeked
        canvas.width = v.videoWidth || 1280;
        canvas.height = v.videoHeight || 720;
        const span = dur > 0 ? dur : 10;
        // sample evenly, skip the very start/end
        for (let i = 0; i < count; i++) times.push(Math.max(0.1, (span * (i + 0.5)) / count));
        seekNext();
      };
      const seekNext = () => {
        if (idx >= times.length) { cleanup(); resolve(frames); return; }
        try { v.currentTime = times[idx]; } catch (_) { cleanup(); resolve(frames); }
      };
      v.onseeked = () => {
        try {
          if (ctx) {
            ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(b => { if (b) frames.push(b); idx++; seekNext(); }, 'image/jpeg', 0.8);
          } else { idx++; seekNext(); }
        } catch (e) { fail(e); }
      };
      v.onerror = () => fail(new Error('could not decode the recording'));
      // safety timeout
      setTimeout(() => { if (idx < times.length) { cleanup(); resolve(frames); } }, 20000);
    });
  }

  // E3: POST /inspections/:id/import-link { url }. The server fetches a PUBLIC
  // Drive FILE link (or any direct public URL) and ingests it: an image lands in
  // capture.frame_urls (and grades); a video is re-hosted for review only (no
  // server-side frame extraction). The endpoint returns 200 {ok:false,message}
  // for every handled refusal (folder/private/html/bad-type/too-big), so we read
  // the message rather than treating those as request failures.
  private async importLink(insp: any): Promise<void> {
    const input = this.container.querySelector<HTMLInputElement>('#cap-link-url');
    const btn = this.container.querySelector<HTMLButtonElement>('#cap-link-import');
    const url = (input?.value || '').trim();
    if (!url) { this.showLinkMsg('Paste a public Drive file link or a direct file URL first.', 'err'); return; }
    if (btn) btn.disabled = true;
    this.showLinkMsg('Fetching the link…', 'warn');
    let j: any = null;
    try {
      const r = await fetch(`${API}/inspections/${insp.id}/import-link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url }),
      });
      j = await r.json().catch(() => ({}));
    } catch (e: any) {
      if (btn) btn.disabled = false;
      this.showLinkMsg(`Import failed: ${this.esc(e?.message || e)}`, 'err');
      return;
    }
    if (btn) btn.disabled = false;
    if (!j) { this.showLinkMsg('Import failed — try again.', 'err'); return; }
    if (j.ok && j.kind === 'image') {
      if (j.inspection) this.replaceInsp(j.inspection);
      // draw() rebuilds the message slot — render FIRST, then write the message
      // (otherwise the confirmation is wiped instantly).
      this.draw();
      this.showLinkMsg('Photo imported — it will be graded with the rest of the frames.', 'ok');
      return;
    }
    if (j.ok && j.kind === 'video') {
      if (j.inspection) this.replaceInsp(j.inspection);
      this.draw();
      this.showLinkMsg(this.esc(j.message || 'Video stored for review. Add photos or a transcript for AI grading.'), 'warn');
      return;
    }
    // {ok:false} — a handled refusal (folder/private/html/bad-type/too-big) or error.
    this.showLinkMsg(this.esc(j.message || j.error || 'Could not import that link.'), 'err');
  }

  // Render into the import card's own message slot (kinds match .cap-msg styles).
  private showLinkMsg(html: string, kind: 'err' | 'warn' | 'ok'): void {
    const el = this.container.querySelector('#cap-link-msg');
    if (el) el.innerHTML = `<div class="cap-msg ${kind}">${html}</div>`;
  }

  // R114 fix: track the upload so Save & exit can wait for it.
  private startVideoUpload(inspId: string, blob: Blob): void {
    const p: Promise<void> = this.uploadVideo(inspId, blob).finally(() => { if (this.videoUploadP === p) this.videoUploadP = null; });
    this.videoUploadP = p;
  }
  // Save & exit while the video is still going up: say so, wait, and offer a
  // way out (leaving the screen does not stop the upload — closing the app does).
  // R114 fix2: leaving the screen another way (← Inspections, Submit → report)
  // settles the wait at once (the view setter calls leaveWaitCancel) and drops
  // its "Leave now" note, whose button would no longer do anything.
  private waitForVideoOrLeave(p: Promise<void>): Promise<void> {
    this.showCapMsg('Your photos are saved on this phone. <b>The walkthrough video is still uploading</b> — it isn’t saved on the phone, so keep the app open until it finishes. <button type="button" class="btn btn-secondary btn-sm cap-leave-now" data-cap-leave-now>Leave now</button>', 'warn', true);
    const note = this.capNote;
    return new Promise<void>(resolve => {
      const onClick = (e: Event) => { if ((e.target as HTMLElement | null)?.closest?.('[data-cap-leave-now]')) done(); };
      const done = () => {
        this.container.removeEventListener('click', onClick);
        if (this.leaveWaitCancel === cancel) this.leaveWaitCancel = null;
        resolve();
      };
      const cancel = () => { if (this.capNote === note) this.capNote = null; done(); };
      this.leaveWaitCancel = cancel;
      this.container.addEventListener('click', onClick);
      p.then(done, done);
    });
  }

  // R114: the inspection id is passed in (taken before any await by the caller).
  private async uploadVideo(inspId: string, blob: Blob): Promise<void> {
    if (!inspId) return;
    // Never attempt an oversized upload — the server caps /video and grading is
    // frame-based, so a too-big walkthrough is simply not stored (by design).
    if (blob.size > VIDEO_MAX_BYTES) return;
    const ext = (blob.type.includes('mp4')) ? 'mp4' : 'webm';
    const fd = new FormData();
    fd.append('video', blob, `walkthrough.${ext}`);
    this.videoUploading = true;
    this.refreshQueueUi();
    try {
      const r = await fetch(`${API}/inspections/${encodeURIComponent(inspId)}/video`, { method: 'POST', body: fd });
      const j = await r.json().catch(() => ({} as any));
      if (r.ok && j.inspection && j.inspection.id === inspId) this.replaceInsp(j.inspection);
    } catch (_) { /* video is best-effort; frames + transcript still analyze */ }
    finally { this.videoUploading = false; this.refreshQueueUi(); }
  }

  // R114: every frame — per-room "+ Photo", bulk, recorded walkthrough,
  // uploaded video — goes through the phone's persist-first queue
  // (lib/captureQueue). The inspection + listing ids are taken at the START
  // and passed in: after an await this.activeId may already be null (Save &
  // exit) or another inspection (the old misfile / null-id bug). Resolves once
  // the photo is safe on the phone; the queue uploads it (dedupe key, room tag,
  // capture time) and reports back through 'surge-frame-queue'.
  private async captureFrame(inspId: string, listingId: string | null, area: string, file: Blob): Promise<boolean> {
    if (!inspId) return false;
    try {
      await enqueueFrame({ inspectionId: inspId, listingId, area, file });
      this.refreshQueueUi();
      return true;
    } catch (e: any) {
      // only reachable if the browser refuses even an in-memory copy — say so, loudly
      this.showCapMsg(`Couldn’t keep that photo on this phone: ${this.esc(e?.message || e)}. Take it again.`, 'err');
      return false;
    }
  }

  // Bulk upload: the ORIGINAL files go into the queue (a camera-roll pick keeps
  // its own lastModified = when it was taken; shrinking happens when each one
  // is sent). The AI auto-sort runs once this inspection's photos have ALL left
  // the phone (onQueueEvent), not when the loop ends.
  private async bulkUpload(insp: any, files: File[]): Promise<void> {
    const inspId = String(insp.id);
    const listingId = insp.listing_id != null ? String(insp.listing_id) : null;
    const total = files.length;
    let saved = 0;
    this.showBulkMsg(`Saving ${total} photo${total === 1 ? '' : 's'} on this phone…`, 'warn');
    for (const f of files) {
      if (await this.captureFrame(inspId, listingId, 'General', f)) saved++;
      if (this.activeId === inspId) this.showBulkMsg(`Saved ${saved} of ${total} on this phone — uploading…`, 'warn');
    }
    if (saved) {
      // the earliest picks may already have landed while later ones were saving
      if (queueCounts(inspId).thisPending === 0) { InspectionsPage.bulkSortFor.delete(inspId); void this.autoSortFrames(inspId, saved); }
      else InspectionsPage.bulkSortFor.set(inspId, (InspectionsPage.bulkSortFor.get(inspId) || 0) + saved);
    }
    if (this.activeId !== inspId || this.view !== 'capture') return;
    this.draw();
    this.showBulkMsg(saved
      ? `${saved} photo${saved === 1 ? '' : 's'} saved on this phone and uploading${saved < total ? ` · ${total - saved} could not be kept — pick them again` : ''}. They auto-sort into rooms once they’ve all landed — you can keep working or Save &amp; exit.`
      : 'No photos could be kept on this phone — pick them again.', saved ? 'warn' : 'err');
  }

  // The landed frames are in capture.frame_urls — ask the AI to auto-sort them.
  // Never blocks anything: no alert on failure, and it only touches the screen
  // if the inspector is still on this inspection's capture view.
  private async autoSortFrames(id: string, landed: number): Promise<void> {
    let j: any = null;
    try {
      const r = await fetch(`${API}/inspections/${id}/classify`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      j = await r.json().catch(() => null);
      if (!r.ok) j = null;
    } catch (_) { j = null; }
    if (this.activeId !== id || this.view !== 'capture') return;   // moved on (submitted / left) — the server kept the sort
    if (!j) {
      this.showBulkMsg(`Uploaded ${landed} photo${landed === 1 ? '' : 's'} — auto-sort didn’t come through, but the photos are saved and Submit still works.`, 'warn');
      return;
    }
    // merge ONLY what classify changed (frame rooms + frames) — a wholesale
    // replace would drop answers typed / held while the sort ran
    const local = this.activeInsp();
    if (j.inspection && local && local.id === id) {
      const rc = j.inspection.capture || {};
      local.capture = { ...(local.capture || {}), frame_meta: rc.frame_meta || (local.capture || {}).frame_meta, frame_urls: [...new Set([...((local.capture || {}).frame_urls || []), ...(rc.frame_urls || [])])] };
    } else if (j.inspection) this.replaceInsp(j.inspection);
    const typing = () => !!(document.activeElement && this.container.contains(document.activeElement) && /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName));
    if (j.ok) {
      // Re-light the strip from the FULL coverage — classify now runs over ALL
      // frames and persists frame_meta for every one (incl 'Unsorted'), so
      // re-seed from the updated inspection instead of rebuilding from just
      // this response's classified subset. Wiping thumbnails of unclassified
      // frames here was the "100 uploaded, 20 shown" bug.
      const cur = this.activeInsp();
      if (!cur) return;
      this.seedPhotoStrip(cur);
      this.stripSeededFor = cur.id;
      const sorted = j.classified ?? (j.classifications || []).length;
      const totalF = j.total_frames ?? this.frameCount(cur);
      // Re-render to light the strip from the AI assignment, THEN write the
      // confirmation (draw() rebuilds #cap-bulk-msg, so set it after). Not
      // while they're typing — the strip lights on the next redraw instead.
      if (!typing()) this.draw();
      this.showBulkMsg(`Auto-sorted ${sorted} of ${totalF} photo${totalF === 1 ? '' : 's'} into rooms. Review the strip, then Submit.`, 'ok');
    } else {
      // GRACEFUL: no key / nothing to classify — photos still uploaded; Submit works.
      if (!typing()) this.draw();
      this.showBulkMsg(`Uploaded ${landed} photo${landed === 1 ? '' : 's'}. ${this.esc(j.message || 'Auto-sort unavailable — Submit still works.')}`, 'warn');
    }
  }

  private showBulkMsg(html: string, kind: 'err' | 'warn' | 'ok'): void {
    const el = this.container.querySelector('#cap-bulk-msg');
    if (el) el.innerHTML = `<div class="cap-msg ${kind}" style="margin-top:8px;">${html}</div>`;
  }

  // ── R114: the phone's photo queue, as this screen shows it ────────────────
  // "Sync now": clears the backoff and runs the queue until it is idle. The
  // result is STATE rendered by the tally (a message written before draw()
  // used to be wiped instantly), and the button is disabled while it runs.
  private async syncPending(): Promise<void> {
    if (this.syncBusy || isSyncing()) return;
    const forId = this.activeId;
    this.syncBusy = true;
    this.syncNote = { text: 'Syncing photos from this phone…', kind: 'warn', for: forId };
    this.refreshQueueUi();
    let r: { uploaded: number; held: number; attention: number; remaining: number } | null = null;
    try { r = await syncNow(); } catch (_) { r = null; }
    this.syncBusy = false;
    const c = queueCounts(forId);
    const waiting = c.total - c.attention;   // still on the phone and still trying
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (!r) this.syncNote = { text: 'Sync didn’t run — the photos are still safe on this phone; it retries on its own.', kind: 'err', for: forId };
    else {
      const parts = [
        r.uploaded ? `${r.uploaded} uploaded` : '',
        r.held ? `${r.held} sent to the office (their inspection no longer exists)` : '',
      ].filter(Boolean);
      if (!parts.length) parts.push(waiting ? 'Nothing could upload just now' : 'Nothing was waiting to upload');
      if (waiting) parts.push(`${waiting} still on this phone — ${offline ? 'they go when you reconnect' : 'retrying automatically'}`);
      if (c.attention) parts.push(`${c.attention} need${c.attention === 1 ? 's' : ''} a look — tap Review`);
      if (!waiting && !c.attention) parts.push('everything from this phone is up');
      this.syncNote = { text: parts.join(' · ') + '.', kind: c.attention ? 'err' : (waiting ? 'warn' : 'ok'), for: forId };
    }
    this.refreshQueueUi(true);
  }

  // One listener per instance (constructor); only the LIVE instance acts.
  private onQueueEvent(d: any): void {
    if (InspectionsPage.live !== this) return;
    if (d && d.type === 'uploaded') {
      const id = d.inspectionId ? String(d.inspectionId) : null;
      if (id && this.activeId === id && d.placement === 'attached') {
        if (d.inspection && d.inspection.id === id) this.replaceInsp(d.inspection);
        const url = d.url ? String(d.url) : '';
        const area = String(d.area || 'General');
        const known = Object.values(this.photoThumbs).some(list => list.includes(url));
        if (url && !known) {
          // the server filed a real room tag into frame_meta; 'General' and
          // video placeholders land in Unsorted until the AI sorts them
          const strip = (area === 'General' || /^Frame \d+$/.test(area)) ? InspectionsPage.UNSORTED : area;
          (this.photoThumbs[strip] = this.photoThumbs[strip] || []).push(url);
          this.photoTags[strip] = (this.photoTags[strip] || 0) + 1;
        }
      }
      // a bulk pick's auto-sort waits for its last photo to leave the phone
      if (id && InspectionsPage.bulkSortFor.has(id) && queueCounts(id).thisPending === 0) {
        const n = InspectionsPage.bulkSortFor.get(id) || 0;
        InspectionsPage.bulkSortFor.delete(id);
        void this.autoSortFrames(id, n);
      }
      this.refreshQueueUi(true);
      return;
    }
    this.refreshQueueUi();
  }

  // Cheap in-place refresh of the tally / field-view banner; a full redraw
  // (thumbnails) only when asked, coalesced, and never under a typing thumb.
  private refreshQueueUi(full = false): void {
    if (InspectionsPage.live && InspectionsPage.live !== this) return;
    const insp = this.view === 'capture' ? this.activeInsp() : null;
    const tally = this.container.querySelector('#cap-frames-tally');
    if (insp && tally) {
      tally.outerHTML = this.framesTallyHtml(insp);
      this.container.querySelector('#cap-sync')?.addEventListener('click', () => void this.syncPending());
      const se = this.container.querySelector<HTMLElement>('[data-cap-saveexit][title]');
      if (se) se.title = this.saveExitTitle(insp);
    }
    const banner = this.container.querySelector('#ifld-fq');
    if (banner) banner.outerHTML = this.phoneQueueBanner();
    if (!full || this.view !== 'capture') return;
    if (this.queueRedrawT) return;
    this.queueRedrawT = window.setTimeout(() => {
      this.queueRedrawT = null;
      if (this.view !== 'capture' || (InspectionsPage.live && InspectionsPage.live !== this)) return;
      const ae = document.activeElement;
      const typing = !!(ae && this.container.contains(ae) && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) && (ae as HTMLInputElement).type !== 'file');
      if (!typing) this.draw();
    }, 400);
  }

  // The capture screen's photo line: what's on the server · what's still on
  // THIS phone for THIS inspection — and, on its own line, photos from
  // earlier inspections (never one global number next to this inspection).
  private framesTallyHtml(insp: any): string {
    const frames = this.frameCount(insp);
    const c = queueCounts(insp.id);
    const busy = this.syncBusy || isSyncing();
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    const note = this.syncNote && this.syncNote.for === insp.id ? this.syncNote : null;
    const head = frames === 0 && !c.thisPending ? 'No photos yet.' : `${frames} photo${frames === 1 ? '' : 's'} uploaded`;
    const mine = c.thisPending
      ? ` · <span class="cap-pending">⏳ ${c.thisPending} uploading from this phone${offline ? ' (offline — they’ll go when you reconnect)' : ''}</span>`
      : '';
    const rejected = c.thisAttention
      ? ` · <span class="cap-pending bad">⚠ ${c.thisAttention} couldn’t upload</span> <button type="button" class="cap-sync" data-fq-open="${this.esc(insp.id)}" data-fq-current="${this.esc(insp.id)}">Review</button>`
      : '';
    const sync = (c.thisPending || c.olderPending)
      ? ` <button type="button" class="cap-sync" id="cap-sync" ${busy ? 'disabled' : ''}>${busy ? 'Syncing…' : 'Sync now'}</button>`
      : '';
    const older = c.olderPending
      ? `<div class="cap-older">📦 ${c.olderPending} photo${c.olderPending === 1 ? '' : 's'} from earlier inspections ${c.olderPending === 1 ? 'is' : 'are'} still on this phone — <button type="button" class="cap-review" data-fq-open="" data-fq-current="${this.esc(insp.id)}">Review</button></div>`
      : '';
    const warn = storageWarning() ? '<div class="cap-older bad">⚠ This browser won’t keep photos offline — leave this screen open until they finish uploading.</div>' : '';
    const video = this.videoUploading ? ' · uploading the walkthrough video…' : '';
    return `<div class="cap-frames-tally" id="cap-frames-tally"><div>${head}${mine}${rejected}${video}${sync}</div>${older}${warn}${note ? `<div class="cap-sync-note ${note.kind}">${this.esc(note.text)}</div>` : ''}</div>`;
  }

  // Save & exit is always safe now (every photo is on the phone first) — the
  // tooltip says what is still going on instead of "Everything is saved".
  // R114 fix: the walkthrough video is the one thing NOT kept on the phone (lib/frameQueuePolicy saveExitText).
  private saveExitTitle(insp: any): string {
    return saveExitText(insp ? queueCounts(insp.id).thisPending : 0, this.videoUploading);
  }

  // The field view's and (R114 fix) the office cockpit's one-line notice — only
  // when photos are waiting on this phone or a walkthrough video is uploading.
  private phoneQueueBanner(): string {
    const c = queueCounts(null);
    const video = this.videoUploading;
    if (!c.total && !video) return '<div id="ifld-fq"></div>';
    const busy = isSyncing();
    const photos = c.total ? `📤 ${phoneQueueLine(c, busy)}` : '';
    const vid = video ? `${photos ? ' · ' : '📤 '}Uploading a walkthrough video — keep the app open until it finishes` : '';
    return `<div id="ifld-fq" class="ifld-fq">
        <span>${photos}${vid}</span>
        ${c.total ? '<button type="button" class="btn btn-secondary btn-sm" data-fq-open="">Review</button>' : ''}
      </div>`;
  }

  // capture-view message slot, rendered from state so a redraw keeps it
  private capNoteHtml(insp: any): string {
    const n = this.capNote;
    if (!n || (n.for && n.for !== insp.id)) return '';
    if (!n.sticky && n.kind !== 'err' && Date.now() - n.at > 20000) return '';
    return `<div class="cap-msg ${n.kind}">${n.html}</div>`;
  }

  private async runAnalyze(insp: any): Promise<void> {
    // R37: submitting a basic inspection with ZERO pillar scores is almost
    // always an accident (the double-tap cascade) — make it deliberate.
    if (insp.form_type === 'basic') {
      const sc = (insp.form && insp.form.scores) || {};
      if (!Object.keys(sc).length && !window.confirm('You haven’t scored the clean yet (the Found/Left pillars on this screen). Submit without scores?')) return;
    }
    // R114: this inspection's photos still on the phone — submitting is the
    // inspector's call, but never a silent one
    const pend = queueCounts(insp.id).thisPending;
    if (pend && !window.confirm(`${pend} photo${pend === 1 ? '' : 's'} for this inspection ${pend === 1 ? 'is' : 'are'} still uploading from this phone.\n\nOK — submit now (${pend === 1 ? 'it still attaches' : 'they still attach'} when ${pend === 1 ? 'it lands' : 'they land'}, but the AI grade may not include ${pend === 1 ? 'it' : 'them'}).\nCancel — wait for the upload to finish.`)) return;
    const ta = this.container.querySelector('#cap-transcript') as HTMLTextAreaElement | null;
    const transcript = (ta?.value ?? this.curTranscript(insp)).trim();
    this.transcriptFinal = transcript;
    const btn = this.container.querySelector('#cap-analyze') as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = 'Submitting…'; }
    // answers held offline must reach the server before the submit reads them
    if (this.formPending[insp.id]) {
      await this.saveForm(insp, {});
      if (this.formPending[insp.id]) {
        if (btn) { btn.disabled = false; btn.textContent = 'Submit Inspection →'; }
        this.showCapMsg('Your scores haven’t saved yet — check your signal and tap Submit again. Nothing was lost.', 'err');
        return;
      }
    }
    this.showCapMsg('Saving your inspection — the AI grades the photos in the background.', 'warn');
    const j = await this.post(`/inspections/${insp.id}/analyze`, { transcript });
    if (!j) { // network/HTTP error already alerted by post()
      if (btn) { btn.disabled = false; btn.textContent = 'Submit Inspection →'; }
      this.showCapMsg('Couldn’t reach the server — check your signal and tap Submit again. Nothing was lost.', 'err');
      return;
    }
    if (j.inspection) this.replaceInsp(j.inspection);
    if (j.ok) {
      // AI-unblock (Jesse: AI trouble "cannot prevent people from doing their
      // jobs"): ok means SUBMITTED — graded, still grading, or AI unavailable.
      // The report card says which; Complete works in every case.
      this.clearCapMsg();
      this.view = 'report';
      this.reportShownAt = Date.now();   // R37: shield the report card's Complete button
      this.draw();
      this.watchAi(insp.id);
    } else {
      // only "nothing to submit yet" lands here now
      if (btn) { btn.disabled = false; btn.textContent = 'Submit Inspection →'; }
      this.showCapMsg(this.esc(j.message || 'Nothing to submit yet.'), 'warn');
    }
  }

  // AI-unblock: while the grade runs in the background, check back every 12 s
  // (up to 15 min) and refresh the report card when it lands. Skips a refresh
  // while the inspector is typing on the card.
  private aiWatchFor = '';
  private watchAi(id: string): void {
    const cur = this.inspections.find(i => i.id === id);
    if (!cur || !cur.ai || cur.ai.status !== 'pending' || this.aiWatchFor === id) return;
    this.aiWatchFor = id;
    const started = Date.now();
    const tick = async () => {
      if (this.aiWatchFor !== id) return;
      if (this.activeId !== id || this.view !== 'report') { this.aiWatchFor = ''; return; }
      const j = await this.get(`/inspections/${encodeURIComponent(id)}/ai`);
      if (j && j.ai && j.ai.status !== 'pending' && j.inspection) {
        const typing = document.activeElement && this.container.contains(document.activeElement) && /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName);
        if (typing) { setTimeout(tick, 4000); return; }
        this.aiWatchFor = '';
        const local = this.inspections.find(i => i.id === id);
        this.replaceInsp(local ? this.mergeAiResult(local, j.inspection) : j.inspection);
        if (this.activeId === id && this.view === 'report') { this.draw(); this.reportShownAt = Date.now(); }   // R37 shield: buttons just moved
        return;
      }
      setTimeout(tick, Date.now() - started > 15 * 60000 ? 60000 : 12000);   // keep checking while the card is open, slower after 15 min
    };
    setTimeout(tick, 12000);
  }
  // A poll snapshot may predate a Complete / an override made on this card:
  // take ONLY the AI's fields from it, keep the local status, and keep a
  // human call on any item.
  private mergeAiResult(local: any, remote: any): any {
    const byKey = new Map((remote.items || []).map((t: any) => [t.key, t]));
    const items = (local.items || []).map((t: any) => {
      const r: any = byKey.get(t.key);
      if (!r) return t;
      const merged: any = { ...t, ai_status: r.ai_status, confidence: r.confidence, evidence_url: r.evidence_url };
      // R120: the stocking annotation (display-only, AI-read items only) rides along
      if (t.source !== 'human' && r.stock_answered) merged.stock_answered = r.stock_answered; else delete merged.stock_answered;
      return t.source === 'human' ? merged : { ...merged, status: r.status, source: r.source, note: r.note };
    });
    return {
      ...local, items,
      ai: remote.ai, grade: remote.grade, issues: this.keepDispositions(local.issues, remote.issues), coverage_gaps: remote.coverage_gaps,
      coverage_stock_answered: remote.coverage_stock_answered,
      logged_issues: remote.logged_issues || local.logged_issues,
      status: local.status === 'complete' || remote.status === 'complete' ? 'complete' : local.status,
      completed_at: local.completed_at || remote.completed_at,
    };
  }
  // AI-unblock: "↻ Try AI grading now" on a report card whose grade didn't run.
  private async retryAi(insp: any): Promise<void> {
    const btn = this.container.querySelector('#rc-ai-retry') as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = 'Grading…'; }
    const j = await this.post(`/inspections/${insp.id}/analyze`, { transcript: this.curTranscript(insp) });
    if (j && j.inspection) this.replaceInsp(j.inspection);
    this.draw();
    if (j && j.ok) this.watchAi(insp.id);
  }

  // R114: the message is STATE (capNote) re-rendered by every draw — a note
  // written just before a redraw used to vanish instantly. Non-sticky ok/warn
  // notes fade after 20 s of redraws; sticky ones stay until replaced/cleared.
  private showCapMsg(html: string, kind: 'err' | 'warn' | 'ok', sticky = false): void {
    this.capNote = { html, kind, for: this.activeId, at: Date.now(), sticky };
    const el = this.container.querySelector('#cap-msg');
    if (el) el.innerHTML = `<div class="cap-msg ${kind}">${html}</div>`;
  }
  private clearCapMsg(): void { this.capNote = null; const el = this.container.querySelector('#cap-msg'); if (el) el.innerHTML = ''; }

  // ── AI report card ──────────────────────────────────────────
  private renderReportCard(insp: any): string {
    const frames = this.frameCount(insp);
    const hasT = !!(insp?.capture?.transcript || '').trim();
    const items: any[] = insp.items || [];
    const issues: any[] = insp.issues || [];
    const gaps: string[] = insp.coverage_gaps || [];
    const completed = insp.status === 'complete';

    // grade ring
    const g = insp.grade;
    const ringPct = g == null ? 0 : Math.max(0, Math.min(100, g));
    const ringColor = this.gradeHex(g);
    const C = 2 * Math.PI * 26;
    const ring = `
      <svg class="rc-ring" width="72" height="72" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r="26" fill="none" stroke="var(--border-soft, #f1f5f9)" stroke-width="8"></circle>
        <circle cx="36" cy="36" r="26" fill="none" stroke="${ringColor}" stroke-width="8" stroke-linecap="round"
          stroke-dasharray="${C}" stroke-dashoffset="${C * (1 - ringPct / 100)}" transform="rotate(-90 36 36)"></circle>
        <text x="36" y="41" text-anchor="middle" font-size="20" font-weight="600" fill="${ringColor}">${g == null ? '—' : g}</text>
      </svg>`;

    // items grouped by area
    const byArea: Record<string, any[]> = {};
    for (const it of items) (byArea[it.area || 'General'] = byArea[it.area || 'General'] || []).push(it);
    const itemsHtml = Object.keys(byArea).map(area => `
      <div class="rc-area-h">${this.esc(area)}</div>
      ${byArea[area].map(it => this.renderReportItem(insp, it, completed)).join('')}
    `).join('');

    // R120: a gap whose stocking parts the inspector confirmed says so
    // ("extinguisher & first aid ticked stocked; exits not shown")
    const covOk: Record<string, string[]> = insp.coverage_stock_answered || {};
    const gapsHtml = gaps.length ? `
      <div class="rc-callout gaps">
        <div class="h">Coverage gaps — not shown in any frame or mentioned</div>
        <ul>${gaps.map(g2 => `<li>${this.esc(g2)}${Array.isArray(covOk[g2]) && covOk[g2].length ? ` <span class="rc-gap-ok">— ${this.esc(this.coverageNote(insp, g2, covOk[g2]))}</span>` : ''}</li>`).join('')}</ul>
      </div>` : '';

    // R26: the inspector's quick-logged tickets render first (photo, room,
    // fixed/follow-up state), then the AI's findings. Routing for tickets
    // happens on the Issues board — the report links there.
    const loggedTix: any[] = insp.logged_issues || [];
    const loggedHtml = loggedTix.map(t => {
      const photo = (Array.isArray(t.photos) && t.photos[0]) || null;
      const state = t.fixed
        ? `<span class="iss-step-badge fixed">✔ fixed by ${this.esc(t.fixed_by || 'inspector')}</span>`
        : t.follow_up
          ? `<span class="iss-step-badge fu">⏰ ${this.esc(String(t.follow_up).replace(/_/g, ' '))}</span>`
          : (t.status === 'resolved' ? '<span class="iss-step-badge fixed">resolved</span>' : '<span class="iss-step-badge open">open</span>');
      return `
        <div class="rc-issue">
          ${photo ? `<img src="${this.esc(photo)}" alt="issue" data-cap-zoom="${this.esc(photo)}">` : ''}
          <div class="rc-issue-main">
            <div class="rc-issue-tags">
              <span class="rc-sev ${this.esc(t.severity || 'med')}">${this.esc(t.severity || 'med')}</span>
              <span class="rc-type">${this.esc(t.category || 'other')}</span>
              ${t.room ? `<span class="rc-type">📍 ${this.esc(t.room)}</span>` : ''}
              <span class="rc-type" title="reported by the inspector">👤 inspector</span>
              ${state}
            </div>
            <div class="rc-issue-note">${this.esc(t.title || '')}${t.note && t.note !== t.title ? ` — ${this.esc(t.note)}` : ''}</div>
            <div class="rc-issue-act"><span class="insp-sub">route it from the Issues board</span></div>
          </div>
        </div>`;
    }).join('');
    // AI-unblock: the grade may still be running, or unavailable — say so plainly.
    const ai = insp.ai || null;
    // the SERVER decides a run is dead (GET /ai and the list read it as
    // failed/'abandoned'); a client-clock guess would offer a duplicate paid
    // run on a slow audit
    const aiPending = !!(ai && ai.status === 'pending');
    const noMedia = !!(ai && ai.status === 'skipped' && ai.kind === 'no_media');
    const aiMissing = !!(ai && (ai.status === 'failed' || (ai.status === 'skipped' && !noMedia)));
    const aiBanner = aiPending
      ? `<div class="rc-callout ai"><b>Submitted ✓</b> — the AI is grading the photos in the background. ${completed ? '' : 'You can complete now; '}the grade and any findings show up here and on the Issues board when it finishes.</div>`
      : noMedia
        ? `<div class="rc-callout ai"><b>Submitted ✓</b> — ${this.esc(ai.note || 'scored by the inspector.')}</div>`
        : aiMissing
          ? `<div class="rc-callout ai"><b>Submitted ✓ — no AI grade yet.</b> ${this.esc(ai.note || 'The AI grade didn’t come through.')} ${completed ? '' : 'Complete the inspection as normal.'}${ai.status === 'failed' ? ` <button class="insp-btn" id="rc-ai-retry" style="margin-left:6px;">↻ Try AI grading now</button>` : ''}</div>`
          : '';
    // R120 (Stacy: "Dismiss doesn't stick"): a dismissed finding leaves the
    // list and folds into a collapsed "Dismissed (N)" group — who, why, when,
    // with reason chips and Undo — instead of staying among the open ones.
    const liveIssues = issues.filter(iss => this.dispositionOf(iss).state !== 'dismissed');
    const dismissedIssues = issues.filter(iss => this.dispositionOf(iss).state === 'dismissed');
    const issuesHtml = (loggedHtml || '') + (liveIssues.length ? liveIssues.map(iss => this.renderIssue(insp, iss)).join('')
      : (loggedTix.length ? '' : `<div class="insp-sub">${dismissedIssues.length ? 'No open issues — everything found was dismissed (below).' : noMedia ? 'No issues reported by the inspector.' : aiPending || aiMissing ? 'None reported by the inspector — AI findings will appear here once it grades.' : 'No issues — none reported by the inspector, none detected by the AI.'}</div>`))
      + this.renderDismissedGroup(insp, dismissedIssues);

    return `
      <div class="cap-wrap">
        <div class="cap-top" style="display:flex; justify-content:space-between; align-items:center; gap:10px;">
          <button class="cap-back" data-cap-back>← Inspections</button>
          <button class="cap-back" data-cap-logissue style="color:var(--accent); font-weight:700;">＋ Log an issue</button>
        </div>
        <div style="margin-bottom:14px;">
          <h2 class="cap-title">${this.esc(insp.listing_name)}</h2>
          <div class="cap-meta">${this.esc(this.tplName(insp.template_id))} · ${insp.inspector ? this.esc(insp.inspector) : 'unassigned'} · ${completed ? this.doneLabel(insp) : `started ${this.fmtDate(insp.created_at)}`}</div>
        </div>
        ${this.phoneQueueBanner()}

        <div class="cap-card card-elev">
          <div class="rc-grade-row">
            ${ring}
            <div class="rc-grade-meta">
              <div class="rc-grade-num" style="color:${ringColor};">${g == null ? '—' : g}<span style="font-size:14px; color:var(--text-muted); font-weight:600;"> / 100</span></div>
              <div class="rc-grade-lbl">${g == null && aiPending ? 'HOME QUALITY — AI grade pending…' : g == null && noMedia ? 'HOME QUALITY — inspector-scored (no photos for AI grading)' : g == null && aiMissing ? 'HOME QUALITY — no AI grade yet' : `HOME QUALITY — AI grade from ${frames} frame${frames === 1 ? '' : 's'}${hasT ? ' + transcript' : ' (no transcript)'} · graded against ${this.esc(this.tplName(insp.template_id))}`}</div>
            </div>
            ${(() => {
              const c = insp.completeness;
              if (!c || c.score == null) return '';
              const cc = c.score >= 90 ? '#16a34a' : c.score >= 70 ? '#b45309' : '#dc2626';
              return `
              <div class="rc-grade-meta" style="border-left:1px solid var(--border); padding-left:16px;">
                <div class="rc-grade-num" style="color:${cc};">${c.score}<span style="font-size:14px; color:var(--text-muted); font-weight:600;">%</span></div>
                <div class="rc-grade-lbl">INSPECTION COMPLETENESS — ${c.pre_clean ? 'pre-clean inspection (property not cleaned yet — “wins” photos not required)' : `how much of the ${this.esc(this.formDefs?.[insp.form_type]?.title || 'form')} was done`}${c.missing?.length ? ` · missing: ${this.esc(c.missing.slice(0, 3).join(' · '))}${c.missing.length > 3 ? ` +${c.missing.length - 3} more` : ''}` : ''}</div>
              </div>`;
            })()}
          </div>
        </div>

        ${aiBanner}
        ${gapsHtml}

        <div class="cap-card card-elev">
          <div class="cap-section-h">Checklist — AI verdict per item (tap to override → marks it human-confirmed)</div>
          ${itemsHtml || '<div class="insp-sub">No items on this inspection.</div>'}
        </div>

        <div class="cap-card card-elev">
          <div class="cap-section-h">Issues found</div>
          ${issuesHtml}
        </div>

        ${completed ? '' : this.renderFocusPanel(insp)}

        ${(() => {   // R41: stocking & safety outcome — gaps are the point of the check
          const g = this.stockGaps(insp);
          if (g) {
            return g.missing.length
              ? `<div class="cap-card card-elev"><div class="cap-section-h">📦 Stocking &amp; safety — ${g.done}/${g.total}</div><div style="font-size:13.5px; color:var(--warning-text,#92400e);"><b>Missing:</b> ${g.missing.map(m => this.esc(m)).join(', ')}</div></div>`
              : `<div class="cap-card card-elev"><div class="cap-section-h">📦 Stocking &amp; safety — all ${g.total} stocked ✓</div></div>`;
          }
          return completed ? '' : `<div class="cap-card card-elev"><div class="cap-section-h">📦 Stocking &amp; safety</div><div class="insp-sub">Not checked this visit — it's the collapsed card on Step 1.</div></div>`;
        })()}

        <div class="rc-actions">
          ${completed ? '<span class="insp-pill" style="background:var(--success-soft); color:var(--success-text, #166534); align-self:center;">complete</span>'
            : `<button class="insp-btn primary" id="rc-complete">Complete inspection</button>`}
          <button class="insp-btn" id="rc-report" title="Open the photo-forward owner report — Print / Save as PDF from there">Owner report <span class="insp-pdf-tag">PDF</span></button>
          ${completed ? '' : `<button class="insp-btn" id="rc-recapture">Capture more</button>`}
          ${!completed && insp.form_type === 'basic' ? `<button class="insp-btn" id="rc-scores">Edit scoring</button>` : ''}
          ${completed ? `<button class="insp-btn" id="rc-reopen" title="Return this inspection to In Progress so scores, items and issues can be edited">Reopen &amp; edit</button>` : ''}
          <span class="insp-link" data-insp-manual="${this.esc(insp.id)}" style="align-self:center;">manual checklist</span>
        </div>
        <div id="cap-msg"></div>
      </div>`;
  }

  private renderReportItem(insp: any, it: any, completed: boolean): string {
    const status = it.status || 'na';
    const conf = it.confidence == null ? null : Math.round(it.confidence * 100);
    const ev = it.evidence_url;
    const evHtml = ev
      ? `<img src="${this.esc(ev)}" alt="evidence" data-cap-zoom="${this.esc(ev)}">`
      : `<div class="none">no cited<br>photo</div>`;
    const seg = ITEM_STATES.map(s =>
      `<button class="${s} ${status === s ? 'on' : ''}" data-rc-item="${this.esc(insp.id)}|${this.esc(it.key)}|${s}" ${completed ? 'disabled' : ''}>${s}</button>`).join('');
    const src = it.source === 'human' ? '<span class="rc-src">overridden</span>' : (it.source === 'ai' ? '<span class="rc-conf">AI</span>' : '');
    const confPill = conf == null ? '' : `<span class="rc-conf" title="AI confidence">${conf}% conf</span>`;
    return `
      <div class="rc-item">
        <div class="rc-ev">${evHtml}</div>
        <div class="rc-item-main">
          <div class="rc-item-ttl">${this.esc(it.title)}</div>
          ${it.note ? `<div class="rc-item-note">${this.esc(it.note)}</div>` : ''}
          ${this.stockAnsweredLine(insp, it)}
          <div class="rc-item-row2">
            <span class="rc-seg">${seg}</span>
            ${confPill}
            ${src}
          </div>
        </div>
      </div>`;
  }

  // R120 (team: "flashlight"): the AI's "not visible" note on an item the
  // inspector's stocking check settles — display only, the verdict stands.
  private stockAnsweredLine(insp: any, it: any): string {
    const keys: string[] = (it && it.stock_answered && Array.isArray(it.stock_answered.keys)) ? it.stock_answered.keys : [];
    if (!keys.length) return '';
    const checks = (insp.form && insp.form.checks) || {};
    const ticked = keys.every(k => checks[k] === true);
    return `<div class="rc-item-stock">✓ ${ticked ? 'Inspector ticked stocked' : 'Confirmed on site'}: ${this.esc(labelsFor(keys).join(', '))}</div>`;
  }
  private coverageNote(insp: any, title: string, keys: string[]): string {
    const sp = splitTitle(title);
    const what = (sp.phrases.length ? sp.phrases : labelsFor(keys)).map(x => x.toLowerCase()).join(' & ');
    const checks = (insp.form && insp.form.checks) || {};
    const how = keys.every(k => checks[k] === true) ? 'ticked stocked' : 'confirmed on site';
    return `${what} ${how}${sp.rest ? `; ${sp.rest.toLowerCase()} not shown` : ''}`;
  }

  // R120 (contract C2): a finding's state — the server's iss.disposition when
  // GET /inspections attached it (it merges the Issues board's dismissals),
  // else derived from the finding's own action. A legacy stamp
  // {kind, ref_id, at, note} reads as via 'legacy' ("not recorded").
  private dispositionOf(iss: any): { state: string; by: string | null; by_id: string | null; at: string | null; reason: string | null; reason_code: string | null; via: string } {
    const d = iss && iss.disposition;
    if (d && d.state) return { state: String(d.state), by: d.by || null, by_id: d.by_id ? String(d.by_id) : null, at: d.at || null, reason: d.reason || null, reason_code: d.reason_code || null, via: String(d.via || 'legacy') };
    const a = iss && iss.action;
    if (a && a.kind === 'dismissed') return { state: 'dismissed', by: a.by || null, by_id: a.by_id ? String(a.by_id) : null, at: a.at || null, reason: a.reason || null, reason_code: a.reason_code || null, via: String(a.via || 'legacy') };
    const st = iss && iss.fixed ? 'fixed' : (a && a.kind ? 'actioned' : 'open');
    return { state: st, by: null, by_id: null, at: null, reason: null, reason_code: null, via: 'inspection' };
  }
  // Review R120 #14 (D11, as the server's findingState.mayUndoNative): who may
  // Undo, or change the reason on, a native dismissal. The office any; anyone
  // else only their OWN (by_id = their login) and the system's (the stocking
  // check, a duplicate) on an inspection that is theirs (their name or an
  // /api/me inspector_names roster name). A pre-R120 stamp records no one, so
  // it is the office's. Unknown (no login id, no roster names) → not offered:
  // the server would refuse it. Review R120 #14 round 3: the system's is
  // theirs when the job was theirs at STAMP time (answered_by.inspector, the
  // server's findingState.autoDismissalOwner; '' = unassigned = the office's),
  // so a later reassignment never moves the Undo; a legacy stamp without it
  // reads the inspection's current inspector.
  private mayUndoDismissal(insp: any, d: { via: string; by_id: string | null }, iss?: any): boolean {
    if (d.via === 'board') return false;   // the overlay's: the office restores it on the board
    if (InspectionsPage.isOfficeRole()) return true;
    if (d.via === 'auto') {
      const norm = (v: any) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim().toLowerCase();
      const ab = iss && iss.answered_by && typeof iss.answered_by === 'object' ? iss.answered_by : null;
      const who = norm(ab && Object.prototype.hasOwnProperty.call(ab, 'inspector') ? ab.inspector : (insp && insp.inspector));
      const mine = new Set([InspectionsPage.profileName(), ...this.myInspectorNames].map(norm).filter(Boolean));
      return !!who && mine.has(who);
    }
    return !!(d.by_id && this.myUserId && d.by_id === this.myUserId);
  }
  private static readonly DISMISS_REASONS: Array<[string, string]> = [
    ['present', 'Present'], ['not_issue', 'Not an issue'], ['duplicate', 'Duplicate'], ['other', 'Other'],
  ];
  private dismissedWho(d: { by: string | null; via: string; reason_code?: string | null }): string {
    // the system: your ticks answered it, or (review R120 #1) it is a reworded
    // copy of a finding someone already reviewed
    if (d.via === 'auto') return d.reason_code === 'duplicate' ? 'automatically' : 'your stocking check';
    if (d.via === 'board') return 'the office';
    if (d.via === 'legacy' || !d.by || d.by === 'system') return 'not recorded';
    const person = String(d.by).split(' — ').pop() || String(d.by);   // a shared login reads "Company — Person"
    return person.trim().split(/\s+/)[0] || 'not recorded';
  }
  private dismissReasonText(d: { reason: string | null; reason_code: string | null; via: string }): string {
    const byCode: Record<string, string> = { present: 'Visually checked — present', not_issue: 'Not an issue', duplicate: 'Duplicate', other: 'Other' };
    return String(d.reason || (d.reason_code ? byCode[d.reason_code] || '' : '')).trim();
  }
  private dismissedOpen = new Set<string>();   // inspection ids whose "Dismissed (N)" group is open
  private justDismissed: string | null = null; // the finding just dismissed — its row is highlighted
  private renderDismissedGroup(insp: any, list: any[]): string {
    if (!list.length) return '';
    const id = this.esc(insp.id);
    const rows = list.map(iss => {
      const d = this.dispositionOf(iss);
      const iid = this.esc(iss.id);
      const when = d.at ? this.fmtShort(this.localDay(d.at)) : '';
      const reason = this.dismissReasonText(d);
      const meta = ['Dismissed', this.dismissedWho(d), reason, when].filter(Boolean).map(x => this.esc(x)).join(' · ');
      // reasons are the inspector's to change on their own dismissals; the
      // system's (auto) and the office's (board) carry their own. Review R120
      // #14: chips and Undo only where this login may undo it (D11)
      const may = this.mayUndoDismissal(insp, d, iss);
      const chips = may && (d.via === 'inspection' || d.via === 'legacy')
        ? `<span class="rc-reasons" role="group" aria-label="Why was it dismissed?">${InspectionsPage.DISMISS_REASONS.map(([code, label]) =>
            `<button type="button" class="rc-reason ${d.reason_code === code ? 'on' : ''}" aria-pressed="${d.reason_code === code ? 'true' : 'false'}" data-rc-reason="${id}|${iid}|${code}">${label}</button>`).join('')}</span>`
        : '';
      const undo = may
        ? `<button type="button" class="rc-undo" data-rc-undo="${id}|${iid}" title="Put this finding back on the open list">Undo</button>`
        : `<span class="rc-dis-office">ask the office to restore it</span>`;
      return `
        <div class="rc-dis-row ${this.justDismissed === iss.id ? 'just' : ''}" data-rc-dis-row="${iid}">
          <div class="rc-dis-note"><span class="rc-sev ${this.esc(iss.severity || 'low')}">${this.esc(iss.severity || 'low')}</span> ${this.esc(iss.note || '')}</div>
          <div class="rc-dis-meta">${meta}</div>
          <div class="rc-dis-acts">${chips}${undo}</div>
        </div>`;
    }).join('');
    return `
      <details class="rc-dis" data-rc-dis="${id}" ${this.dismissedOpen.has(insp.id) ? 'open' : ''}>
        <summary class="rc-dis-h">Dismissed (${list.length})</summary>
        ${rows}
      </details>`;
  }

  private renderIssue(insp: any, iss: any): string {
    const ev = iss.evidence_url
      ? `<img src="${this.esc(iss.evidence_url)}" alt="issue" data-cap-zoom="${this.esc(iss.evidence_url)}">`
      : '';
    // Already actioned → show the result + a link to where the work lives.
    const act = iss.action;
    const actedHtml = act ? this.renderIssueActed(act) : this.renderIssueButtons(insp, iss);
    return `
      <div class="rc-issue">
        ${ev}
        <div class="rc-issue-main">
          <div class="rc-issue-tags">
            <span class="rc-sev ${this.esc(iss.severity || 'low')}">${this.esc(iss.severity || 'low')}</span>
            <span class="rc-type">${this.esc(iss.type || 'maintenance')}</span>
            ${iss.source === 'inspector' ? '<span class="rc-type" title="reported by the inspector">👤 inspector</span>' : ''}
            ${iss.fixed ? '<span class="iss-step-badge fixed">✔ fixed during inspection</span>' : ''}
            ${!iss.fixed && iss.follow_up ? `<span class="iss-step-badge fu">⏰ ${this.esc(String(iss.follow_up).replace(/_/g, ' '))}</span>` : ''}
          </div>
          <div class="rc-issue-note">${this.esc(iss.note || '')}</div>
          <div class="rc-issue-act">${actedHtml}</div>
        </div>
      </div>`;
  }

  // Read-only render of an issue that already carries an action (issue.action).
  private renderIssueActed(act: any): string {
    const kind = act.kind;
    const note = act.note ? `<div class="rc-issue-note" style="color:var(--text-muted); margin-top:4px;">“${this.esc(act.note)}”</div>` : '';
    if (kind === 'wo') {
      return `<div class="rc-acted"><span class="rc-acted-mark">✓ Work order created</span> <span class="rc-acted-link" data-rc-go="maintenance">open in Maintenance →</span></div>${note}`;
    }
    if (kind === 'restock') {
      return `<div class="rc-acted"><span class="rc-acted-mark">✓ Restock requested</span> <span class="rc-acted-link" data-rc-go="supplies">open in Supplies →</span></div>${note}`;
    }
    if (kind === 'cleaner') {   // R104 #6: a staging miss sent to the cleaner
      return `<div class="rc-acted"><span class="rc-acted-mark">✓ Sent to the cleaner — must photograph on the next clean${act.strike ? ' · ⚠ strike candidate filed' : ''}</span> <span class="rc-acted-link" data-rc-go="hk-insp" data-rc-tab="housekeeping">next clean →</span></div>${note}`;   // review R104 #24: hk-insp hub, Housekeeping tab
    }
    if (kind === 'owner_note') {
      return `<div class="rc-acted"><span class="rc-acted-mark">✓ Owner note saved</span></div>${note}`;
    }
    if (kind === 'strike') {
      return `<div class="rc-acted"><span class="rc-acted-mark">⚠ Strike candidate flagged</span> <span class="rc-acted-link" data-rc-go="hk-insp" data-rc-tab="performance">review queue →</span></div>${note}`;
    }
    // dismissed
    return `<div class="rc-acted dismissed"><span class="rc-acted-mark">✓ Dismissed</span></div>${note}`;
  }

  // Action buttons for an un-actioned issue. The suggested_action button is primary.
  private renderIssueButtons(insp: any, iss: any): string {
    const sa: IssueAction = (iss.suggested_action || 'none');
    const id = this.esc(insp.id);
    const iid = this.esc(iss.id);
    const btn = (action: string, label: string, primary: boolean) =>
      `<button class="rc-act-btn ${primary ? 'primary' : ''}" data-rc-action="${id}|${iid}|${action}">${label}</button>`;
    const office = ['admin', 'director', 'manager', 'remote_ops'].includes(String(sessionStorage.getItem('userRole') || ''));
    // R104 #6 (Jesse: "6 of 8 towels after a clean is a cleaning miss, not a
    // restock — we over-order"): a supply finding asks the closet question and
    // the primary button follows the cause — the cleaner for a staging miss,
    // Restock only for a confirmed stock-out.
    const isSupply = String(iss.type || '') === 'supply' || iss.cause;
    const miss = iss.cause === 'cleaning_miss';
    const chip = (val: string, label: string, on: boolean) =>
      `<button class="rc-closet ${on ? 'on' : ''}" data-rc-closet="${id}|${iid}|${val}" title="${val === 'stocked' ? 'The closet / backstock had it — the cleaner did not stage it (cleaning miss)' : val === 'empty' ? 'Genuinely out — order it (stock-out)' : 'Not checked'}">${label}</button>`;
    const closet = isSupply ? `
      <div class="rc-closet-row">
        <span class="rc-closet-l">${miss ? '🧹 cleaning miss' : iss.cause === 'stock_out' ? '📦 stock-out' : 'staging miss or stock-out?'}${iss.expected != null && iss.observed != null ? ` · ${this.esc(iss.observed)} of ${this.esc(iss.expected)}` : ''}</span>
        <span class="rc-closet-chips">closet: ${chip('stocked', 'stocked', iss.closet_stocked === true)}${chip('empty', 'empty', iss.closet_stocked === false)}${chip('unknown', "didn't check", iss.closet_stocked == null)}</span>
        <span class="rc-acted-link" data-rc-guide="1" title="Why am I asked this?">why?</span>
      </div>` : '';
    return `${closet}
      <div class="rc-act-row">
        ${isSupply ? btn('cleaner', '→ Cleaner (next clean)', sa === 'cleaner' || miss) : ''}
        ${btn('wo', 'Create work order', sa === 'wo')}
        ${btn('restock', 'Restock', sa === 'restock' && !miss)}
        ${btn('owner_note', 'Owner note', sa === 'owner_note')}
        ${office ? btn('claim', '⚖ Claim', false) : ''}
        ${btn('strike', '⚠ Strike', false)}
        ${btn('dismiss', 'Dismiss', false)}
      </div>`;
  }

  private bindReport(insp: any): void {
    const root = this.container;
    root.querySelector('[data-cap-back]')?.addEventListener('click', () => { this.exitCapture(); this.draw(); });
    root.querySelector('[data-cap-logissue]')?.addEventListener('click', () => {
      QuickLogIssue.open({
        sourceContext: 'inspection', listingId: String(insp.listing_id), listingName: insp.listing_name,
        inspectionId: insp.id, createdBy: insp.inspector || '',
        // R26: refresh so tickets added via the modal appear on this view too
        onLogged: async (n) => { if (n) { try { await this.reloadInspections(); } catch (_) { /* stale view ok */ } this.draw(); } },
      });
    });
    root.querySelector('[data-insp-manual]')?.addEventListener('click', () => { this.openId = insp.id; this.renderDetailModal(); });

    // override an item — flips source to 'human' via POST /inspections/:id
    root.querySelectorAll<HTMLElement>('[data-rc-item]').forEach(el =>
      el.addEventListener('click', async () => {
        const [id, key, status] = el.dataset.rcItem!.split('|');
        const j = await this.post(`/inspections/${id}`, { items: [{ key, status }] });
        if (j?.inspection) { this.replaceInsp(j.inspection); this.draw(); }
      }));

    // evidence lightbox
    root.querySelectorAll<HTMLElement>('[data-cap-zoom]').forEach(el =>
      el.addEventListener('click', () => this.openLightbox(el.dataset.capZoom!)));

    // issue actions — spawn a WO / restock / owner note / dismiss (Phase 2)
    root.querySelectorAll<HTMLElement>('[data-rc-action]').forEach(el =>
      el.addEventListener('click', () => {
        const [id, issueId, action] = el.dataset.rcAction!.split('|');
        this.runIssueAction(id, issueId, action, el as HTMLButtonElement);
      }));
    // R120 (Stacy: "Dismiss doesn't stick"): the Dismissed group — open state,
    // optional reason chips (re-post the dismiss with a reason) and Undo.
    root.querySelectorAll<HTMLDetailsElement>('details[data-rc-dis]').forEach(el =>
      el.addEventListener('toggle', () => { if (el.open) this.dismissedOpen.add(el.dataset.rcDis!); else this.dismissedOpen.delete(el.dataset.rcDis!); }));
    root.querySelectorAll<HTMLButtonElement>('[data-rc-reason]').forEach(el =>
      el.addEventListener('click', () => {
        const [id, issueId, code] = el.dataset.rcReason!.split('|');
        if (el.classList.contains('on')) return;   // the same reason again is a no-op
        void this.postFindingAction(id, issueId, { action: 'dismiss', reason_code: code }, el);
      }));
    root.querySelectorAll<HTMLButtonElement>('[data-rc-undo]').forEach(el =>
      el.addEventListener('click', () => {
        const [id, issueId] = el.dataset.rcUndo!.split('|');
        void this.postFindingAction(id, issueId, { action: 'restore' }, el);
      }));
    // R104 #6: the closet question sets the cause (stocked → cleaning miss; empty → stock-out)
    root.querySelectorAll<HTMLElement>('[data-rc-closet]').forEach(el =>
      el.addEventListener('click', async () => {
        const [id, issueId, val] = el.dataset.rcCloset!.split('|');
        const closet_stocked = val === 'stocked' ? true : val === 'empty' ? false : null;
        const j = await this.post(`/inspections/${id}/issue-update`, { issue_id: issueId, closet_stocked, by: this.activeInsp()?.inspector || '' });
        if (j?.issues) { this.adoptIssues(this.inspections.find((i: any) => i.id === id), j.issues); this.draw(); }
      }));
    root.querySelectorAll<HTMLElement>('[data-rc-guide]').forEach(el =>
      el.addEventListener('click', () => void openApiDoc(`${API}/supplies/cleaning-miss-guide`, 'Cleaning miss vs stock-out')));
    // links from an already-actioned issue → jump to where the work lives.
    // data-rc-tab preselects a hub tab via the one-shot hub_tab key (R58 fix:
    // 'hk-performance' was not a page id — the review queue is the
    // 'performance' TAB inside the hk-insp hub).
    root.querySelectorAll<HTMLElement>('[data-rc-go]').forEach(el =>
      el.addEventListener('click', () => {
        if (el.dataset.rcTab) { try { sessionStorage.setItem('hub_tab', el.dataset.rcTab); } catch { /* private mode */ } }
        document.dispatchEvent(new CustomEvent('app:navigate', { detail: el.dataset.rcGo }));
      }));

    // Feature B — tick a required-this-visit cadence item in the report card too.
    root.querySelectorAll<HTMLInputElement>('[data-foc-tick]').forEach(inp =>
      inp.addEventListener('change', () => {
        const id = inp.dataset.focTick!;
        if (inp.checked) this.focusDone.add(id); else this.focusDone.delete(id);
        inp.closest('.foc-row')?.classList.toggle('on', inp.checked);
      }));
    root.querySelectorAll<HTMLElement>('[data-foc-skip]').forEach(btn =>
      btn.addEventListener('click', () => {
        const id = btn.dataset.focSkip!;
        if (this.focusSkip.has(id)) this.focusSkip.delete(id);
        else { this.focusSkip.add(id); this.focusDone.delete(id); }
        this.draw();
      }));
    // R58: WO actions on focus items in the report-card view too.
    root.querySelectorAll<HTMLElement>('[data-foc-photo]').forEach(img =>
      img.addEventListener('click', () => openExternal(img.dataset.focPhoto!, '_blank')));
    root.querySelectorAll<HTMLButtonElement>('[data-foc-wodone]').forEach(btn =>
      btn.addEventListener('click', () => void this.focusWoAction(btn.dataset.focWodone!, 'wo-done', btn)));
    root.querySelectorAll<HTMLButtonElement>('[data-foc-assignmn]').forEach(btn =>
      btn.addEventListener('click', () => void this.focusWoAction(btn.dataset.focAssignmn!, 'assign-maintenance', btn)));

    // capture more → back to capture view (stamp the shield: the capture
    // screen's sticky CTA lands where this button was — R37 double-tap class)
    root.querySelector('#rc-recapture')?.addEventListener('click', () => { this.view = 'capture'; this.captureStep = 1; this.stepChangedAt = Date.now(); this.draw(); });

    // R37: straight back to the SCORING screen — the report card used to be a
    // one-way door for scores (Capture more always lands on step 1).
    root.querySelector('#rc-scores')?.addEventListener('click', () => { this.view = 'capture'; this.captureStep = 2; this.stepChangedAt = Date.now(); this.draw(); });

    // R37: reopen a completed inspection ("we need a way to go back and
    // update/edit these tasks") — server flips it to in_progress preserving
    // grade/items/issues/scores, then we land on the scoring screen.
    root.querySelector('#rc-reopen')?.addEventListener('click', async () => {
      if (!window.confirm('Reopen this inspection for editing? It returns to In Progress (and drops out of stats) until you complete it again.')) return;
      const j = await this.post(`/inspections/${insp.id}/reopen`, { by: InspectionsPage.profileName() });
      if (j?.inspection) {
        this.replaceInsp(j.inspection);
        this.view = 'capture';
        this.captureStep = insp.form_type === 'basic' ? 2 : 1;
        this.stepChangedAt = Date.now();
        await this.reloadStatsAndBoard();
        this.draw();
      }
    });

    // complete — reconcile the cadence focus set: POST /:id/complete carries the
    // ticked focus ids; the server marks satisfied, records misses (ding +
    // re-check ticket), and flips the inspection to 'complete'.
    root.querySelector('#rc-ai-retry')?.addEventListener('click', () => void this.retryAi(insp));
    if (insp.ai && insp.ai.status === 'pending') this.watchAi(insp.id);   // re-opened while it grades
    root.querySelector('#rc-complete')?.addEventListener('click', async () => {
      // R37: ignore the tail of a double-tap that just submitted the analyze,
      // and make completing with no scores a deliberate choice.
      if (Date.now() - this.reportShownAt < 700) return;
      if (this.formPending[insp.id]) {   // held offline answers go first
        await this.saveForm(insp, {});
        if (this.formPending[insp.id]) { alert('Your answers haven’t saved yet — check your signal and tap Complete again. Nothing was lost.'); return; }
      }
      if (insp.form_type === 'basic') {
        const sc = (insp.form && insp.form.scores) || {};
        if (!Object.keys(sc).length && !window.confirm('No pillar scores recorded for this clean — complete anyway? (“Edit scoring” takes you back.)')) return;
      }
      const focus_done = [...this.focusDone];
      const focus_skip = [...this.focusSkip];
      const j = await this.post(`/inspections/${insp.id}/complete`, { focus_done, focus_skip });
      if (j?.inspection) {
        this.replaceInsp(j.inspection);
        // Invalidate the cached focus set (last_done / overdue have moved).
        this.focusFor = null; this.focus = null; this.focusDone = new Set(); this.focusSkip = new Set();
        await this.reloadStatsAndBoard();
        this.draw();
        const r = j.reconciled || { satisfied: [], missed: [] };
        const sat = (r.satisfied || []).length;
        const miss = (r.missed || []).length;
        if (sat || miss) {
          const parts: string[] = [];
          if (sat) parts.push(`${sat} check${sat === 1 ? '' : 's'} logged`);
          if (miss) parts.push(`${miss} missed → ticket${miss === 1 ? '' : 's'} opened`);
          this.showCapMsg(this.esc(parts.join(' · ')), miss ? 'warn' : 'err');
          // 'err' kind is red; use warn (amber) when nothing missed isn't ideal —
          // override to a success tone via a one-off success message.
          if (!miss) {
            const el = this.container.querySelector('#cap-msg');
            if (el) el.innerHTML = `<div class="cap-msg ok">${this.esc(parts.join(' · '))}</div>`;
          }
        }
      }
    });

    // owner report
    root.querySelector('#rc-report')?.addEventListener('click', () => {
      void openApiDoc(`${API}/inspections/${insp.id}/report`, 'Inspection report');
    });
  }

  // POST /inspections/:id/issue-action → mark the issue actioned inline.
  private async runIssueAction(id: string, issueId: string, action: string, btn: HTMLButtonElement): Promise<void> {
    // R58: 'claim' rides the Damages & Claims flow, not issue-action — the
    // board_id composite 'insp:<inspection>:<issue>' is what /claims/from-issue
    // already accepts.
    if (action === 'claim') {
      if (!confirm('Open a damage claim for this issue?')) return;
      btn.disabled = true;
      const r = await fetch(`${API}/claims/from-issue`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ board_id: `insp:${id}:${issueId}`, by: (() => { try { return String(JSON.parse(sessionStorage.getItem('userProfile') || '{}').email || ''); } catch { return ''; } })() }),
      }).catch(() => null);
      btn.disabled = false;
      const j = r ? await r.json().catch(() => ({})) : null;
      if (!r || !r.ok) { alert((j && j.error) || 'Couldn\'t open the claim — try again.'); return; }
      btn.textContent = '✓ Claim opened';
      return;
    }
    const body: any = { issue_id: issueId, action };
    if (action === 'owner_note') {
      // Optional free-text (window.prompt, like editGoal elsewhere). Cancel aborts.
      const insp = this.activeInsp();
      const iss = (insp?.issues || []).find((x: any) => x.id === issueId);
      const note = window.prompt('Owner note (leave as-is to use the issue text):', iss?.note || '');
      if (note === null) return; // cancelled
      if (note.trim()) body.note = note.trim();
    }
    if (action === 'strike') {
      if (!confirm('Flag this as a strike candidate against the cleaner? An admin reviews it before any strike is recorded.')) return;
    }
    // R104 #6: the cleaner path can file a strike candidate too; ordering a
    // staging miss asks first (and the server refuses without force_order)
    if (action === 'cleaner') {
      body.strike = confirm('Send this to the cleaner as a must-photograph item on the next clean.\n\nAlso flag a strike candidate for the cleaner? (an admin reviews it before anything sticks)\n\nOK = yes, also flag · Cancel = just send it');
      body.by = this.activeInsp()?.inspector || '';
    }
    if (action === 'restock') {
      const insp0 = this.activeInsp();
      const iss0 = (insp0?.issues || []).find((x: any) => x.id === issueId);
      if (iss0?.cause === 'cleaning_miss') {
        if (!confirm('This looks like a staging miss — the cleaner sets this item out every turn. Order anyway (closet confirmed empty)?')) return;
        body.force_order = true;
      }
    }
    // R120 (D10): Dismiss stays ONE tap — the default reason "Visually
    // checked — present" rides along; the reason chips on the Dismissed row
    // can change it afterwards. Never a blocking prompt.
    if (action === 'dismiss') body.reason_code = 'present';
    // disable the whole row while in flight
    const row = btn.closest('.rc-act-row');
    row?.querySelectorAll('button').forEach(b => ((b as HTMLButtonElement).disabled = true));
    const orig = btn.textContent;
    btn.textContent = action === 'dismiss' ? 'Dismissing…' : 'Working…';
    const landed = (j: any) => {
      this.applyActionResponse(id, issueId, j);
      if (action === 'dismiss') { this.dismissedOpen.add(id); this.justDismissed = issueId; }
      // Re-render the report card so the issue now shows its actioned state.
      this.draw();
    };
    // a 409 cleaning-miss refusal surfaces as post()'s alert (mark the closet
    // chip, then retry); the board unreadable offers Try again (round 6)
    const j = await this.post(`/inspections/${id}/issue-action`, body, landed);
    if (!j || !j.ok) {
      // post() already said why — restore the row
      row?.querySelectorAll('button').forEach(b => ((b as HTMLButtonElement).disabled = false));
      btn.textContent = orig;
      return;
    }
    landed(j);
  }
  // The issue-action answer: the stored inspection (contract C3 also returns
  // the one issue) — either refreshes the local copy.
  private applyActionResponse(id: string, issueId: string, j: any): void {
    if (j && j.inspection) { this.replaceInsp(j.inspection); return; }
    const insp = this.inspections.find(i => i.id === id);
    if (!insp || !j || !j.issue) return;
    insp.issues = (insp.issues || []).map((x: any) => (x && x.id === issueId ? j.issue : x));
    this.replaceInsp(insp);
  }
  // R120: a reason chip or Undo on a dismissed finding (contract C3:
  // { action: 'dismiss', reason_code } | { action: 'restore' }). A refusal
  // (e.g. 409 — dismissed by the office) is post()'s alert; nothing changes.
  private async postFindingAction(id: string, issueId: string, body: any, btn: HTMLButtonElement): Promise<void> {
    const row = btn.closest('.rc-dis-acts');
    row?.querySelectorAll('button').forEach(b => ((b as HTMLButtonElement).disabled = true));
    const landed = (j: any) => {
      this.applyActionResponse(id, issueId, j);
      this.justDismissed = body.action === 'restore' ? null : issueId;
      this.draw();
    };
    const j = await this.post(`/inspections/${id}/issue-action`, { issue_id: issueId, ...body }, landed);   // board unreadable: Try again (round 6)
    if (!j || !j.ok) { row?.querySelectorAll('button').forEach(b => ((b as HTMLButtonElement).disabled = false)); return; }
    landed(j);
  }

  private openLightbox(url: string): void {
    const box = document.createElement('div');
    box.className = 'cap-lightbox';
    box.innerHTML = `<img src="${this.esc(url)}" alt="evidence">`;
    box.addEventListener('click', () => box.remove());
    this.container.appendChild(box);
  }

  // ── R35 #10: cleaner's documentation panel on the capture surface ─────────
  // The day's clean for this listing: reference/quirk checks the cleaner
  // completed + their per-item photos, each with an inspector Verify toggle.
  // DOM-only updates (like onCount) — a draw() here would remount the embed.
  private cleanCtx: any = null;
  private cleanCtxFor: string | null = null;
  private async loadCleanContext(inspId: string, root: HTMLElement): Promise<void> {
    const host = root.querySelector<HTMLElement>('#insp-cleanctx');
    if (!host) return;
    // Paint instantly from cache, then ALWAYS refetch — the cleaner may still
    // be uploading item photos while the inspector works. Failures keep the
    // last good data and are never cached as "no context".
    if (this.cleanCtx && this.cleanCtxFor === inspId) this.paintCleanContext(inspId, host);
    const j = await fetch(`${API}/inspections/${inspId}/clean-context`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (this.activeId !== inspId) return;   // navigated away while loading
    if (j) {
      this.cleanCtx = j; this.cleanCtxFor = inspId;
      const h2 = root.querySelector<HTMLElement>('#insp-cleanctx');
      if (h2) this.paintCleanContext(inspId, h2);
    }
  }
  private paintCleanContext(inspId: string, host: HTMLElement): void {
    const j = this.cleanCtx;
    const turn = j && j.turn;
    if (!turn) { host.innerHTML = ''; return; }
    const done = new Set<string>(Array.isArray(turn.checklist_done) ? turn.checklist_done : []);
    const photos: Record<string, string[]> = turn.checklist_photos || {};
    const verified: Record<string, any> = (j.ref_verified || {});
    // Show the property-specific work: quirk/reference items plus anything photographed.
    const keys = [...new Set([...done, ...Object.keys(photos)])]
      .filter(k => k.startsWith('⚑') || k.startsWith('📸') || (photos[k] || []).length);
    if (!keys.length) { host.innerHTML = ''; return; }
    // A verify is scoped to ITS clean — item strings repeat across cleans.
    const vFor = (k: string) => { const v = verified[k]; return v && (!v.turn_id || v.turn_id === turn.id) ? v : null; };
    const nVerified = keys.filter(k => vFor(k)).length;
    const rows = keys.map(k => {
      const ph = photos[k] || [];
      const v = vFor(k);
      return `<div class="iss-step-row" style="align-items:flex-start;">
        <div class="iss-step-main">
          <div style="font-size:13.5px; ${done.has(k) ? '' : 'color:var(--text-muted);'}">${done.has(k) ? '✅' : '◻'} ${this.esc(k)}</div>
          ${ph.length ? `<div style="display:flex; gap:6px; flex-wrap:wrap; margin-top:6px;">${ph.map(u => `<img src="${this.esc(u)}" data-ctx-zoom="${this.esc(u)}" style="width:64px; height:64px; object-fit:cover; border-radius:8px; border:1px solid var(--border); cursor:zoom-in;">`).join('')}</div>` : ''}
          ${v ? `<div style="font-size:11px; color:var(--success-text,#166534); margin-top:3px;">✓ verified${v.by ? ` by ${this.esc(v.by)}` : ''}</div>` : ''}
        </div>
        <button class="insp-btn ${v ? 'primary' : ''}" data-ctx-verify="${this.esc(k)}">${v ? '✓ Verified' : 'Verify'}</button>
      </div>`;
    }).join('');
    host.innerHTML = `
      <div class="cap-card card-elev">
        <div class="cap-section-h">Cleaner's documentation — clean of ${this.esc(turn.check_out)}${turn.cleaners && turn.cleaners.length ? ` · ${this.esc(turn.cleaners.join(' + '))}` : ''} · ${nVerified}/${keys.length} verified</div>
        <div class="cap-hint" style="margin-bottom:8px;">Checks and photos from the clean — tap a photo to inspect it, Verify to sign off. Anything unverified still needs your eyes in person.</div>
        ${rows}
      </div>`;
    host.querySelectorAll<HTMLElement>('[data-ctx-zoom]').forEach(el => el.addEventListener('click', () => this.openLightbox(el.dataset.ctxZoom!)));
    host.querySelectorAll<HTMLElement>('[data-ctx-verify]').forEach(el => el.addEventListener('click', async () => {
      const item = el.dataset.ctxVerify!;
      const cur = !!vFor(item);
      let by = '';
      try { by = JSON.parse(sessionStorage.getItem('userProfile') || '{}').full_name || ''; } catch (_) { /* anonymous */ }
      const r = await fetch(`${API}/inspections/${inspId}/ref-verify`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ item, verified: !cur, by, turn_id: turn.id }),
      }).then(x => x.ok ? x.json() : null).catch(() => null);
      if (r) { this.cleanCtx.ref_verified = r.ref_verified || {}; this.paintCleanContext(inspId, host); }
    }));
  }

  // ════════════════════════════════════════════════════════════
  //  CONDITION HISTORY (Phase 3) — per-property grade trend,
  //  recurring issues, and the PDS appliance roster.
  // ════════════════════════════════════════════════════════════

  // Grade-trend sparkline — mirrors MoneyOverview.spark() (polyline + last dot).
  private gradeSpark(vals: number[]): string {
    const fin = vals.filter(v => Number.isFinite(v));
    if (fin.length < 2 || vals.length < 2) return '';
    const w = 160, h = 40, pad = 4;
    const mn = Math.min(...fin), mx = Math.max(...fin), rng = (mx - mn) || 1;
    const xy = (v: number, i: number): [number, number] =>
      [pad + i * (w - 2 * pad) / (vals.length - 1), h - pad - ((v - mn) / rng) * (h - 2 * pad)];
    const pts = vals.map((v, i) => xy(v, i).map(n => n.toFixed(1)).join(',')).join(' ');
    const [lx, ly] = xy(vals[vals.length - 1], vals.length - 1);
    const last = vals[vals.length - 1];
    const dotColor = this.gradeHex(last);
    return `<svg class="ch-spark" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" aria-hidden="true"><polyline points="${pts}" fill="none" stroke="var(--text-muted)" stroke-width="1.5" stroke-linejoin="round"/><circle cx="${lx.toFixed(1)}" cy="${ly.toFixed(1)}" r="3" fill="${dotColor}"/></svg>`;
  }

  // Open the history modal for a property and fetch GET /history/:listing_id.
  private async openHistory(listing_id: string, name: string): Promise<void> {
    this.historyFor = { listing_id, name };
    this.history = null;
    this.historyLoading = true;
    this.renderHistoryModal();
    const j = await this.get(`/inspections/history/${encodeURIComponent(listing_id)}`);
    this.history = j;
    this.historyLoading = false;
    this.renderHistoryModal();
  }

  private closeHistory(): void {
    this.historyFor = null;
    this.history = null;
    this.historyLoading = false;
    document.getElementById('insp-history-modal')?.remove();
  }

  private renderHistoryModal(): void {
    document.getElementById('insp-history-modal')?.remove();
    if (!this.historyFor) return;

    let inner: string;
    if (this.historyLoading) {
      inner = `<div style="padding:30px; text-align:center;">${LoadingSpinner.render()}</div>`;
    } else if (!this.history) {
      inner = `<div class="cap-msg err" style="margin:0;">Couldn’t load history — GET /history failed. Check the server log.</div>`;
    } else {
      inner = this.renderHistoryBody(this.history);
    }

    const overlay = document.createElement('div');
    overlay.className = 'insp-modal-bg';
    overlay.id = 'insp-history-modal';
    overlay.innerHTML = `
      ${this.styleBlock()}
      <div class="insp-modal" data-insp-stop>
        <div class="insp-modal-head">
          <div>
            <h3>Condition history</h3>
            <div class="insp-sub" style="margin-top:3px;">${this.esc(this.historyFor.name)}</div>
          </div>
          <button class="insp-x" data-insp-close>×</button>
        </div>
        ${inner}
      </div>`;
    this.container.appendChild(overlay);
    overlay.addEventListener('click', e => { if (e.target === overlay) this.closeHistory(); });
    overlay.querySelector('[data-insp-close]')?.addEventListener('click', () => this.closeHistory());
  }

  private renderHistoryBody(h: any): string {
    const count: number = h.count || 0;
    const trend: any[] = h.grade_trend || [];
    const recurring: any[] = h.recurring || [];
    const appliances: any[] = h.appliances || [];

    // ── grade trend (latest big + Δ vs first; sparkline of graded points) ──
    const graded = trend.filter(t => typeof t.grade === 'number');
    let gradeBlock: string;
    if (!count) {
      gradeBlock = `<div class="insp-ai" style="margin:0;">${this.esc(h.note || 'No completed inspections yet.')} Complete an inspection to start the condition record.</div>`;
    } else if (!graded.length) {
      gradeBlock = `<div class="insp-ai" style="margin:0;">${count} completed inspection${count === 1 ? '' : 's'}, but none were AI-graded — no grade trend to plot. (Checklist-only inspections count toward coverage but not grade.)</div>`;
    } else {
      const latest = graded[graded.length - 1].grade as number;
      const first = graded[0].grade as number;
      const delta = latest - first;
      const dCls = graded.length < 2 ? 'flat' : (delta > 0 ? 'up' : (delta < 0 ? 'down' : 'flat'));
      const dTxt = graded.length < 2 ? 'first graded' : `${delta > 0 ? '+' : ''}${delta} vs first`;
      const spark = this.gradeSpark(graded.map(t => t.grade as number));
      gradeBlock = `
        <div class="ch-grade-row">
          <div class="ch-grade-num" style="color:${this.gradeHex(latest)};">${latest}<span style="font-size:14px; color:var(--text-muted); font-weight:600;"> / 100</span></div>
          <span class="ch-delta ${dCls}">${dTxt}</span>
          ${spark}
        </div>
        <div class="ch-meta" style="margin-top:6px;">${graded.length} graded of ${count} completed · ${this.esc(graded[0].date)} → ${this.esc(graded[graded.length - 1].date)} · last inspection ${this.fmtDate(h.last_inspection_at)}</div>`;
    }

    // ── recurring issues (red pill when fail_count ≥ 2) ──
    let recurringBlock: string;
    if (!count) {
      recurringBlock = '<div class="insp-sub">No history to aggregate yet.</div>';
    } else if (!recurring.length) {
      recurringBlock = '<div class="insp-sub">No failing items or issues across completed inspections — clean record.</div>';
    } else {
      recurringBlock = recurring.map(r => {
        const isRec = !!r.recurring || (r.fail_count >= 2);
        const title = r.title || r.item_key || (r.type ? `${r.type} issue` : 'issue');
        return `
          <div class="ch-rec">
            <div class="ch-rec-main">
              <div class="ch-rec-ttl">${this.esc(title)}</div>
              <div class="ch-rec-sub">failed ${r.fail_count} time${r.fail_count === 1 ? '' : 's'}${r.last_seen ? ` · last seen ${this.esc(r.last_seen)}` : ''}</div>
            </div>
            <span class="ch-rec-pill ${isRec ? 'rec' : 'once'}">${isRec ? 'recurring' : 'once'}</span>
          </div>`;
      }).join('');
    }

    // ── appliances on file (PDS roster; muted hint when empty) ──
    const applBlock = appliances.length
      ? appliances.map(a => `
          <div class="ch-appl">
            <div class="ch-appl-name">${this.esc(a.name)}</div>
            <div class="ch-appl-model">${a.value ? this.esc(a.value) : '<span style="color:var(--text-faint, #9ca3af);">no model on file</span>'}</div>
          </div>`).join('')
      : '<div class="insp-sub">No appliances on file. Add them in Onboarding (make/model/serial) to track them here and enrich the adaptive checklist.</div>';

    return `
      <div class="cap-card card-elev" style="margin:0 0 4px;">
        ${gradeBlock}
      </div>
      <div class="ch-sec-h">Recurring issues</div>
      <div class="insp-card" style="padding:4px 14px;">${recurringBlock}</div>
      <div class="ch-sec-h">Appliances on file</div>
      <div class="insp-card" style="padding:4px 14px;">${applBlock}</div>
      <div class="insp-basis" style="margin-top:12px;">Recurring = an item failed (or an issue referenced it) in ≥ 2 completed inspections. Grade trend plots AI-graded inspections only. Appliances come from this property’s PDS in Onboarding.</div>`;
  }

  // ── tab 3: templates ────────────────────────────────────────
  // ── tab 5: Managers (Feature D) ─────────────────────────────
  //   PM roster CRUD + per-property PM assignment + a PM scorecard with honest
  //   denominators and a per-PM "Property audit report" (window.open → print).
  private renderManagers(): string {
    if (this.managers == null || this.propertyPm == null || this.pmScorecard == null) {
      void this.loadManagers();
      return `<div class="insp-sub" style="padding:14px;">Loading the inspectors &amp; PMs list…</div>`;
    }
    const mgrs: any[] = this.managers || [];
    const listings: any[] = (this.propertyPm.listings || []);
    const sc = this.pmScorecard || {};
    const scRows: any[] = Array.isArray(sc.managers) ? sc.managers : [];
    const days = sc.window_days || 90;

    // ── roster — R120 (Pearl/Jesse: "give someone inspection access"): this
    // list IS the inspector roster (every inspector picker reads it through
    // lib/inspectorPool); the email links a row to its person's login, so
    // their own "My inspections" finds jobs assigned under the row's name.
    const rosterRows = mgrs.length
      ? mgrs.map(m => this.editMgrId === m.id ? `
        <tr class="pm-edit-row">
          <td><input id="pm-ed-name" value="${this.esc(m.name)}" aria-label="Name" style="width:100%; min-height:36px; box-sizing:border-box;"></td>
          <td><input id="pm-ed-email" type="email" value="${this.esc(m.email || '')}" placeholder="their login email" aria-label="Email" style="width:100%; min-height:36px; box-sizing:border-box;"></td>
          <td>${m.phone ? this.esc(m.phone) : '<span class="insp-sub">—</span>'}</td>
          <td style="text-align:right; white-space:nowrap;"><button class="insp-btn primary" data-pm-save="${this.esc(m.id)}">Save</button> <button class="insp-btn" data-pm-cancel>Cancel</button></td>
        </tr>` : `
        <tr>
          <td class="insp-name">${this.esc(m.name)}</td>
          <td>${m.email ? this.esc(m.email) : '<span class="insp-sub" title="No login linked — add their login email so their own \'My inspections\' finds the jobs">— no login email</span>'}</td>
          <td>${m.phone ? this.esc(m.phone) : '<span class="insp-sub">—</span>'}</td>
          <td style="text-align:right; white-space:nowrap;"><button class="insp-btn" data-pm-edit="${this.esc(m.id)}">Edit</button> <button class="insp-btn danger" data-pm-del="${this.esc(m.id)}">Remove</button></td>
        </tr>`).join('')
      : `<tr><td colspan="4" class="insp-sub" style="padding:12px;">No one on the list yet — add someone below to make them assignable for inspections (and to assign properties and grade audits).</td></tr>`;

    const roster = `
      <div class="insp-basis"><b>Inspectors &amp; PMs.</b> Anyone on this list can be assigned inspections. Add their login email so their own ‘My inspections’ finds the jobs. Property managers (PMs) are on this list too — a manual list, NOT pulled from Hostaway; assign each property to a PM below and an Audit inspection's grade and issues roll up to that PM.</div>
      <div id="pm-msg" role="status" style="font-size:12.5px; margin:0 0 8px;">${this.mgrMsg ? `<span style="color:var(--danger-text, #b91c1c); font-weight:600;">${this.esc(this.mgrMsg)}</span>` : ''}</div>
      <div class="insp-card insp-form card-elev" id="pm-new-form" style="margin-bottom:12px;">
        <label>Name
          <input id="pm-nf-name" placeholder="full name (as on their login)" style="width:180px;">
        </label>
        <label>Email
          <input id="pm-nf-email" type="email" placeholder="their login email" style="width:200px;">
        </label>
        <label>Phone
          <input id="pm-nf-phone" placeholder="phone (optional)" style="width:140px;">
        </label>
        <button id="pm-nf-add">+ Add to the list</button>
      </div>
      <div class="insp-card" style="overflow-x:auto; margin-bottom:18px;">
        <table class="insp-tbl">
          <colgroup><col><col style="width:220px;"><col style="width:150px;"><col style="width:170px;"></colgroup>
          <thead><tr><th>Inspector / PM</th><th>Login email</th><th>Phone</th><th style="text-align:right;">&nbsp;</th></tr></thead>
          <tbody>${rosterRows}</tbody>
        </table>
      </div>`;

    // ── per-property PM assignment table
    const pmOpts = (selected: string | null) => [
      `<option value=""${selected ? '' : ' selected'}>— unassigned —</option>`,
      ...mgrs.map(m => `<option value="${this.esc(m.id)}"${selected === m.id ? ' selected' : ''}>${this.esc(m.name)}</option>`),
    ].join('');
    const assignRows = listings.length
      ? listings.map(l => `
        <tr>
          <td class="insp-name" title="${this.esc(l.name)}">${this.esc(l.name)}</td>
          <td style="text-align:right;">
            <select data-pm-assign="${this.esc(l.id)}" style="min-width:180px;">${pmOpts(l.pm_id || null)}</select>
          </td>
        </tr>`).join('')
      : `<tr><td colspan="2" class="insp-sub" style="padding:12px;">No active properties.</td></tr>`;
    const assign = `
      <div class="insp-basis" style="margin-top:4px;">Assign each active property to a PM. Changes save immediately.</div>
      <div class="insp-card" style="overflow-x:auto; margin-bottom:18px;">
        <table class="insp-tbl">
          <colgroup><col><col style="width:220px;"></colgroup>
          <thead><tr><th>Property</th><th style="text-align:right;">Assigned PM</th></tr></thead>
          <tbody>${assignRows}</tbody>
        </table>
      </div>`;

    // ── PM scorecard (honest denominators; null avg when nothing graded)
    const scoreBody = scRows.length
      ? scRows.map(r => {
          const gradeCell = r.avg_audit_grade == null
            ? `<span class="insp-sub" title="${r.audits ? 'no graded audits in window' : 'no audits in window'}">${r.audits ? 'none graded' : 'no audits'}</span>`
            : `<span class="insp-grade" style="background:${this.gradeSoft(r.avg_audit_grade)}; color:${this.gradeHex(r.avg_audit_grade)};">${r.avg_audit_grade}</span> <span class="insp-sub">over ${r.graded_count} graded</span>`;
          return `
          <tr>
            <td class="insp-name">${this.esc(r.name)}</td>
            <td class="mono num" style="text-align:right;">${r.properties}</td>
            <td class="mono num" style="text-align:right;">${r.audits}</td>
            <td style="text-align:right;">${gradeCell}</td>
            <td class="mono num" style="text-align:right;">${r.issues_found}</td>
            <td class="mono num" style="text-align:right;">${r.condition_inspections}</td>
            <td style="text-align:right;"><button class="insp-btn" data-pm-report="${this.esc(r.pm_id)}" title="Open the PM audit report — Print / Save as PDF from there">PM audit report</button></td>
          </tr>`;
        }).join('')
      : `<tr><td colspan="7" class="insp-sub" style="padding:12px;">No property managers on the roster yet.</td></tr>`;
    const unassigned = sc.unassigned_audits ?? 0;
    const scorecard = `
      <div class="q-head" style="margin-top:8px;">
        <div class="insp-basis" style="margin:0;">PM scorecard · last ${days} days · audits = completed Audit inspections on the PM's assigned properties · avg audit grade averages only graded audits (honest null when none) · issues = total issues across those audits · condition = routine condition inspections for context.</div>
      </div>
      <div class="insp-card" style="overflow-x:auto;">
        <table class="insp-tbl">
          <colgroup><col><col style="width:90px;"><col style="width:80px;"><col style="width:150px;"><col style="width:80px;"><col style="width:100px;"><col style="width:150px;"></colgroup>
          <thead><tr><th>Property manager</th><th style="text-align:right;">Properties</th><th style="text-align:right;">Audits</th><th style="text-align:right;">Avg audit grade</th><th style="text-align:right;">Issues</th><th style="text-align:right;" title="Routine condition inspections — context, not graded against the PM">Condition</th><th style="text-align:right;">Report</th></tr></thead>
          <tbody>${scoreBody}</tbody>
        </table>
      </div>
      ${unassigned > 0 ? `<div class="insp-basis" style="margin-top:8px; color:var(--danger-text, #b91c1c);">${unassigned} completed audit${unassigned === 1 ? '' : 's'} in window on properties with no assigned PM — assign those properties above so the grades roll up.</div>` : ''}`;

    return roster + assign + scorecard;
  }

  private async loadManagers(): Promise<void> {
    const [m, p, s] = await Promise.all([
      this.get('/inspections/managers'),
      this.get('/inspections/property-pm'),
      this.get('/inspections/pm-scorecard?days=90'),
    ]);
    this.managers = Array.isArray(m?.managers) ? m.managers : [];
    if (Array.isArray(m?.managers)) this.roster = m.managers;   // R120: the pickers' roster stays current
    this.propertyPm = p || { assignments: {}, listings: [] };
    this.pmScorecard = s || { managers: [], unassigned_audits: 0, window_days: 90 };
    if (this.tab === 'managers') this.draw();
  }

  // R120: create / update one roster row. A duplicate login email (409
  // DUP_EMAIL, contract C9) says whose row already has it, in plain words.
  private async saveRosterRow(body: { id?: string; name: string; email: string; phone?: string }): Promise<boolean> {
    let r: Response;
    try { r = await fetch(`${API}/inspections/managers`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); }
    catch (_) { this.mgrMsg = 'Couldn’t reach the server — check your connection and try again.'; this.draw(); return false; }
    const j = await r.json().catch(() => ({}));
    if (r.ok) { this.mgrMsg = ''; return true; }
    this.mgrMsg = r.status === 409 && j && j.code === 'DUP_EMAIL'
      ? `${j.existing_name || 'Someone'} already has this email on the list — edit that row instead.`
      : String((j && j.error) || `That didn’t save (${r.status}).`);
    this.draw();
    return false;
  }

  private async reloadManagers(): Promise<void> {
    this.managers = null; this.propertyPm = null; this.pmScorecard = null;
    await this.loadManagers();
  }

  private renderTemplates(): string {
    const cards = this.templates.length
      ? this.templates.map(t => {
        const areas = [...new Set((t.items || []).map((it: any) => it.area))];
        return `
          <div class="insp-card" style="padding:14px; margin-bottom:12px;">
            <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:12px;">
              <div>
                <div class="insp-name" style="font-size:13px;">${this.esc(t.name)} ${t.is_standard ? '<span class="insp-chip">standard</span>' : ''}</div>
                <div class="insp-sub" style="margin-top:3px;">${(t.items || []).length} items · areas: ${areas.map(a => this.esc(a)).join(', ') || '—'}</div>
              </div>
              <div style="display:flex; gap:8px; flex:none;">
                <button class="insp-btn" data-insp-tpledit="${this.esc(t.id)}">Edit</button>
                <button class="insp-btn danger" data-insp-tpldel="${this.esc(t.id)}">Delete</button>
              </div>
            </div>
          </div>`;
      }).join('')
      : emptyState('No templates', 'Create an inspection checklist below.', '📋');

    return `
      <div class="insp-basis">Templates define the checklist; each item carries an area (Exterior, Living, Kitchen, Bedrooms, Bathrooms, Safety…). New inspections copy these items.</div>
      <div style="margin-bottom:12px;"><button class="insp-btn primary" data-insp-tplnew>+ New template</button></div>
      ${cards}
      ${this.renderFocusManager()}`;
  }

  // Focus-items manager (Feature B): list/create/edit/delete cadence items that
  // must happen on a schedule (not every visit). Lives in the Templates tab.
  private renderFocusManager(): string {
    let body: string;
    if (this.focusManager == null) {
      void this.loadFocusManager();
      body = `<div class="insp-sub" style="padding:10px 0;">Loading cadence items…</div>`;
    } else if (!this.focusManager.length) {
      body = emptyState('No cadence items', 'Add a required check that must happen on a schedule.', '🔁');
    } else {
      body = this.focusManager.map(f => {
        const cad = f.cadence || { kind: 'every_turn', n: 1 };
        const cadTxt = cad.kind === 'every_turn' ? 'every visit'
          : cad.kind === 'every_days' ? `every ${cad.n} day${cad.n === 1 ? '' : 's'}`
          : `every ${cad.n} inspection${cad.n === 1 ? '' : 's'}`;
        const scopeTxt = f.scope === 'global' ? 'all properties' : 'this property only';
        return `
          <div class="insp-card" style="padding:14px; margin-bottom:12px;">
            <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:12px;">
              <div>
                <div class="insp-name" style="font-size:13px;">${this.esc(f.title)} <span class="insp-chip">${this.esc(f.area || 'General')}</span></div>
                <div class="insp-sub" style="margin-top:3px;">cadence: ${this.esc(cadTxt)} · scope: ${this.esc(scopeTxt)}</div>
              </div>
              <div style="display:flex; gap:8px; flex:none;">
                <button class="insp-btn" data-foc-edit="${this.esc(f.id)}">Edit</button>
                <button class="insp-btn danger" data-foc-del="${this.esc(f.id)}">Delete</button>
              </div>
            </div>
          </div>`;
      }).join('');
    }
    return `
      <div class="insp-basis" style="margin-top:22px;">Required cadence checks — items that must happen on a schedule (smoke/CO detectors, extinguisher charge, deep-clean behind appliances), not every visit but never neglected. The system surfaces which are due on each inspection and opens a re-check ticket when a due item is skipped.</div>
      <div style="margin-bottom:12px;"><button class="insp-btn primary" data-foc-new>+ New cadence item</button></div>
      ${body}`;
  }

  private async loadFocusManager(): Promise<void> {
    const j = await this.get('/inspections/focus-items');
    this.focusManager = Array.isArray(j?.items) ? j.items : [];
    if (this.tab === 'templates') this.draw();
  }

  // Focus-item editor modal — editFocusId '' means new.
  private renderFocusModal(): void {
    const isNew = this.editFocusId === '';
    const f = isNew
      ? { id: '', title: '', area: 'Safety', cadence: { kind: 'every_days', n: 90 }, scope: 'global' }
      : (this.focusManager || []).find(x => x.id === this.editFocusId);
    if (!f) { this.editFocusId = null; return; }
    const cad = f.cadence || { kind: 'every_days', n: 90 };

    const overlay = document.createElement('div');
    overlay.className = 'insp-modal-bg';
    overlay.id = 'insp-foc-modal';
    const opt = (v: string, label: string) => `<option value="${v}" ${cad.kind === v ? 'selected' : ''}>${label}</option>`;
    overlay.innerHTML = `
      <div class="insp-modal" data-insp-stop>
        <div class="insp-modal-head">
          <h3>${isNew ? 'New cadence item' : 'Edit cadence item'}</h3>
          <button class="insp-x" data-insp-close>×</button>
        </div>
        <label class="insp-fld">Title
          <input id="foc-title" value="${this.esc(f.title || '')}" placeholder="e.g. Smoke &amp; CO detectors">
        </label>
        <label class="insp-fld">Area
          <input id="foc-area" value="${this.esc(f.area || 'General')}" placeholder="e.g. Safety">
        </label>
        <div class="insp-fld" style="text-transform:none; letter-spacing:0;">Cadence
          <div class="fm-cad">
            <select id="foc-kind">
              ${opt('every_turn', 'Every visit')}
              ${opt('every_days', 'Every N days')}
              ${opt('every_inspections', 'Every N inspections')}
            </select>
            <input id="foc-n" type="number" min="1" value="${this.esc(cad.n || 1)}" style="width:90px;">
          </div>
        </div>
        <label class="insp-fld" style="text-transform:none; letter-spacing:0;">Scope
          <select id="foc-scope">
            <option value="global" ${f.scope === 'global' ? 'selected' : ''}>All properties (global)</option>
            ${this.boardListings.map(l => `<option value="${this.esc(l.listing_id)}" ${String(f.scope) === String(l.listing_id) ? 'selected' : ''}>${this.esc(l.listing_name)} — this property only</option>`).join('')}
          </select>
        </label>
        <div class="insp-modal-actions" style="margin-top:6px;">
          <button class="insp-btn primary" id="foc-save">Save cadence item</button>
        </div>
      </div>`;
    this.container.appendChild(overlay);

    const close = () => { this.editFocusId = null; overlay.remove(); };
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
    overlay.querySelector('[data-insp-close]')?.addEventListener('click', close);

    // hide N when "every visit"
    const kindSel = overlay.querySelector('#foc-kind') as HTMLSelectElement;
    const nInp = overlay.querySelector('#foc-n') as HTMLInputElement;
    const syncN = () => { nInp.style.display = kindSel.value === 'every_turn' ? 'none' : ''; };
    kindSel.addEventListener('change', syncN); syncN();

    overlay.querySelector('#foc-save')?.addEventListener('click', async () => {
      const title = (overlay.querySelector('#foc-title') as HTMLInputElement)?.value?.trim();
      if (!title) { alert('Title is required.'); return; }
      const kind = kindSel.value;
      const n = kind === 'every_turn' ? 1 : Math.max(1, Number(nInp.value) || 1);
      const area = (overlay.querySelector('#foc-area') as HTMLInputElement)?.value?.trim() || 'General';
      const scope = (overlay.querySelector('#foc-scope') as HTMLSelectElement)?.value || 'global';
      const body: any = { title, area, cadence: { kind, n }, scope };
      if (!isNew) body.id = f.id;
      const j = await this.post('/inspections/focus-items', body);
      if (j?.item) {
        this.editFocusId = null;
        overlay.remove();
        this.focusManager = null; // refetch on next draw
        this.focusFor = null; this.focus = null; // any open capture focus set is stale
        this.draw();
      }
    });
  }

  // template editor modal — editTplId '' means new.
  private renderTemplateModal(): void {
    const isNew = this.editTplId === '';
    const tpl = isNew ? { id: '', name: '', is_standard: false, items: [] } : this.templates.find(t => t.id === this.editTplId);
    if (!tpl) { this.editTplId = null; return; }

    const itemRows = (items: any[]) => items.map((it, idx) => `
      <div class="insp-tpl-item" data-insp-trow="${idx}">
        <input data-insp-titem-title placeholder="Item title" value="${this.esc(it.title || '')}">
        <input data-insp-titem-area placeholder="Area" value="${this.esc(it.area || '')}">
        <button class="insp-btn danger" data-insp-titem-del>Remove</button>
      </div>`).join('');

    const overlay = document.createElement('div');
    overlay.className = 'insp-modal-bg';
    overlay.id = 'insp-tpl-modal';
    overlay.innerHTML = `
      <div class="insp-modal" data-insp-stop>
        <div class="insp-modal-head">
          <h3>${isNew ? 'New template' : 'Edit template'}</h3>
          <button class="insp-x" data-insp-close>×</button>
        </div>
        <label class="insp-fld">Name
          <input id="insp-t-name" value="${this.esc(tpl.name || '')}" placeholder="e.g. Standard Inspection">
        </label>
        <label class="insp-fld" style="flex-direction:row; align-items:center; gap:6px; text-transform:none; letter-spacing:0;">
          <input type="checkbox" id="insp-t-std" ${tpl.is_standard ? 'checked' : ''} style="width:auto;"> Standard (used by default for new inspections)
        </label>
        <div class="insp-area" style="margin-top:4px;">Items</div>
        <div id="insp-t-items">${itemRows(tpl.items || [])}</div>
        <div><button class="insp-btn" id="insp-t-additem">+ Add item</button></div>
        <div class="insp-modal-actions" style="margin-top:6px;">
          <button class="insp-btn primary" id="insp-t-save">Save template</button>
        </div>
      </div>`;
    this.container.appendChild(overlay);

    const close = () => { this.editTplId = null; overlay.remove(); };
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
    overlay.querySelector('[data-insp-close]')?.addEventListener('click', close);

    const itemsEl = overlay.querySelector('#insp-t-items') as HTMLElement;
    const bindRowDeletes = () => {
      itemsEl.querySelectorAll<HTMLElement>('[data-insp-titem-del]').forEach(btn => {
        btn.onclick = () => { btn.closest('[data-insp-trow]')?.remove(); };
      });
    };
    bindRowDeletes();
    overlay.querySelector('#insp-t-additem')?.addEventListener('click', () => {
      const div = document.createElement('div');
      div.className = 'insp-tpl-item';
      div.setAttribute('data-insp-trow', String(Date.now()));
      div.innerHTML = `
        <input data-insp-titem-title placeholder="Item title" value="">
        <input data-insp-titem-area placeholder="Area" value="">
        <button class="insp-btn danger" data-insp-titem-del>Remove</button>`;
      itemsEl.appendChild(div);
      bindRowDeletes();
    });
    overlay.querySelector('#insp-t-save')?.addEventListener('click', async () => {
      const name = (overlay.querySelector('#insp-t-name') as HTMLInputElement)?.value?.trim();
      if (!name) { alert('Template name is required.'); return; }
      const is_standard = (overlay.querySelector('#insp-t-std') as HTMLInputElement)?.checked || false;
      const items = Array.from(itemsEl.querySelectorAll<HTMLElement>('[data-insp-trow]')).map(row => ({
        title: (row.querySelector('[data-insp-titem-title]') as HTMLInputElement)?.value?.trim() || '',
        area: (row.querySelector('[data-insp-titem-area]') as HTMLInputElement)?.value?.trim() || 'General',
      })).filter(it => it.title);
      const body: any = { name, is_standard, items };
      if (!isNew) body.id = tpl.id;
      const j = await this.post('/inspection-templates', body);
      if (j) {
        this.templates = j.templates || this.templates;
        this.editTplId = null;
        overlay.remove();
        this.draw();
      }
    });
  }

  // Create a capture-first inspection for a property and open the capture view.
  // template_id omitted → the server uses the standard template (13 items).
  // Shared by the Inspections "+ New" form and the Queue's "Start inspection →".
  private async startInspectionFor(listing_id: string, listing_name: string | undefined, inspector = '', template_id?: string, inspection_type: 'condition' | 'audit' = 'condition', form_type: string | null = 'basic', for_key = ''): Promise<void> {
    if (!listing_id) { alert('Pick a property.'); return; }
    // R30 #5: a field inspector starting their own inspection IS the inspector
    // — attribute it (shared logins resolve to "Company — Person" here).
    // NOT under admin view-as: the viewed inspector didn't actually start it.
    const viewingAs = (() => { try { return !!sessionStorage.getItem('viewAsUserId'); } catch { return false; } })();
    if (!inspector && !viewingAs && InspectionsPage.fieldRole() === 'inspector') inspector = InspectionsPage.profileName() || '';
    // Capture-first inspections record a walkthrough → mode 'video'.
    // for_key: the 📌 / rescheduled queue job this was started from — a visit
    // ahead of that job's date still counts it done (lib/inspMove.doneRecord)
    const j = await this.post('/inspections', { listing_id, template_id, mode: 'video', inspector, listing_name, inspection_type, form_type, ...(for_key ? { for_key } : {}) });
    if (j?.inspection) {
      await this.reloadInspections();
      this.activeId = j.inspection.id;
      this.view = 'capture';
      this.captureStep = 1;   // stepwise basic flow always starts at Issues
      this.draw();
    }
  }

  // Form-type picker (R17): starting an inspection asks WHICH form — Basic
  // (routine 4-pillar), Deep (sections A–H), or Audit (leadership only).
  private pendingStart: { listing_id: string; name?: string; for_key?: string } | null = null;
  private openStartPicker(listing_id: string, name?: string, for_key = ''): void {
    this.pendingStart = { listing_id, name, for_key };
    const role = InspectionsPage.fieldRole();
    const office = !['inspector', 'cleaner', 'maintenance'].includes(role);
    const old = document.getElementById('insp-start-picker'); if (old) old.remove();
    const wrap = document.createElement('div');
    wrap.id = 'insp-start-picker';
    wrap.innerHTML = `
      <div style="position:fixed; inset:0; background:rgba(15,23,42,.45); z-index:1000; display:flex; align-items:flex-end; justify-content:center;" data-sp-close>
        <div style="background:var(--card,#fff); border-radius:16px 16px 0 0; padding:18px 16px 26px; width:100%; max-width:480px;" onclick="event.stopPropagation()">
          <div style="font-size:15px; font-weight:700; margin-bottom:2px;">${this.esc(name || listing_id)}</div>
          <div style="font-size:12px; color:var(--text-muted); margin-bottom:12px;">Which inspection?</div>
          <button data-sp-type="basic" style="display:block; width:100%; text-align:left; padding:13px 14px; margin-bottom:8px; border:1px solid var(--border); border-radius:12px; background:var(--bg-subtle); cursor:pointer;"><b>Basic</b><br><span style="font-size:12px; color:var(--text-muted);">4 pillars · issue photos + 8–12 “wins” shots · the routine visit</span></button>
          <button data-sp-type="deep" style="display:block; width:100%; text-align:left; padding:13px 14px; margin-bottom:8px; border:1px solid var(--border); border-radius:12px; background:var(--bg-subtle); cursor:pointer;"><b>Deep</b><br><span style="font-size:12px; color:var(--text-muted);">Sections A–H: FF&E, linens, systems, safety, kitchen, exterior, paint</span></button>
          ${office ? `<button data-sp-type="audit" style="display:block; width:100%; text-align:left; padding:13px 14px; margin-bottom:8px; border:1px solid var(--border); border-radius:12px; background:var(--bg-subtle); cursor:pointer;"><b>Audit</b><br><span style="font-size:12px; color:var(--text-muted);">Leadership check-in — 17 sub-scores + the X factor</span></button>` : ''}
          <button data-sp-close style="display:block; width:100%; padding:11px; border:none; border-radius:12px; background:none; color:var(--text-muted); cursor:pointer;">Cancel</button>
        </div>
      </div>`;
    document.body.appendChild(wrap);
    wrap.querySelectorAll<HTMLElement>('[data-sp-close]').forEach(el => el.addEventListener('click', () => { wrap.remove(); this.pendingStart = null; }));
    wrap.querySelectorAll<HTMLElement>('[data-sp-type]').forEach(el => el.addEventListener('click', () => {
      const t = el.dataset.spType!;
      const p = this.pendingStart; wrap.remove(); this.pendingStart = null;
      if (p) void this.startInspectionFor(p.listing_id, p.name, '', undefined, t === 'audit' ? 'audit' : 'condition', t, p.for_key || '');
    }));
  }

  // ── request supplies (R17 PDF #5) — a compact storefront for the inspector
  // portal. Same endpoint as the cleaner storefront and the office Supplies
  // page: POST /api/assetops/supplies/orders (409 over_budget → confirm).
  private async openSupplyRequest(): Promise<void> {
    // R115: consumables only (scope=field — par > 0 or a consumable category);
    // the 154-item catalog's TVs / mattresses / AC units are office orders.
    const cat = await this.get('/supplies/standard?scope=field');
    const items: any[] = cat?.items || cat?.supplies || (Array.isArray(cat) ? cat : []);
    const srCats = [...new Set(items.map((it: any) => String(it.category || 'Other')))];
    const props = this.boardListings || [];
    const old = document.getElementById('insp-supply-req'); if (old) old.remove();
    const wrap = document.createElement('div');
    wrap.id = 'insp-supply-req';
    wrap.innerHTML = `
      <div style="position:fixed; inset:0; background:rgba(15,23,42,.45); z-index:1000; display:flex; align-items:flex-end; justify-content:center;" data-sr-close>
        <div style="background:var(--card,#fff); border-radius:16px 16px 0 0; padding:18px 16px 26px; width:100%; max-width:520px; max-height:82vh; overflow-y:auto;" onclick="event.stopPropagation()">
          <div style="font-size:15px; font-weight:700; margin-bottom:10px;">🛒 Request supplies</div>
          <select id="sr-prop" style="width:100%; padding:10px 12px; border:1px solid var(--border); border-radius:10px; font-size:13px; margin-bottom:12px;">
            <option value="">Which property?</option>
            ${props.map((p: any) => `<option value="${this.esc(p.listing_id)}|${this.esc(p.listing_name)}">${this.esc(p.listing_name)}</option>`).join('')}
          </select>
          <div style="display:flex; flex-direction:column; gap:6px; margin-bottom:12px;">
            ${srCats.map(c => `<div style="font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); padding:6px 2px 0;">${this.esc(c)}</div>` + items.map((it: any, i: number) => ({ it, i })).filter(x => String(x.it.category || 'Other') === c).map(({ it, i }) => `
              <div style="display:flex; align-items:center; gap:8px; padding:8px 10px; border:1px solid var(--border); border-radius:10px;">
                <div style="flex:1; min-width:0; font-size:13px; font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${this.esc(it.name)}${(it.target_price || it.unit_price) ? ` <span style="font-weight:400; color:var(--text-muted); font-size:11.5px;">$${Number(it.target_price || it.unit_price).toFixed(2)} / pack${Number(it.pack_qty) > 1 ? ` of ${Number(it.pack_qty)}` : ''}</span>` : ''}</div>
                <button data-sr-minus="${i}" style="width:36px; height:36px; flex:none; border:1px solid var(--border); border-radius:8px; background:var(--bg-subtle); font-weight:700; cursor:pointer;">−</button>
                <span data-sr-qty="${i}" data-name="${this.esc(it.name)}" data-price="${Number(it.target_price || it.unit_price) || 0}" style="min-width:22px; text-align:center; font-weight:700; font-size:14px;">0</span>
                <button data-sr-plus="${i}" style="width:36px; height:36px; flex:none; border:1px solid var(--border); border-radius:8px; background:var(--bg-subtle); font-weight:700; cursor:pointer;">+</button>
              </div>`).join('')).join('') || '<div style="font-size:12.5px; color:var(--text-muted);">No catalog items — use the custom row below.</div>'}
          </div>
          <div style="display:flex; gap:8px; margin-bottom:10px;">
            <input id="sr-custom" placeholder="Something else? (item name)" style="flex:1; padding:10px 12px; border:1px solid var(--border); border-radius:10px; font-size:13px;">
            <input id="sr-custom-qty" type="number" min="1" value="1" style="width:70px; padding:10px; border:1px solid var(--border); border-radius:10px; font-size:13px;">
          </div>
          <input id="sr-note" placeholder="Note (optional)" style="width:100%; padding:10px 12px; border:1px solid var(--border); border-radius:10px; font-size:13px; margin-bottom:14px;">
          <button id="sr-submit" style="width:100%; padding:14px; border:none; border-radius:12px; background:var(--info,#1d4ed8); color:#fff; font-size:14px; font-weight:700; cursor:pointer;">Submit request</button>
          <button data-sr-close style="display:block; width:100%; padding:11px; border:none; background:none; color:var(--text-muted); cursor:pointer; margin-top:4px;">Cancel</button>
        </div>
      </div>`;
    document.body.appendChild(wrap);
    wrap.querySelectorAll<HTMLElement>('[data-sr-close]').forEach(el => el.addEventListener('click', () => wrap.remove()));
    const qtyEl = (i: string) => wrap.querySelector(`[data-sr-qty="${i}"]`) as HTMLElement | null;
    wrap.querySelectorAll<HTMLElement>('[data-sr-plus]').forEach(el => el.addEventListener('click', () => {
      const q = qtyEl(el.dataset.srPlus!); if (q) q.textContent = String(Number(q.textContent) + 1);
    }));
    wrap.querySelectorAll<HTMLElement>('[data-sr-minus]').forEach(el => el.addEventListener('click', () => {
      const q = qtyEl(el.dataset.srMinus!); if (q) q.textContent = String(Math.max(0, Number(q.textContent) - 1));
    }));
    const submitBtn = wrap.querySelector('#sr-submit') as HTMLButtonElement;
    submitBtn?.addEventListener('click', async () => {
      const propVal = (wrap.querySelector('#sr-prop') as HTMLSelectElement)?.value;
      if (!propVal) { alert('Pick a property.'); return; }
      const [listing_id, listing_name] = propVal.split('|');
      const orderItems: any[] = [];
      wrap.querySelectorAll<HTMLElement>('[data-sr-qty]').forEach(q => {
        const n = Number(q.textContent);
        if (n > 0) orderItems.push({ name: q.dataset.name, qty: n, unit_price: Number(q.dataset.price) || 0 });
      });
      const custom = (wrap.querySelector('#sr-custom') as HTMLInputElement)?.value.trim();
      if (custom) orderItems.push({ name: custom, qty: Math.max(1, Number((wrap.querySelector('#sr-custom-qty') as HTMLInputElement)?.value) || 1), unit_price: 0 });
      if (!orderItems.length) { alert('Add at least one item.'); return; }
      const note = (wrap.querySelector('#sr-note') as HTMLInputElement)?.value.trim() || '';
      const requested_by = InspectionsPage.profileName() || 'inspector';
      submitBtn.disabled = true; submitBtn.textContent = 'Submitting…';
      const body = { listing_id, listing_name, items: orderItems, requested_by, note, source: 'manual' };
      let r = await fetch(`${API}/supplies/orders`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (r.status === 409) {
        const j = await r.json().catch(() => ({} as any));
        if (confirm(`${j.message || 'This puts the property over its monthly supply budget.'} Order anyway?`)) {
          r = await fetch(`${API}/supplies/orders`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...body, override: true }) });
        } else { submitBtn.disabled = false; submitBtn.textContent = 'Submit request'; return; }
      }
      if (r.ok) { wrap.remove(); alert('Supply request submitted — the office will order it.'); }
      else { const j = await r.json().catch(() => ({} as any)); alert(`Request failed: ${j.error || r.status}`); submitBtn.disabled = false; submitBtn.textContent = 'Submit request'; }
    });
  }

  // ── bind (shell + per-tab) ──────────────────────────────────
  private bind(): void {
    bindFieldViewSwitcher(this.container);   // R64: field role self-switch chips
    const $ = (sel: string) => this.container.querySelector(sel) as HTMLInputElement | null;
    this.container.querySelector('#ifld-supplies')?.addEventListener('click', () => void this.openSupplyRequest());
    // Donielle: any-property start (inspector mobile) — datalist over ALL active
    // listings (incl. onboarding), label→id resolution, then the shared
    // startInspectionFor() capture flow (same path as the queue's Start →).
    // R64 (office): ad-hoc schedule composer — property (unique match) +
    // date + inspector → POST /inspections/assign {manual:true}; the queue
    // synthesizes a 📌 row the inspector's default "Mine" view picks up.
    this.container.querySelector('[data-sched-toggle]')?.addEventListener('click', async () => {
      this.schedOpen = !this.schedOpen;
      this.draw();
      if (this.schedOpen && this.mobListings === null) {
        const l = await fetch('/api/listings/active?include=onboarding').then(r => r.ok ? r.json() : null).catch(() => null);
        if (l) {
          this.mobListings = Array.isArray(l) ? l : (l.listings || []);
          const dl = this.container.querySelector('#sched-props');
          if (dl) dl.innerHTML = (this.mobListings || []).map((x: any) => `<option value="${this.esc(String(x.short_name || x.name || x.id))}"></option>`).join('');
        }
      }
    });
    this.container.querySelector('#sched-go')?.addEventListener('click', async () => {
      const typed = ((this.container.querySelector('#sched-prop') as HTMLInputElement | null)?.value || '').trim().toLowerCase();
      const date = (this.container.querySelector('#sched-date') as HTMLInputElement | null)?.value || '';
      const inspector = (this.container.querySelector('#sched-insp') as HTMLSelectElement | null)?.value || '';
      if (!typed) { alert('Type a property name.'); return; }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { alert('Pick a date.'); return; }
      if (!inspector) { alert('Pick an inspector.'); return; }
      const ls = this.mobListings || [];
      const label = (l: any) => String(l.short_name || l.name || l.id);
      const exact = ls.filter((l: any) => label(l).toLowerCase() === typed);
      const cands = exact.length ? exact
        : ls.filter((l: any) => label(l).toLowerCase().includes(typed) || String(l.name || '').toLowerCase().includes(typed));
      if (!cands.length) { alert('No property matched — pick one from the list.'); return; }
      if (cands.length > 1) { alert('Multiple properties match — keep typing or pick one from the list.'); return; }
      const hit = cands[0];
      // review #36: same 6-day-streak 409 handshake as the queue dropdown —
      // this.post would surface the 409 as a dead-end alert with no override.
      const send = async (override: boolean): Promise<boolean> => {
        const r = await fetch(`${API}/inspections/assign`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ listing_id: String(hit.id), checkout: date, inspector, manual: true, ...(override ? { override: true } : {}) }),
        });
        const jj = await r.json().catch(() => ({} as any));
        if (r.status === 409 && jj.needs_confirm) {
          if (window.confirm(jj.error || 'Over the 6-day streak rule — assign anyway?')) return send(true);
          return false;
        }
        if (!r.ok) { alert(jj.error || `request failed (${r.status})`); return false; }
        return true;
      };
      if (!(await send(false))) return;
      this.schedOpen = false;
      // reloadQueue keeps the &date= param when exact-date mode is active —
      // a plain days fetch here corrupted the date view (review).
      await this.reloadQueue();
      this.draw();
      alert(`Scheduled — ${label(hit)} on ${date}, assigned to ${inspector}.`);
    });
    // R64: Mine / Everyone toggle (inspector field view).
    this.container.querySelector('#ifld-mine')?.addEventListener('click', () => { if (this.mobShowAll) { this.mobShowAll = false; this.draw(); } });
    this.container.querySelector('#ifld-all')?.addEventListener('click', () => { if (!this.mobShowAll) { this.mobShowAll = true; this.draw(); } });
    this.container.querySelector('#ifld-anystart')?.addEventListener('click', async () => {
      this.mobStartOpen = !this.mobStartOpen;
      this.draw();
      if (this.mobStartOpen && this.mobListings === null) {
        const l = await fetch('/api/listings/active?include=onboarding').then(r => r.ok ? r.json() : null).catch(() => null);
        if (l) {
          this.mobListings = Array.isArray(l) ? l : (l.listings || []);
          // Patch the open panel in place — a full draw() here would wipe the
          // property name the inspector is mid-typing (same rule as #ifld-qsearch).
          const dl = this.container.querySelector('#ifld-proplist');
          if (dl) dl.innerHTML = (this.mobListings || []).map((x: any) => `<option value="${this.esc(String(x.short_name || x.name || x.id))}"></option>`).join('');
          this.container.querySelector('#ifld-anyload')?.remove();
        }
      }
    });
    this.container.querySelector('#ifld-anygo')?.addEventListener('click', () => {
      const inp = this.container.querySelector('#ifld-anyprop') as HTMLInputElement | null;
      const typed = (inp?.value || '').trim().toLowerCase();
      if (!typed) { alert('Type a property name.'); return; }
      const ls = this.mobListings || [];
      const label = (l: any) => String(l.short_name || l.name || l.id);
      // Only auto-start on a UNIQUE match — a loose .find() would silently
      // open an inspection on the wrong home for colliding/partial names.
      const exact = ls.filter((l: any) => label(l).toLowerCase() === typed);
      const cands = exact.length ? exact
        : ls.filter((l: any) => label(l).toLowerCase().includes(typed) || String(l.name || '').toLowerCase().includes(typed));
      if (!cands.length) { alert('No property matched — pick one from the list.'); return; }
      if (cands.length > 1) { alert('Multiple properties match — keep typing or pick one from the list.'); return; }
      const hit = cands[0];
      if (!exact.length && !confirm(`Start basic inspection at ${label(hit)}?`)) return;
      this.mobStartOpen = false;
      void this.startInspectionFor(String(hit.id), String(hit.name || hit.short_name || hit.id), '', undefined, 'condition', 'basic');
    });
    // Donielle: live name filter over the mobile queue cards — hides/shows via
    // DOM (no redraw, so the keyboard focus survives); mobSearch persists the
    // filter across redraws (drawInspectorMobile re-applies it).
    const qs = this.container.querySelector('#ifld-qsearch') as HTMLInputElement | null;
    qs?.addEventListener('input', () => {
      this.mobSearch = qs.value;
      const q = qs.value.trim().toLowerCase();
      this.container.querySelectorAll<HTMLElement>('[data-qname]').forEach(c => {
        c.style.display = !q || (c.dataset.qname || '').includes(q) ? '' : 'none';
      });
    });
    this.container.querySelectorAll<HTMLElement>('[data-insp-tab]').forEach(el =>
      el.addEventListener('click', async () => {
        this.tab = el.dataset.inspTab as any;
        this.draw();
        if (this.tab === 'calendar' && !this.opsCal) {
          const j = await this.get('/ops-calendar?days=14');
          this.opsCal = j || { items: [] };
          this.draw();
        }
      }));

    // queue: days selector (7/14/30) + "Start inspection →" per row
    this.container.querySelectorAll<HTMLElement>('[data-q-days]').forEach(el =>
      el.addEventListener('click', async () => {
        const d = Number(el.dataset.qDays);
        if (d === this.queueDays) return;
        this.queueDays = d;
        await this.reloadQueue();
        this.draw();
      }));
    // office queue: Today / 7d / 14d view tabs (one 14d fetch, filtered client-side)
    this.container.querySelectorAll<HTMLElement>('[data-q-tab]').forEach(el =>
      el.addEventListener('click', async () => {
        const t = el.dataset.qTab as 'today' | '7d' | '14d';
        if (!t) return;
        if (t === this.queueTab && !this.queueDate) return;
        const hadDate = !!this.queueDate;
        this.queueTab = t;
        this.queueDate = null;
        if (hadDate) await this.reloadQueue();   // cached fetch was date-scoped
        this.draw();
      }));
    // R46: exact-date jump — refetches in server date mode (see reloadQueue).
    // checkValidity guards half-typed keyboard dates (Chrome commits per
    // segment edit); clearing the input refetches the window rows.
    this.container.querySelectorAll<HTMLInputElement>('[data-q-date]').forEach(el =>
      el.addEventListener('change', async () => {
        if (el.value && !el.checkValidity()) return;
        const v = el.value || null;
        if (v === this.queueDate) return;
        this.queueDate = v;
        await this.reloadQueue();
        this.draw();
      }));
    this.container.querySelectorAll<HTMLElement>('[data-q-start]').forEach(el =>
      el.addEventListener('click', () => {
        const [listing_id, listing_name, light] = el.dataset.qStart!.split('|');
        const forKey = el.dataset.qFor || '';
        // Light check-in rows skip the form picker — a pre-arrival re-check is
        // always the Basic (4-pillar) pass.
        if (light === 'light') { void this.startInspectionFor(listing_id, listing_name, '', undefined, 'condition', 'basic', forKey); return; }
        this.openStartPicker(listing_id, listing_name, forKey);
      }));
    // R44 (Mark): "not possible — guest checked in early" skip + undo. The
    // early_checkin skip also stamps the manual ECI tag on the arrival so HK
    // and the Arrivals board learn the guest is in early.
    this.container.querySelectorAll<HTMLElement>('[data-q-skip]').forEach(el =>
      el.addEventListener('click', async () => {
        const [listing_id, checkout_date, next_checkin] = el.dataset.qSkip!.split('|');
        if (!window.confirm('Mark this inspection NOT POSSIBLE because the guest checked in early? It stays on the queue as skipped (undo any time), and the early check-in gets flagged for the cleaning + arrivals boards.')) return;
        const j = await this.post('/inspections/queue/skip', {
          listing_id, checkout_date, next_checkin: next_checkin || undefined,
          reason: 'early_checkin', by: InspectionsPage.profileName(),
        });
        if (j) { await this.reloadQueue(); this.draw(); }
      }));
    this.container.querySelectorAll<HTMLElement>('[data-q-unskip]').forEach(el =>
      el.addEventListener('click', async () => {
        const [listing_id, checkout_date] = el.dataset.qUnskip!.split('|');
        const j = await this.post('/inspections/queue/skip', { listing_id, checkout_date, clear: true, by: InspectionsPage.profileName() });
        if (j) { await this.reloadQueue(); this.draw(); }
      }));
    // queue: assign an inspector to an upcoming inspection (R19 #3)
    this.container.querySelectorAll<HTMLSelectElement>('[data-q-assign]').forEach(sel =>
      sel.addEventListener('change', async () => {
        const [listing_id, checkout] = sel.dataset.qAssign!.split('|');
        // R87 #2.2: the 6-days-in-a-row rule — the server 409s with the run;
        // one confirm overrides (mirrors the R82 manual-turn handshake).
        const send = async (override: boolean): Promise<boolean> => {
          const r = await fetch(`${API}/inspections/assign`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ listing_id, checkout, inspector: sel.value, ...(override ? { override: true } : {}) }),
          });
          const j = await r.json().catch(() => ({} as any));
          if (r.status === 409 && j.needs_confirm) {
            if (window.confirm(j.error || 'Over the 6-day streak rule — assign anyway?')) return send(true);
            return false;
          }
          if (!r.ok) { alert(j.error || `request failed (${r.status})`); return false; }
          return true;
        };
        const ok = await send(false);
        const row = (this.queue?.rows || []).find((r: any) => String(r.listing_id) === listing_id
          && (r.type === 'checkin_light' ? r.checkin_date : r.checkout_date) === checkout);   // review #37: light rows key on check-IN
        if (ok) { if (row) row.assigned_inspector = sel.value; }
        else if (row) sel.value = row.assigned_inspector || '';   // snap back — nothing was saved
        else if (!ok) sel.value = '';
      }));

    // R114 (Madel): ⋯ → "↪ Move to date…" / "↩ Move back" + the move dialog.
    bindKebabs(this.container);
    this.container.querySelectorAll<HTMLButtonElement>('[data-q-move]').forEach(el =>
      el.addEventListener('click', () => { if (!el.disabled) this.openMoveDialog(el.dataset.qMove!); }));
    this.container.querySelectorAll<HTMLButtonElement>('[data-q-moveback]').forEach(el =>
      el.addEventListener('click', () => { if (!el.disabled) void this.moveBack(el.dataset.qMoveback!); }));
    this.container.querySelectorAll<HTMLButtonElement>('[data-q-cancel]').forEach(el =>
      el.addEventListener('click', () => { if (!el.disabled) void this.cancelQueued(el.dataset.qCancel!); }));
    const mvBg = this.container.querySelector<HTMLElement>('[data-qmv-bg]');
    if (mvBg) {
      const close = async () => {
        if (!this.moveDlg || this.moveDlg.busy) return;
        const stale = !!this.moveDlg.stale;
        this.moveDlg = null;
        if (stale) await this.reloadQueue();
        this.draw();
      };
      mvBg.addEventListener('click', (e) => { if (e.target === mvBg) void close(); });
      mvBg.querySelectorAll<HTMLElement>('[data-qmv-close]').forEach(b => b.addEventListener('click', () => void close()));
      mvBg.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.stopPropagation(); void close(); } });
      const di = mvBg.querySelector<HTMLInputElement>('#qmv-date');
      const keep = () => { if (this.moveDlg && di) this.moveDlg.to = di.value; };   // never post on change — Chrome fires it per segment
      di?.addEventListener('input', keep);
      di?.addEventListener('change', keep);
      di?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); keep(); void this.submitMove(); } });
      mvBg.querySelector('#qmv-go')?.addEventListener('click', () => void this.submitMove());
    }

    // overview: condition history — board row click + property picker
    this.container.querySelectorAll<HTMLElement>('[data-insp-history]').forEach(el =>
      el.addEventListener('click', () => {
        const [listing_id, name] = el.dataset.inspHistory!.split('|');
        this.openHistory(listing_id, name);
      }));
    const histPick = this.container.querySelector('#insp-hist-pick') as HTMLSelectElement | null;
    histPick?.addEventListener('change', () => {
      const v = histPick.value;
      if (!v) return;
      const [listing_id, name] = v.split('|');
      this.openHistory(listing_id, name);
      histPick.value = '';
    });

    // inspections: open a row → report card if analyzed, else resume capture.
    this.container.querySelectorAll<HTMLElement>('[data-insp-open]').forEach(el =>
      el.addEventListener('click', () => {
        const insp = this.inspections.find(i => i.id === el.dataset.inspOpen);
        if (!insp) return;
        this.activeId = insp.id;
        const analyzed = insp.grade != null || (insp.items || []).some((it: any) => it.source === 'ai') || !!insp.submitted_at || !!insp.ai;   // AI-unblock: submitted = report card, graded or not
        // R37: a COMPLETED inspection always opens the report card — even
        // never-analyzed ones (tickets-only / manual-checklist completes).
        // That's where Reopen & edit lives, and capture must not mutate a
        // completed record without the reopen audit.
        this.view = (insp.status === 'complete' || analyzed) ? 'report' : 'capture';
        this.captureStep = 1;   // resuming also lands on the Issues step (stale step-2 must not leak across inspections)
        this.draw();
      }));
    // inspections: open the manual checklist detail (fallback, never regresses).
    this.container.querySelectorAll<HTMLElement>('[data-insp-manual]').forEach(el =>
      el.addEventListener('click', e => { e.stopPropagation(); this.openId = el.dataset.inspManual!; this.renderDetailModal(); }));
    // inspections: open the photo-forward owner report (Print / Save as PDF lives
    // inside the report doc). GET /inspections/:id/report is a public HTML route,
    // so window.open is the clean path; stopPropagation keeps the row from opening.
    this.container.querySelectorAll<HTMLElement>('[data-insp-report]').forEach(el =>
      el.addEventListener('click', e => { e.stopPropagation(); void openApiDoc(`${API}/inspections/${el.dataset.inspReport}/report`, 'Inspection report'); }));
    // R58: cleaner-facing feedback page (issue photos + AI-polished notes).
    this.container.querySelectorAll<HTMLElement>('[data-insp-creport]').forEach(el =>
      el.addEventListener('click', e => { e.stopPropagation(); void openApiDoc(`${API}/inspections/${el.dataset.inspCreport}/cleaner-report`, 'Cleaner report'); }));
    // R58: list date filter — chips apply instantly; typed dates settle 600ms
    // (Chrome fires 'change' per SEGMENT edit — repo precedent).
    this.container.querySelectorAll<HTMLElement>('[data-insp-dchip]').forEach(el =>
      el.addEventListener('click', () => {
        const kind = el.dataset.inspDchip;
        const today = new Date().toLocaleDateString('en-CA');
        if (kind === 'today') { this.inspFrom = today; this.inspTo = today; }
        else if (kind === '7d') { const d = new Date(); d.setDate(d.getDate() - 7); this.inspFrom = d.toLocaleDateString('en-CA'); this.inspTo = today; }
        else { this.inspFrom = ''; this.inspTo = ''; }
        this.draw();
      }));
    const bindInspDate = (sel: string, field: 'inspFrom' | 'inspTo') => {
      const inp = this.container.querySelector<HTMLInputElement>(sel);
      inp?.addEventListener('change', () => {
        if (this.inspDateTimer != null) window.clearTimeout(this.inspDateTimer);
        this.inspDateTimer = window.setTimeout(() => {
          this.inspDateTimer = null;
          if (!inp.value || inp.checkValidity()) { (this as any)[field] = inp.value || ''; this.draw(); }
        }, 600);
      });
    };
    bindInspDate('[data-insp-from]', 'inspFrom');
    bindInspDate('[data-insp-to]', 'inspTo');

    // inspections: new → opens the capture-first view.
    $('#insp-nf-add')?.addEventListener('click', async () => {
      const listing_id = $('#insp-nf-listing')?.value;
      const template_id = $('#insp-nf-tpl')?.value || undefined;
      const inspector = $('#insp-nf-inspector')?.value?.trim() || '';
      const inspType = ($('#insp-nf-type')?.value === 'audit') ? 'audit' : 'condition';
      const formType = ($('#insp-nf-form')?.value ?? 'basic') || null;
      if (!listing_id) { alert('Pick a property.'); return; }
      const listing = this.boardListings.find(l => String(l.listing_id) === String(listing_id));
      this.startInspectionFor(listing_id, listing?.listing_name, inspector, template_id, inspType, formType);
    });
    // remember the chosen inspection type across redraws of the form.
    (this.container.querySelector('#insp-nf-type') as HTMLSelectElement | null)?.addEventListener('change', e => {
      this.newInspectionType = ((e.target as HTMLSelectElement).value === 'audit') ? 'audit' : 'condition';
    });

    // templates: new / edit / delete
    this.container.querySelector('[data-insp-tplnew]')?.addEventListener('click', () => { this.editTplId = ''; this.renderTemplateModal(); });
    this.container.querySelectorAll<HTMLElement>('[data-insp-tpledit]').forEach(el =>
      el.addEventListener('click', () => { this.editTplId = el.dataset.inspTpledit!; this.renderTemplateModal(); }));
    this.container.querySelectorAll<HTMLElement>('[data-insp-tpldel]').forEach(el =>
      el.addEventListener('click', async () => {
        const id = el.dataset.inspTpldel!;
        const t = this.templates.find(x => x.id === id);
        if (!confirm(`Delete template "${t?.name || id}"? Existing inspections keep their copied items.`)) return;
        const j = await this.del(`/inspection-templates/${id}`);
        if (j) { this.templates = j.templates || this.templates.filter(x => x.id !== id); this.draw(); }
      }));

    // focus-items manager (Feature B): new / edit / delete cadence items
    this.container.querySelector('[data-foc-new]')?.addEventListener('click', () => { this.editFocusId = ''; this.renderFocusModal(); });
    this.container.querySelectorAll<HTMLElement>('[data-foc-edit]').forEach(el =>
      el.addEventListener('click', () => { this.editFocusId = el.dataset.focEdit!; this.renderFocusModal(); }));
    this.container.querySelectorAll<HTMLElement>('[data-foc-del]').forEach(el =>
      el.addEventListener('click', async () => {
        const id = el.dataset.focDel!;
        const f = (this.focusManager || []).find(x => x.id === id);
        if (!confirm(`Delete cadence item "${f?.title || id}"? It will stop appearing as required on visits.`)) return;
        const j = await this.del(`/inspections/focus-items/${id}`);
        if (j?.ok) { this.focusManager = (this.focusManager || []).filter(x => x.id !== id); this.focusFor = null; this.focus = null; this.draw(); }
      }));

    // managers (Feature D): add PM / remove PM / assign property → PM / PM report
    $('#pm-nf-add')?.addEventListener('click', async () => {
      const name = $('#pm-nf-name')?.value?.trim() || '';
      const email = $('#pm-nf-email')?.value?.trim() || '';
      const phone = $('#pm-nf-phone')?.value?.trim() || '';
      if (!name) { alert('Enter their name.'); return; }
      if (await this.saveRosterRow({ name, email, phone })) await this.reloadManagers();
    });
    // R120: per-row Edit (name / login email) → POST /inspections/managers by id
    this.container.querySelectorAll<HTMLElement>('[data-pm-edit]').forEach(el =>
      el.addEventListener('click', () => { this.editMgrId = el.dataset.pmEdit!; this.mgrMsg = ''; this.draw(); }));
    this.container.querySelector('[data-pm-cancel]')?.addEventListener('click', () => { this.editMgrId = null; this.mgrMsg = ''; this.draw(); });
    this.container.querySelectorAll<HTMLButtonElement>('[data-pm-save]').forEach(el =>
      el.addEventListener('click', async () => {
        const id = el.dataset.pmSave!;
        const name = $('#pm-ed-name')?.value?.trim() || '';
        const email = $('#pm-ed-email')?.value?.trim() || '';
        if (!name) { alert('Enter their name.'); return; }
        el.disabled = true;
        if (await this.saveRosterRow({ id, name, email })) { this.editMgrId = null; await this.reloadManagers(); }
        else { el.disabled = false; }
      }));
    this.container.querySelectorAll<HTMLElement>('[data-pm-del]').forEach(el =>
      el.addEventListener('click', async () => {
        const id = el.dataset.pmDel!;
        const m = (this.managers || []).find(x => x.id === id);
        if (!confirm(`Remove PM "${m?.name || id}"? Their property assignments stay but stop rolling up.`)) return;
        const j = await this.del(`/inspections/managers/${id}`);
        if (j?.ok) await this.reloadManagers();
      }));
    this.container.querySelectorAll<HTMLSelectElement>('[data-pm-assign]').forEach(el =>
      el.addEventListener('change', async () => {
        const listing_id = el.dataset.pmAssign!;
        const pm_id = el.value || null;
        const j = await this.post('/inspections/property-pm', { listing_id, pm_id });
        if (j) await this.reloadManagers(); // refresh scorecard roll-ups
      }));
    this.container.querySelectorAll<HTMLElement>('[data-pm-report]').forEach(el =>
      el.addEventListener('click', () =>
        void openApiDoc(`${API}/inspections/pm-report/${el.dataset.pmReport}?days=90`, 'PM report')));
  }
}

// R114: any [data-fq-open] (the capture tally's Review, the field view's
// banner) opens the phone's photo-queue sheet — delegated once at the document
// so in-place tally refreshes need no rebinding.
if (typeof document !== 'undefined') {
  document.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement | null)?.closest?.('[data-fq-open]') as HTMLElement | null;
    if (!el) return;
    e.preventDefault();
    openPhotoQueueSheet({ focus: el.getAttribute('data-fq-open') || null, current: el.getAttribute('data-fq-current') || null });
  });
}
